// Clinic Dashboard — the head COCKPIT (Phase 3 SC-D). Pull-based, no realtime; reads ONLY via the SC-C RPCs.
// D1 slice: overview + roster + therapist drill-in + client chart + content-open. Chat absent everywhere.
// Strings are English here (pending D4 i18n: draft→review→byte-verify→both dicts). Deviations from the mock
// are flagged, not improvised.

import { withClear } from '../search-clear/index.js';
import { mountWaCard } from '../wa-card/index.js';

const H = () => window.HUD || {};
const T = (k, p) => window.HUD?.T?.(k, p) || k;
function supa() { const c = window.HUD?._supa; return CS.admin && c ? _adminClient(c) : c; }
// C-5 C1b (QA3 = A) — for an admin, every RPC refused with 42501 re-checks the membership. A refusal is not
// proof of removal (a capability the admin lacks also answers 42501), so only my_clinics() decides.
let _adminProxy = null, _adminProxyFor = null, _adminRechecking = false, _adminCheckedAt = 0;
function _adminClient(c) {
  if (_adminProxyFor !== c) {
    _adminProxyFor = c;
    _adminProxy = new Proxy(c, { get(t, k) {
      // a thenable over the builder (the cockpit only awaits / .then()s its RPCs): awaiting it dispatches the request
      if (k === 'rpc') return (...a) => {
        const p = Promise.resolve(t.rpc(...a)).then(r => { if (r && r.error && r.error.code === '42501') _adminRecheck(); return r; });
        return { then: (res, rej) => p.then(res, rej), catch: (rej) => p.catch(rej), finally: (f) => p.finally(f) };
      };
      const v = t[k]; return typeof v === 'function' ? v.bind(t) : v;
    } });
  }
  return _adminProxy;
}
async function _adminRecheck() {
  // one re-read per burst: a view's reads refused together (e.g. calendar + its counts) ask once, not once each
  if (_adminRechecking || !CS.admin || CS.admin.state === 'noaccess' || CS.admin.state === 'invite' || Date.now() - _adminCheckedAt < 3000) return;
  _adminRechecking = true;
  try {
    const { data, error } = await window.HUD._supa.rpc('my_clinics');
    if (error) throw error;
    if (!(data || []).some(r => r.role === 'admin')) _adminNoAccess();
  } catch (e) { console.error('❌ my_clinics (admin recheck):', e); }
  finally { _adminRechecking = false; _adminCheckedAt = Date.now(); }
}
function _adminNoAccess() {
  CS.admin.state = 'noaccess';
  [...document.querySelectorAll('.ck-dlg-back')].reverse().forEach(d => (d.__ckClose ? d.__ckClose() : d.remove()));   // a dialog open at removal goes with the access
  _closeCkPicker();
  CS.view = 'overview'; CS.loading = false; CS.err = null;
  render();
}
function el(...a) { return H().el(...a); }
function toast(m, t) { return H().toast?.(m, t); }
// SC4 — dual-language: resolve a server-composed name at RENDER via the HUD resolver (script-sniffs the
// live UI lang, canonical on tie/none). _nm(o) resolves any {name,name_alt} row (member/child/sp/patient).
function dn(c, a) { return H().dn ? H().dn(c, a) : (c == null ? '' : c); }
const _nm = (o) => (o ? dn(o.name, o.name_alt) : '');

// C-7 — dates/times follow the APP language, not the browser: index.html's _locDate/_locTime (he → he-IL, English →
// the browser's locale, the app-wide convention), read through the bridge so a language switch applies at once.
function _ckDate(d, opts) { const f = H()._locDate; return f ? f(d, opts) : d.toLocaleDateString(undefined, opts); }
function _ckTime(d, opts) { const f = H()._locTime; return f ? f(d, opts) : d.toLocaleTimeString(undefined, opts); }

// C-6 — a person's name is its own bidi unit, direction from its own text (dir=auto / <bdi>): a Latin name in the
// Hebrew UI keeps its START visible (ellipsis at its end) and can't reorder the text around it.
const _bdi = (t) => el('bdi', {}, [t]);

// ── internal view state (pull-based; refetch on open + on drill-in) ──
const CS = { view: 'overview', overview: null, isOwner: false, member: null, client: null, artifact: null, cal: null, pay: null, payUnprocessed: null, payouts: null, payoutsCatchNet: null, worklist: null, billing: null, billingScreen: 'hub', loading: false, err: null, unassignedCount: null,
  admin: null };   // C-5 C1: null for heads/therapists; a clinic_admin account → { state, clinicName, caps } (the cockpit IS its app)
let _acctOpen = false, _acctLangs = false;
// C-5 C2 — what this viewer may open. A head (CS.admin null) may open everything; an admin only the sections its
// capabilities (my_clinics().capabilities) switch on. ALWAYS read here, never inferred from data: the server masks
// what an admin may not see as null, which looks the same as "nothing recorded" (QM2b1-2). The server still enforces.
// Phased first admin (planning 2026-10-04): a capability counts only once its server package is LIVE. A capability
// whose package isn't applied yet (billing, patients, clinical) is never offered to an admin and its switch is hidden
// in the head's Admins section; each package's apply adds it here in its own client commit.
const CAP_LIVE = new Set(['calendar', 'payments', 'members']);
function can(cap) { return !CS.admin || (CAP_LIVE.has(cap) && (CS.admin.caps || []).includes(cap)); }
let _overlay = null;
let _escHandler = null;   // document keydown listener while the cockpit is open (Escape → close, dialog-aware)
// Arc A2 — persistent shell: built once per open; across navigations only .ck-body's children swap
// (head re-derives in place). _bodyView = the view whose REAL content currently sits in _body (null
// while a skeleton or error is shown), used to (a) keep the body up during a same-view reload instead
// of blanking, and (b) fire the crossfade ONLY when the shown view actually changes.
let _shell = null, _head = null, _body = null;
let _prevView = null, _bodyView = null;
// C-2 — re-render when the window crosses a width band (768 / 1024), debounced; never inside a band.
let _ckBandNow = null, _ckResizeT = null;


export function initClinicDashboard() {
  injectStyles();
  window.HUD_CLINIC = { open: openCockpit, close: closeCockpit, openAdmin };
}

// ── plain-text extraction from a Tiptap/ProseMirror doc (read-only preview; full rich render is D4 polish) ──
function tiptapText(node) {
  if (node == null) return '';
  if (typeof node === 'string') return node;
  if (Array.isArray(node)) return node.map(tiptapText).join('');
  let out = '';
  if (node.text) out += node.text;
  if (node.content) out += tiptapText(node.content);
  if (node.type === 'paragraph' || node.type === 'heading') out += '\n';
  return out;
}

// ══ open / close ════════════════════════════════════════════════════════════
async function openCockpit() {
  if (_overlay) return;
  _mountOverlay();
  render();
  await loadOverview();
}
function _mountOverlay() {
  _overlay = el('div', { class: 'ck-overlay' + (CS.admin ? ' ck-admin' : '') });
  // Tap the dimmed backdrop (outside the panel) → close the whole cockpit. When a body-level dialog is open its own
  // backdrop (z 1600, above this 1400) intercepts the tap, so this only fires with no dialog present.
  // C-5 C1: an admin has nothing behind the cockpit, so the backdrop, Esc and ‹ on the overview never close it.
  _overlay.onclick = (e) => { if (e.target === _overlay && !CS.admin) closeCockpit(); };
  document.body.appendChild(_overlay);
  // Escape closes the cockpit — but NOT while a dialog is open (the dialog's capture-phase Esc closes it first and
  // stops propagation; this guard is belt-and-suspenders). Reopening always lands on the overview (Object.assign below).
  _escHandler = (e) => {
    if (e.key !== 'Escape' || document.querySelector('.ck-dlg-back')) return;
    if (_acctOpen) { _acctClose(); return; }
    if (!CS.admin) closeCockpit();
  };
  document.addEventListener('keydown', _escHandler);
  _ckBandNow = _ckBand(); window.addEventListener('resize', _ckOnResize);
  Object.assign(CS, { view: 'overview', member: null, client: null, artifact: null, err: null });
}
// C-5 C1 — a clinic_admin account: the app shell renders nothing of its own and calls this on every repaint
// (index.html renderShell). The first call opens the cockpit; later ones repaint it (e.g. after a language change).
async function openAdmin() {
  if (_overlay) { if (CS.admin) render(); return; }
  CS.admin = { state: 'loading', clinicName: null, caps: [] };
  _mountOverlay();
  render();
  await _adminResolve();
}
async function _adminResolve() {
  try {
    const { data, error } = await supa().rpc('my_clinics');
    if (error) throw error;
    const row = (data || []).find(r => r.role === 'admin');
    if (!row) {
      // QC1-1 = A: a pending admin invite (a first invite by e-mail, or a former admin's re-invite) → the Join screen
      const { data: inv, error: iErr } = await supa().rpc('my_admin_invite');
      if (iErr) throw iErr;
      if (inv && inv.length) { Object.assign(CS.admin, { state: 'invite', invite: inv[0], clinicName: inv[0].clinic_name }); CS.loading = false; CS.err = null; render(); return; }
      _adminNoAccess(); return;   // QA3: also on every sign-in while the account has no active clinic
    }
    Object.assign(CS.admin, { state: 'ok', clinicName: row.clinic_name, caps: row.capabilities || [] });
  } catch (e) { console.error('❌ my_clinics (admin):', e); CS.err = T('ck_err_load_overview'); render(); return; }
  await loadOverview();
}
function closeCockpit() {
  if (_ckNowInt) { clearInterval(_ckNowInt); _ckNowInt = null; }
  if (_escHandler) { document.removeEventListener('keydown', _escHandler); _escHandler = null; }
  window.removeEventListener('resize', _ckOnResize); clearTimeout(_ckResizeT); _ckResizeT = null;
  if (_overlay) { _overlay.remove(); _overlay = null; }
  _shell = _head = _body = null; _prevView = _bodyView = null;  // Arc A2: drop persistent-shell refs so a reopen rebuilds cleanly
  _closeCkPicker();  // B3: tear down a lingering date-picker popover
  if (_ckBorrowCollapse) { try { _ckBorrowCollapse(); } catch (_) {} }  // B2.5: drop a lingering borrow + its outside listener
}

// The frame, padding and type follow the band in CSS alone. Only the calendar builds a different DOM per band
// (phone day view = chip switcher + one member column), so only it is re-rendered; other views are left as they
// are (a rebuild would drop typed-but-unsaved billing inputs). render() swaps .ck-body's children only, so
// body-level dialogs stay open, and _ckApplyScroll restores the calendar's scroll position.
function _ckOnResize() {
  clearTimeout(_ckResizeT);
  _ckResizeT = setTimeout(() => {
    _ckResizeT = null;
    const b = _ckBand(); if (b === _ckBandNow) return;
    _ckBandNow = b;
    if (_overlay && CS.view === 'calendar' && !CS.loading) render();
  }, 150);
}

function goBack() {
  if (CS.view === 'artifact') { CS.artifact = null; CS.view = CS.client ? 'client' : 'member'; render(); }
  else if (CS.view === 'client') { CS.client = null; CS.view = CS.member ? 'member' : 'overview'; render(); }
  else if (CS.view === 'member') { CS.member = null; CS.view = 'overview'; render(); }
  else if (CS.view === 'manage') { CS.view = 'overview'; render(); }
  else if (CS.view === 'area') { CS.view = 'overview'; render(); }
  else if (CS.view === 'calendar') { CS.view = 'overview'; render(); }
  else if (CS.view === 'payments') { CS.view = 'overview'; render(); }
  else if (CS.view === 'payouts') { CS.view = 'overview'; render(); }
  else if (CS.view === 'worklist') { CS.view = (CS.worklist && CS.worklist.origin === 'calendar') ? 'calendar' : 'payouts'; render(); }
  else if (CS.view === 'billing') { if (CS.billingScreen !== 'hub') { CS.billingScreen = 'hub'; render(); } else { CS.view = 'overview'; render(); } }
  else if (CS.view === 'settings') { CS.view = CS._settingsFrom || 'overview'; render(); }   // C-5 C1c: back to where Settings was opened
  else if (!CS.admin) closeCockpit();
}

// ══ RPC loaders ═════════════════════════════════════════════════════════════
async function loadOverview() {
  CS.loading = true; CS.err = null; render();
  try {
    const { data, error } = await supa().rpc('clinic_head_overview');
    if (error) throw error;
    CS.overview = data;
    // SC4 sort-on-resolved: re-order members by the viewer-resolved name, heads first (the RPC orders by
    // canonical display_name; a Hebrew viewer wants Hebrew-collated). Script-agnostic collation deferred.
    if (data && Array.isArray(data.members)) data.members.sort((a, b) => ((b.role === 'head') - (a.role === 'head')) || _nm(a).localeCompare(_nm(b)));
    try { const { data: own } = await supa().rpc('is_clinic_owner_of', { p_clinic: data.clinic_id }); CS.isOwner = own === true; } catch (e) { CS.isOwner = false; }
    // B-status: unprocessed count for the overview Payments card (head-only; one extra read).
    if (can('payments')) { try { const { data: pq } = await supa().rpc('clinic_payment_queue', { p_view: 'unprocessed' }); CS.payUnprocessed = Array.isArray(pq) ? pq.length : 0; } catch (e) { CS.payUnprocessed = null; } }   // C-5 C2
    // SC-B4b: catch-net count for the overview Payouts-card badge (current month). p_log:false → this badge
    // read writes NO 'billing_payouts_open' access-log row (a count, not an access event); only OPENING the
    // view logs (default true). Same math source (clinic_payout_compute). Opening the view re-fetches.
    if (can('billing')) { try { const { data: po } = await supa().rpc('clinic_payouts', { p_clinic: data.clinic_id, p_month: _monthStart(new Date()), p_log: false }); const ct = po?.clinic_totals || {}; CS.payoutsCatchNet = (ct.unclassified_billable || 0) + (ct.unmarked || 0); } catch (e) { CS.payoutsCatchNet = null; } }   // C-5 C2
    // Patient Lifecycle L4 — Unassigned count now rides the overview payload (totals.unassigned).
    CS.unassignedCount = (data && data.totals && data.totals.unassigned != null) ? data.totals.unassigned : null;
  } catch (e) { console.error('❌ clinic_head_overview:', e); CS.err = T('ck_err_load_overview'); }
  CS.loading = false; render();
}
async function openMember(m) {
  CS.loading = true; CS.err = null; CS.view = 'member'; CS.member = { user_id: m.user_id, name: m.name, name_alt: m.name_alt, tier: m.tier, role: m.role }; render();
  try {
    const { data, error } = await supa().rpc('clinic_member_caseload', { p_member: m.user_id });
    if (error) throw error;
    CS.member = Object.assign(CS.member, data);
  } catch (e) { console.error('❌ clinic_member_caseload:', e); CS.err = T('ck_err_load_caseload'); }
  CS.loading = false; render();
}
async function openClient(childId, spId, name, nameAlt) {
  CS.loading = true; CS.err = null; CS.view = 'client'; CS.client = { name, name_alt: nameAlt || null, child_id: childId || null, sp_id: spId || null }; render();
  try {
    const { data, error } = await supa().rpc('clinic_client_chart', { p_child: childId || null, p_sp: spId || null });
    if (error) throw error;
    CS.client = Object.assign(CS.client, data);
  } catch (e) { console.error('❌ clinic_client_chart:', e); CS.err = T('ck_err_load_chart'); }
  CS.loading = false; render();
}
async function openArtifact(type, id, title, summary) {
  CS.loading = true; CS.err = null; CS.view = 'artifact'; CS.artifact = { type, id, title }; render();
  try {
    const { data, error } = await supa().rpc('clinic_open_artifact', { p_type: type, p_id: id });
    if (error) throw error;
    // carry the parent summary from the chart row (clinic_client_chart already returns note.summary) — no SQL needed
    CS.artifact = Object.assign({}, data, { summary: summary != null ? summary : null }); // { type, id, title, author, is_draft, content, summary }
  } catch (e) { console.error('❌ clinic_open_artifact:', e); CS.err = T('ck_err_open_record'); }
  CS.loading = false; render();
}

// ══ render ══════════════════════════════════════════════════════════════════
function badge(text, cls) { return el('span', { class: 'ck-badge ' + (cls || '') }, [text]); }
function logPill() { return el('span', { class: 'ck-logpill' }, [T('ck_access_logged')]); }

// Arc A2 — the shell + head persist across navigations; only .ck-body's children swap. The frame no
// longer rebuilds (so it can't resize or flash), and a crossfade plays ONLY when the shown view changes.
function render() {
  if (!_overlay) return;
  _ensureShell();
  _renderHead();

  // Arc A: calendar GRID mode gets .cal (grid-body is the sole scroller); strip mode + every other
  // view scroll on .ck-body itself (attendance strip rides the shell — ruling 7).
  const calMode = CS.view === 'calendar' && CS.cal && (CS.cal.view === 'day' || CS.cal.mode === 'grid'); // day view is also a grid-scroller

  // SAME-VIEW reload with data in flight (week-step / mode / filter): keep the current body visible —
  // never blank the frame to a loader. The old content stays until the new data lands, then the branch
  // below swaps it in place. Gated on the body actually holding content, so the very first open (empty
  // body, data not yet fetched) falls through to the skeleton instead of showing a blank frame.
  if (CS.loading && !CS.err && _bodyView === CS.view && _body.childElementCount > 0) { _prevView = CS.view; return; }

  _body.className = 'ck-body' + (calMode ? ' cal' : '');
  _body.replaceChildren();
  if (CS.admin && (CS.admin.state === 'noaccess' || CS.admin.state === 'invite') && CS.view !== 'settings') { _body.appendChild(CS.admin.state === 'invite' ? _renderJoin() : _renderNoAccess());   // Settings stays reachable (mock)
    _bodyView = null; _prevView = CS.view; return; }
  // C-8: list/table views render into a centred reading column (.ck-measure, max 1200px) inside the full frame.
  const host = _ckMeasured() ? _body.appendChild(el('div', { class: 'ck-measure' })) : _body;
  if (CS.err) {
    host.appendChild(el('div', { class: 'ck-err' }, [CS.err]));
    _bodyView = null;
  } else if (CS.loading) {
    host.appendChild(_skeletonBody());   // cross-view / first load — a skeleton, never '…'
    _bodyView = null;
  } else {
    _dispatchBody(host);
    const contentViewChanged = CS.view !== _bodyView;
    _bodyView = CS.view;
    if (contentViewChanged) _fadeBody();  // crossfade ONLY when the shown view actually changes
  }
  _prevView = CS.view;
}

// C-8 — views that are lists/tables get the reading column; the calendar, the overview cards, the billing hub
// cards and the client chart stay full width.
const CK_MEASURE_VIEWS = new Set(['payments', 'payouts', 'worklist', 'manage', 'area', 'member', 'settings']);
function _ckMeasured() { return CK_MEASURE_VIEWS.has(CS.view) || (CS.view === 'billing' && CS.billingScreen !== 'hub'); } // billing: price list, client prices, therapist pay

// Build the persistent overlay contents once per open (nulled on close so a reopen rebuilds).
function _ensureShell() {
  if (_shell) return;
  _shell = el('div', { class: 'ck-shell' });   // Arc A: one constant shell — no per-view width class
  _head = el('div', { class: 'ck-head' });
  _body = el('div', { class: 'ck-body' });
  _shell.appendChild(_head);
  _shell.appendChild(_body);
  _overlay.replaceChildren(_shell);
}

// Head is cheap; re-derive it in place every render so back/title/log-pill track CS.view.
function _renderHead() {
  _head.replaceChildren();
  // Back chevron steps WITHIN the cockpit; only off the overview (nothing to step back to from overview).
  if (CS.view !== 'overview') { const back = el('button', { class: 'ck-back', title: T('ck_back') }, ['‹']); back.onclick = goBack; _head.appendChild(back); }
  const _ht = headerTitle();   // C-6: a name title keeps its start (dir=auto); overview/calendar come as parts (split title)
  _head.appendChild(Array.isArray(_ht) ? el('div', { class: 'ck-title ck-title-split' }, _ht)
    : el('div', Object.assign({ class: 'ck-title' }, (CS.view === 'member' || CS.view === 'client') ? { dir: 'auto' } : {}), [_ht]));
  if (CS.view !== 'overview' && CS.view !== 'settings') _head.appendChild(logPill());   // C-5 C1c: own account settings read no client data
  if (CS.admin) { _renderAcct(); return; }   // C-5 C1 (QA4 = A): the account button takes the ✕'s place
  // Persistent Exit on EVERY view — X icon only (no label), closes the whole cockpit in one tap. Header
  // end; in RTL that is the left edge (flex + dir handle it, no manual flip). aria-label reuses btn_close.
  const exit = el('button', { class: 'ck-exit', title: T('btn_close'), 'aria-label': T('btn_close') }, ['✕']); exit.onclick = closeCockpit; _head.appendChild(exit);
}

// C-5 C1 — the admin's account button + menu (mock C-4-admin-header, approved). Rebuilt with the head on every
// render; _acctOpen keeps the menu open across a repaint. Language switches through the app's own _setLang
// (which re-renders the shell → openAdmin → render()); Sign out is the app's doLogout.
function _acctClose() { _acctOpen = false; _acctLangs = false; document.removeEventListener('pointerdown', _acctOutside, true); if (_head) _renderHead(); }
function _acctOutside(e) { if (!e.target.closest || !e.target.closest('.ck-acct, .ck-menu')) _acctClose(); }
function _renderAcct() {
  const ss = H().session || {};
  const first = ss.name || (ss.email || '').split('@')[0] || '';
  const btn = el('button', { class: 'ck-acct', 'aria-haspopup': 'menu', 'aria-expanded': _acctOpen ? 'true' : 'false', 'aria-label': T('ck_admin_account_menu') },
    [el('span', { class: 'ck-acct-av' }, [(first || '?').trim().charAt(0).toUpperCase()]), el('bdi', { class: 'ck-acct-nm' }, [first]), el('span', { class: 'ck-acct-car' }, [_acctOpen ? '▴' : '▾'])]);
  btn.onclick = (e) => {
    e.stopPropagation();
    if (_acctOpen) { _acctClose(); return; }
    _acctOpen = true; document.addEventListener('pointerdown', _acctOutside, true); _renderHead();
  };
  _head.appendChild(btn);
  if (!_acctOpen) return;
  const menu = el('div', { class: 'ck-menu', role: 'menu' });
  const who = el('div', { class: 'ck-menu-who' });
  who.appendChild(el('div', { class: 'ck-menu-nm', dir: 'auto' }, [ss.displayName || first]));
  if (ss.email) who.appendChild(el('div', { class: 'ck-menu-mail' }, [ss.email]));
  who.appendChild(el('span', { class: 'ck-menu-role' }, [T('ck_admin_role')]));
  menu.appendChild(who);
  const item = (ic, label, onClick, cls) => {
    const b = el('button', { class: 'ck-mi' + (cls ? ' ' + cls : ''), role: 'menuitem' }, [el('span', { class: 'ck-mi-ic' }, [ic]), label]);
    b.onclick = (e) => { e.stopPropagation(); onClick(); };
    return b;
  };
  menu.appendChild(item('⚙', T('nav_settings'), () => {   // C-5 C1c: Settings opens over the cockpit; ‹ returns
    _acctClose(); if (CS.view !== 'settings') { CS._settingsFrom = CS.view; CS.view = 'settings'; render(); }
  }));
  const langs = H()._availableLangs || [];
  if (langs.length > 1) {
    const cur = langs.find(l => l.code === H()._langNow) || langs[0];
    const li = item('🌐', T('settings_language'), () => { _acctLangs = !_acctLangs; _renderHead(); });
    li.appendChild(el('span', { class: 'ck-mi-val' }, [cur.label + ' ' + (_acctLangs ? '▾' : '▸')]));
    menu.appendChild(li);
    if (_acctLangs) langs.forEach(l => {
      const b = item(l.code === cur.code ? '✓' : '', l.label, () => { _acctClose(); if (l.code !== cur.code) H()._setLang(l.code); }, 'sub');
      menu.appendChild(b);
    });
  }
  menu.appendChild(el('div', { class: 'ck-menu-sep' }));
  menu.appendChild(item('⎋', T('nav_sign_out'), () => { _acctClose(); H().doLogout(); }, 'out'));
  _head.appendChild(menu);
}

function _dispatchBody(body) {
  if (CS.view === 'overview') renderOverview(body);
  else if (CS.view === 'manage') renderManage(body);
  else if (CS.view === 'area') renderArea(body);
  else if (CS.view === 'member') renderMember(body);
  else if (CS.view === 'client') renderClient(body);
  else if (CS.view === 'artifact') renderArtifact(body);
  else if (CS.view === 'calendar') renderCalendarView(body);
  else if (CS.view === 'payments') renderPaymentsView(body);
  else if (CS.view === 'payouts') renderPayoutsView(body);
  else if (CS.view === 'worklist') renderWorklistView(body);
  else if (CS.view === 'billing') renderBilling(body);
  else if (CS.view === 'settings') body.appendChild(H()._adminSettingsCards({ removed: !!(CS.admin && CS.admin.state === 'noaccess') }));   // C-5 C1c; F-41: delete only when removed
}

// Restart the crossfade: drop the class, force a reflow, re-add — otherwise a second view change
// wouldn't replay the animation.
function _fadeBody() {
  _body.classList.remove('ck-fade');
  void _body.offsetWidth;
  _body.classList.add('ck-fade');
}

// C-5 C1b (QA3 = A, mock C-4-admin-header): after a head removes the admin — the cockpit is gone, the account
// button stays (Settings / Language / Sign out), and the card offers Sign out.
function _renderNoAccess() {
  const wrap = el('div', { class: 'ck-noacc' });
  const card = el('div', { class: 'ck-noacc-card' });
  card.appendChild(el('div', { class: 'ck-noacc-ic' }, ['🔒']));
  card.appendChild(el('div', { class: 'ck-noacc-t' }, [T('ck_admin_noaccess_title')]));
  card.appendChild(el('div', { class: 'ck-noacc-d' }, [T('ck_admin_noaccess_body')]));
  const out = el('button', { class: 'ck-btn primary' }, [T('nav_sign_out')]); out.onclick = () => H().doLogout();
  card.appendChild(out);
  // F-41 (QF41-4 = A): a removed admin can delete the account from here too
  const del = el('button', { class: 'ck-btn ck-noacc-del' }, [T('settings_delete_account')]); del.onclick = () => H()._showDeleteAdminModal();
  card.appendChild(del);
  wrap.appendChild(card);
  return wrap;
}
// C-5 C1 (QC1-1 = A): one Join screen for any pending admin invite, neutral wording. Join = accept_admin_invite(),
// then the cockpit resolves again (my_clinics → overview). Sign out stays offered.
let _joinBusy = false;
function _renderJoin() {
  const wrap = el('div', { class: 'ck-noacc' });
  const card = el('div', { class: 'ck-noacc-card ck-join-card' });
  card.appendChild(el('div', { class: 'ck-noacc-ic' }, ['🏥']));
  card.appendChild(el('div', { class: 'ck-noacc-t' }, [T('ck_admin_invite_title', { clinic: CS.admin.invite?.clinic_name || '' })]));
  const acts = el('div', { class: 'ck-join-acts' });
  const join = el('button', { class: 'ck-btn primary' }, [T('ck_admin_invite_join')]);
  join.onclick = async () => {
    if (_joinBusy) return; _joinBusy = true; join.disabled = true;
    try {
      const { error } = await supa().rpc('accept_admin_invite');
      if (error) throw error;
      CS.admin.state = 'loading'; CS.admin.invite = null; render();
      await _adminResolve();
    } catch (e) { console.error('❌ accept_admin_invite:', e); toast(T('err_generic'), 'error'); join.disabled = false; }
    _joinBusy = false;
  };
  const out = el('button', { class: 'ck-btn ghost' }, [T('nav_sign_out')]); out.onclick = () => H().doLogout();
  acts.appendChild(join); acts.appendChild(out);
  card.appendChild(acts);
  wrap.appendChild(card);
  return wrap;
}
function _skeletonBody() {
  const wrap = el('div', { class: 'ck-skel' });
  for (let i = 0; i < 4; i++) wrap.appendChild(el('div', { class: 'ck-skel-row' }));
  return wrap;
}

function headerTitle() {
  // C-5 C1b: the no-access screen keeps the clinic name only while it is known from this session
  if (CS.admin && (CS.admin.state === 'noaccess' || CS.admin.state === 'invite') && CS.view !== 'settings') return [el('span', { class: 'ck-title-fix' }, ['🏥 ']), el('bdi', { class: 'ck-title-nm' }, [CS.admin.clinicName || CS.overview?.clinic_name || 'Huddledin'])];
  if (CS.view === 'overview') return [el('span', { class: 'ck-title-fix' }, ['🏥 ']), el('bdi', { class: 'ck-title-nm' }, [CS.overview?.clinic_name || T('ck_clinic_fallback')])];   // C-6
  if (CS.view === 'member') return _nm(CS.member) || T('ck_therapist_fallback');
  if (CS.view === 'client') return _nm(CS.client) || T('ck_client_fallback');
  if (CS.view === 'artifact') return CS.artifact?.title || T('ck_record_fallback');
  if (CS.view === 'manage') return T('ck_manage_members');
  if (CS.view === 'area') return T(CS.area === 'profile' ? 'cs_area_profile' : 'cs_area_msgcal');
  if (CS.view === 'calendar') return [el('span', { class: 'ck-title-fix' }, ['📅 ']), el('bdi', { class: 'ck-title-nm' }, [CS.overview?.clinic_name || T('ck_clinic_fallback')]),
                                       el('span', { class: 'ck-title-fix' }, [T('ck_calendar_title_suffix')])];   // C-6
  if (CS.view === 'payments') return '💳 ' + T('ck_payments');
  if (CS.view === 'payouts') return '📊 ' + T('pay_payouts');
  if (CS.view === 'worklist') return CS.worklist?.title || T('pay_payouts');
  if (CS.view === 'billing') return CS.billingScreen === 'hub' ? T('cs_area_money') : T('pay_hub_title');
  if (CS.view === 'settings') return '⚙ ' + T('nav_settings');
  return T('ck_clinic_fallback');
}

function renderOverview(body) {
  const o = CS.overview; if (!o) return;
  const bar = el('div', { class: 'ck-actbar' });
  // C-5 C2: each action shows only with its capability (heads: all); an empty bar is not drawn
  if (can('patients')) { const addBtn = el('button', { class: 'ck-btn primary sm' }, [T('ck_add_patient')]); addBtn.onclick = createAssignDialog; bar.appendChild(addBtn); }
  if (bar.childElementCount) body.appendChild(bar);
  // roll-up
  const t = o.totals || {};
  const roll = el('div', { class: 'ck-roll' });
  // Patient Lifecycle L2 — the UNASSIGNED (drafts) box is tappable + "live"; the other stat cards become
  // tappable in L4. Its count is CS.unassignedCount (direct head-RLS read at overview load).
  [
    { l: T('ck_roll_members'), n: t.members, onClick: openMembersPopup },
    { l: T('ck_roll_heads'), n: t.heads, onClick: openHeadsPopup },
    { l: T('ck_patients'), n: t.patients, onClick: can('patients') ? openPatientsPopup : null },   // C-5 C2: the lists need 'patients'
    { l: T('pl_roll_unassigned'), n: CS.unassignedCount, live: true, onClick: can('patients') ? openDraftPile : null },
    { l: T('ck_roll_needs_reassign'), n: t.needs_reassignment },   // full needs-reassign popup deferred
  ].forEach(bx => {
    const b = el('div', { class: 'ck-box' + (bx.live ? ' ck-box-live' : '') });
    b.appendChild(el('div', { class: 'ck-box-n' }, [String(bx.n ?? 0)]));
    b.appendChild(el('div', { class: 'ck-box-l' }, [bx.l]));
    if (bx.onClick) { b.classList.add('ck-box-tap'); b.onclick = bx.onClick; }
    roll.appendChild(b);
  });
  body.appendChild(roll);
  // C-5 C2: an admin with no section switched on gets one line instead of an empty screen (only sections that have UI count)
  if (CS.admin && !['patients', 'members', 'calendar', 'payments', 'billing'].some(can)) body.appendChild(el('div', { class: 'ck-nocaps' }, [T('ck_admin_no_caps')]));
  // CLINIC-SETTINGS-IA SC1: Work (do) above Clinic settings (set up). Text rows, no icons. Payments and Payouts keep their gates, badges and the .ck-pay-card class.
  const workRow = (cls, title, sub, onClick, badgeN) => {
    const r = el('div', { class: 'ck-row ck-row-click ' + cls });
    const g = el('div', { class: 'ck-grow' });
    g.appendChild(el('div', { class: 'ck-nm' }, [title]));
    g.appendChild(el('div', { class: 'ck-sub' }, [sub]));
    r.appendChild(g);
    if (badgeN) r.appendChild(el('span', { class: 'ck-badge p-aw' }, [String(badgeN)]));
    r.appendChild(el('span', { class: 'ck-chev' }, ['›']));
    r.onclick = onClick;
    return r;
  };
  const cs = el('div', { class: 'ck-cs-wrap' });   // wrapper: the roster rows stay the only direct .ck-row children of the body
  const workRows = [];
  // B-status: Payments row — unprocessed count → the 3-tab queue.
  if (can('payments')) workRows.push(workRow('ck-pay-card', T('ck_payments'), CS.payUnprocessed == null ? T('ck_pay_view_hint') : T('ck_pay_unprocessed_count', { n: CS.payUnprocessed }), openPayments, CS.payUnprocessed));
  // SC-B4b: Payouts row — badge = catch-net (unclassified-billable + unmarked).
  if (can('billing')) workRows.push(workRow('ck-pay-card', T('pay_payouts'), T('pay_payouts_card_d'), openPayouts, CS.payoutsCatchNet));
  if (can('calendar')) workRows.push(workRow('ck-work-row ck-work-cal', T('ck_calendar'), T('ck_this_week'), openCalendar));
  if (can('patients')) workRows.push(workRow('ck-work-row ck-work-pat', T('spec_nav_patients'), T('cs_work_patients_d', { n: t.patients ?? 0 }), openPatientsPopup));
  if (workRows.length) { cs.appendChild(el('div', { class: 'ck-section' }, [T('cs_work')])); workRows.forEach(r => cs.appendChild(r)); }
  const setRows = [];
  if (can('members')) setRows.push(workRow('ck-set-row ck-set-people', T('cs_area_people'), T('cs_area_people_d'), () => { CS.view = 'manage'; render(); if (!CS.admin) _loadAdmins(); }));   // SC1: still the Manage members page; SC2 moves it into the area
  if (can('billing')) setRows.push(workRow('ck-set-row ck-set-money', T('cs_area_money'), T('cs_area_money_d'), openBilling));
  if (!CS.admin) {   // head-only areas (the same facts that gate them today: WhatsApp, the letterhead)
    setRows.push(workRow('ck-set-row ck-set-msgcal', T('cs_area_msgcal'), T('cs_area_msgcal_d'), () => { CS.view = 'area'; CS.area = 'msgcal'; render(); }));
    setRows.push(workRow('ck-set-row ck-set-profile', T('cs_area_profile'), T('cs_area_profile_d'), () => { CS.view = 'area'; CS.area = 'profile'; render(); }));
  }
  if (setRows.length) { cs.appendChild(el('div', { class: 'ck-section' }, [T('clinic_settings_section')])); setRows.forEach(r => cs.appendChild(r)); }
  if (cs.childElementCount) body.appendChild(cs);
  // roster
  (o.members || []).forEach(m => {
    const row = el('div', { class: 'ck-row ck-row-click' });
    const av = el('div', { class: 'ck-av' }, [(_nm(m) || '?').trim().charAt(0).toUpperCase()]);
    row.appendChild(av);
    const grow = el('div', { class: 'ck-grow' });
    const nm = el('div', { class: 'ck-nm' }, [_nm(m) || '—']);
    if (m.role === 'head') nm.appendChild(badge(T('ck_badge_head'), 'b-head'));
    grow.appendChild(nm);
    grow.appendChild(el('div', { class: 'ck-sub' }, [badge(T('clinic_tier_' + m.tier), 'b-' + m.tier)]));
    row.appendChild(grow);
    const counts = el('div', { class: 'ck-counts' });
    counts.appendChild(stat(m.patients, T('ck_patients')));
    counts.appendChild(stat(m.upcoming, T('ck_this_week')));   // m.upcoming now = appts in the current week (Sun–Sat); label reuses the calendar's "This week"
    row.appendChild(counts);
    if (can('patients')) row.onclick = () => openMember(m); else row.classList.remove('ck-row-click');   // C-5 C2: a caseload needs 'patients'
    body.appendChild(row);
  });
}
function stat(n, l) { const s = el('div', { class: 'ck-stat' }); s.appendChild(el('div', { class: 'ck-stat-n' }, [String(n ?? 0)])); s.appendChild(el('div', { class: 'ck-stat-l' }, [l])); return s; }

function renderMember(body) {
  const m = CS.member; if (!m) return;
  const bar = el('div', { class: 'ck-actbar' });
  bar.appendChild(el('div', { class: 'ck-meta', style: 'flex:1;margin:0' }, [badge(T('clinic_tier_' + (m.tier || 'schedule')), 'b-' + (m.tier || 'schedule')), ' ' + T('ck_tier_gates_note')]));
  const leave = el('button', { class: 'ck-btn sm danger' }, [T('ck_leaving_reassign_btn')]);
  leave.onclick = () => leavingDialog(m.user_id);
  bar.appendChild(leave);
  body.appendChild(bar);
  (m.children || []).forEach(c => {
    const row = el('div', { class: 'ck-row ck-row-click' });
    row.appendChild(el('div', { class: 'ck-av sm' }, ['🧒']));
    const grow = el('div', { class: 'ck-grow' });
    grow.appendChild(el('div', { class: 'ck-nm' }, [_bdi(_nm(c) || '—')]));
    const bits = [T('ck_count_upcoming', { n: c.upcoming })];
    if (c.reports != null) bits.push(T('ck_count_reports', { n: c.reports }));
    if (c.notes != null) bits.push(T('ck_count_notes', { n: c.notes }));
    grow.appendChild(el('div', { class: 'ck-sub' }, [bits.join(' · ')]));
    row.appendChild(grow);
    const mv = el('button', { class: 'ck-btn xs ghost' }, ['⇄']); mv.title = T('ck_move_to_therapist');
    mv.onclick = (e) => { e.stopPropagation(); reassignDialog(m.user_id, c.child_id, null, _nm(c)); };
    row.appendChild(mv);
    // C-5 C2: the client chart is clinical — an admin without 'clinical' (not grantable yet, QA7) isn't offered it
    if (can('clinical')) { row.appendChild(el('span', { class: 'ck-chev' }, ['›'])); row.onclick = () => openClient(c.child_id, null, c.name, c.name_alt); } else row.classList.remove('ck-row-click');
    body.appendChild(row);
  });
  if (m.pre_parent != null) {
    body.appendChild(el('div', { class: 'ck-section' }, [T('ck_pre_parent_section')]));
    (m.pre_parent || []).forEach(sp => {
      const row = el('div', { class: 'ck-row ck-row-click' });
      row.appendChild(el('div', { class: 'ck-av sm' }, ['👤']));
      row.appendChild(el('div', { class: 'ck-grow' }, [el('div', { class: 'ck-nm' }, [_bdi(_nm(sp) || '—')])]));
      const mv = el('button', { class: 'ck-btn xs ghost' }, ['⇄']); mv.title = T('ck_move_to_therapist');
      mv.onclick = (e) => { e.stopPropagation(); reassignDialog(m.user_id, null, sp.sp_id, _nm(sp)); };
      row.appendChild(mv);
      if (can('clinical')) { row.appendChild(el('span', { class: 'ck-chev' }, ['›'])); row.onclick = () => openClient(null, sp.sp_id, sp.name, sp.name_alt); } else row.classList.remove('ck-row-click');   // C-5 C2
      body.appendChild(row);
    });
  }
  if (!(m.children || []).length && !(m.pre_parent || []).length) body.appendChild(el('div', { class: 'ck-empty' }, [T('ck_member_empty')]));
}

function renderClient(body) {
  const c = CS.client; if (!c) return;
  const section = (title, rows) => {
    body.appendChild(el('div', { class: 'ck-section' }, [title]));
    if (!rows.length) { body.appendChild(el('div', { class: 'ck-empty' }, [T('ck_none_visible_tiers')])); return; }
    rows.forEach(r => body.appendChild(r));
  };
  // C-3: the clinic's OWN captured contact hints (head-only, clinic-scoped on the server). A missing key means the server is not updated: show nothing, never a false "none".
  if (c.contact !== undefined) {
    const ct = c.contact;
    const bits = ct ? [[T('pl_f_parent_name'), dn(ct.name, ct.name_alt)], [T('pl_f_parent_email'), ct.email], [T('pl_f_parent_phone'), ct.phone]].filter(x => x[1]) : [];
    if (!bits.length) { body.appendChild(el('div', { class: 'ck-section' }, [T('ck_chart_contact')])); body.appendChild(el('div', { class: 'ck-empty' }, [T('pl_no_contact')])); }
    else section(T('ck_chart_contact'), bits.map(([lbl, val]) => el('div', { class: 'ck-row ck-contact' }, [el('div', { class: 'ck-grow' }, [el('div', { class: 'ck-sub' }, [lbl]), el('div', { class: 'ck-nm', dir: 'auto' }, [val])])])));
  }
  section(T('ck_section_reports'), (c.reports || []).map(r => {
    const row = el('div', { class: 'ck-row ck-row-click' });
    const nm = el('div', { class: 'ck-nm' }, [r.name || T('ck_untitled')]);
    if (r.is_draft) nm.appendChild(badge(T('ck_badge_draft'), 'b-draft'));
    row.appendChild(el('div', { class: 'ck-grow' }, [nm]));
    row.onclick = () => openArtifact('report', r.id, r.name || T('ck_report_fallback'));
    return row;
  }));
  section(T('ck_section_notes'), (c.notes || []).map(n => {
    const row = el('div', { class: 'ck-row ck-row-click' });
    const nm = el('div', { class: 'ck-nm' }, [n.title || T('ck_untitled')]);
    if (n.is_draft) nm.appendChild(badge(T('ck_badge_draft'), 'b-draft'));
    else if (n.published) nm.appendChild(badge(T('ck_badge_shared'), 'b-pub'));
    const grow = el('div', { class: 'ck-grow' }, [nm]);
    if (n.summary) { const sub = el('div', { class: 'ck-sub' }); sub.appendChild(el('span', { class: 'ck-sumbadge' }, [T('ck_parent_summary_badge')])); sub.appendChild(document.createTextNode(' ' + n.summary)); grow.appendChild(sub); }
    row.appendChild(grow);
    row.onclick = () => openArtifact('note', n.id, n.title || T('ck_note_fallback'), n.summary);
    return row;
  }));
  section(T('ck_section_home_exercises'), (c.homework || []).map(h => el('div', { class: 'ck-row' }, [el('div', { class: 'ck-grow' }, [el('div', { class: 'ck-nm' }, [T('ck_home_exercise_program')])])])));
}

// D4-3: rich Tiptap-doc → DOM (replaces D1's plain-text fallback). Plain-text fallback for non-doc (note) bodies.
function _ttAppendInline(parent, nodes) {
  (nodes || []).forEach(n => {
    if (n.type === 'text') {
      let node = document.createTextNode(n.text || '');
      (n.marks || []).forEach(m => { const tag = m.type === 'bold' ? 'strong' : m.type === 'italic' ? 'em' : m.type === 'code' ? 'code' : 'span'; const w = document.createElement(tag); w.appendChild(node); node = w; });
      parent.appendChild(node);
    } else if (n.type === 'hardBreak') parent.appendChild(document.createElement('br'));
  });
}
function _ttBlock(n) {
  if (!n) return null;
  if (n.type === 'paragraph') { const p = document.createElement('p'); p.dir = 'auto'; _ttAppendInline(p, n.content); return p; } // F-55c: per-paragraph direction
  if (n.type === 'heading') { const lvl = Math.min(3, Math.max(1, (n.attrs && n.attrs.level) || 2)); const h = document.createElement('h' + lvl); h.dir = 'auto'; _ttAppendInline(h, n.content); return h; }
  if (n.type === 'bulletList' || n.type === 'orderedList') {
    const list = document.createElement(n.type === 'orderedList' ? 'ol' : 'ul');
    (n.content || []).forEach(li => { if (li.type === 'listItem') { const item = document.createElement('li'); (li.content || []).forEach(cc => { if (cc.type === 'paragraph') _ttAppendInline(item, cc.content); else { const b = _ttBlock(cc); if (b) item.appendChild(b); } }); list.appendChild(item); } });
    return list;
  }
  if (n.type === 'blockquote') { const bq = document.createElement('blockquote'); (n.content || []).forEach(c => { const b = _ttBlock(c); if (b) bq.appendChild(b); }); return bq; }
  if (n.content) { const d = document.createElement('div'); (n.content || []).forEach(c => { const b = _ttBlock(c); if (b) d.appendChild(b); }); return d; }
  return null;
}
function renderTiptap(content) {
  const frag = document.createDocumentFragment();
  if (content && typeof content === 'object' && content.type === 'doc') {
    (content.content || []).forEach(n => { const b = _ttBlock(n); if (b) frag.appendChild(b); });
    if (!frag.childNodes.length) frag.appendChild(el('p', { class: 'ck-muted' }, [T('ck_empty_doc')]));
    return frag;
  }
  const txt = typeof content === 'string' ? content : (content == null ? '' : tiptapText(content));
  (txt || T('ck_no_readable_content')).split('\n').forEach(line => { if (line.trim()) frag.appendChild(el('p', { dir: 'auto' }, [line])); }); // F-55c
  return frag;
}

function renderArtifact(body) {
  const a = CS.artifact; if (!a) return;
  if (a.is_draft) body.appendChild(el('div', { class: 'ck-meta' }, [badge(T('ck_badge_draft'), 'b-draft')]));
  if (a.type === 'note') {
    // two layers — heads at clinical tier read the internal body BY DESIGN; label which is which
    body.appendChild(el('div', { class: 'ck-layer-lbl' }, [T('ck_internal_note_lbl')]));
    const inner = el('div', { class: 'ck-content' }); inner.appendChild(renderTiptap(a.content)); body.appendChild(inner);
    body.appendChild(el('div', { class: 'ck-layer-lbl shared' }, [T('ck_shared_with_parent_lbl')]));
    const shared = el('div', { class: 'ck-content ck-shared' });
    if (a.summary && String(a.summary).trim()) String(a.summary).split('\n').forEach(l => { if (l.trim()) shared.appendChild(el('p', { dir: 'auto' }, [l])); }); // F-55c
    else shared.appendChild(el('p', { class: 'ck-muted' }, [T('ck_nothing_shared')]));
    body.appendChild(shared);
  } else {
    const inner = el('div', { class: 'ck-content' }); inner.appendChild(renderTiptap(a.content)); body.appendChild(inner);
  }
}

// ══ D2 — manage actions (SC-C2 RPCs) ═════════════════════════════════════════
function activeMembers(exclude) { return (CS.overview?.members || []).filter(m => m.user_id !== exclude); }
function memberName(id) { const m = (CS.overview?.members || []).find(x => x.user_id === id); return m ? _nm(m) : id; }

// nested dialog above the cockpit overlay (own z-index so it sits over the sheet)
function ckDialog(title, buildBody, actions, onClose) {   // onClose (optional): runs once on ANY close (button, Esc, backdrop)
  const back = el('div', { class: 'ck-dlg-back' });
  const dlg = el('div', { class: 'ck-dlg' });
  // Escape closes ONLY this dialog (capture-phase + stopImmediatePropagation so the cockpit's Esc never fires);
  // the tap-outside is handled by back.onclick below (its backdrop sits above the cockpit overlay).
  // C-1 B6: only the TOPMOST dialog handles Esc — a lower dialog's listener (registered first) steps aside, so Esc on a
  // nested confirm / type picker closes just that one, not the dialog underneath.
  const _onKey = (e) => { if (e.key === 'Escape') { const all = document.querySelectorAll('.ck-dlg-back'); if (all.length && all[all.length - 1] !== back) return; e.stopImmediatePropagation(); close(); } };
  let _closed = false;
  const close = () => { document.removeEventListener('keydown', _onKey, true); back.remove(); if (!_closed) { _closed = true; if (onClose) onClose(); } };
  dlg.appendChild(el('div', { class: 'ck-dlg-head' }, [title]));
  const bd = el('div', { class: 'ck-dlg-body' });
  buildBody(bd, close);
  dlg.appendChild(bd);
  const foot = el('div', { class: 'ck-dlg-foot' });
  (actions || []).forEach(a => { const b = el('button', { class: 'ck-btn ' + (a.cls || '') }, [a.label]); if (a.disabled) b.disabled = true; b.onclick = () => a.onClick(close); foot.appendChild(b); });
  dlg.appendChild(foot);
  back.appendChild(dlg);
  document.body.appendChild(back);
  back.onclick = (e) => { if (e.target === back) close(); };   // tap outside the dialog → close only the dialog
  back.__ckClose = close;   // C-5 C1b: the admin no-access flip closes every open dialog the normal way
  document.addEventListener('keydown', _onKey, true);
  return { close };
}
function memberPicker(onPick, selected, exclude) {
  const wrap = el('div', { class: 'ck-pick' });
  activeMembers(exclude || null).forEach(m => {
    const chip = el('div', { class: 'ck-chip' + (m.user_id === selected ? ' sel' : ''), dir: 'auto' }, [_nm(m) || '—']);
    chip.onclick = () => { wrap.querySelectorAll('.ck-chip').forEach(x => x.classList.remove('sel')); chip.classList.add('sel'); onPick(m.user_id); };
    wrap.appendChild(chip);
  });
  if (!wrap.children.length) wrap.appendChild(el('div', { class: 'ck-empty' }, [T('ck_no_eligible_members')]));
  return wrap;
}
function transferNote() {
  return el('div', { class: 'ck-transfer' }, [
    el('div', { class: 'ck-t-yes' }, [T('ck_transfer_yes')]),
    el('div', { class: 'ck-t-no' }, [T('ck_transfer_no')]),
  ]);
}

// d — Add patient (Patient Lifecycle L2, evolved): client + optional parent/reason fields; assign OPTIONAL;
//     two exits — "Save unassigned" (→ patient_drafts draft, DIRECT table under the head RLS) and
//     "Create & assign" (today's head_create_patient; carrying the parent fields on the DIRECT path is L3).
//     Reused for Edit (pass a draft row as prefill + an onSaved callback to reload the pile).
const _PL_EMAIL_RE = /^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]+$/;
function createAssignDialog(prefillDraft, onSaved) {
  const editing = !!(prefillDraft && prefillDraft.id);
  const f = {
    client_name: prefillDraft?.client_name || '', parent_name: prefillDraft?.parent_name || '',
    client_name_alt: prefillDraft?.client_name_alt || '', parent_name_alt: prefillDraft?.parent_name_alt || '', // dual-language SC2
    parent_email: prefillDraft?.parent_email || '', parent_phone: prefillDraft?.parent_phone || '', reason: prefillDraft?.reason || '',
  };
  let assignee = null, errEl = null;
  const showErr = (m) => { if (errEl) { errEl.textContent = m; errEl.style.display = 'block'; } };
  ckDialog(T(editing ? 'pl_edit_title' : 'pl_add_title'), (bd) => {
    const field = (labelKey, key, opts) => {
      bd.appendChild(el('div', { class: 'ck-lbl' }, [T(labelKey)]));
      const inp = (opts && opts.area) ? el('textarea', { class: 'ck-inp' }) : el('input', { class: 'ck-inp', type: (opts && opts.type) || 'text' });
      inp.value = f[key]; inp.oninput = () => { f[key] = inp.value; };
      bd.appendChild(inp);
    };
    // dual-language SC2 — script-bound name fields: label by content script, place canonical in its
    // own script's field + alt in the other, save each back to its slot. (Promotion at save.)
    const _he = (s) => (H()._isHeb ? H()._isHeb(s) : /[֐-׿]/.test(s || ''));
    const scriptPair = (canonKey, altKey, base) => {
      const cHe = _he(f[canonKey] || '');
      [{ key: canonKey, he: cHe }, { key: altKey, he: !cHe }].forEach(sp => {
        bd.appendChild(el('div', { class: 'ck-lbl' }, [T(base + (sp.he ? '_he' : '_en'))]));
        const inp = el('input', { class: 'ck-inp', type: 'text' });
        inp.value = f[sp.key] || ''; inp.oninput = () => { f[sp.key] = inp.value; };
        bd.appendChild(inp);
      });
    };
    scriptPair('client_name', 'client_name_alt', 'dn_lbl_pt');
    scriptPair('parent_name', 'parent_name_alt', 'dn_lbl_pr');
    field('pl_f_parent_email', 'parent_email', { type: 'email' });
    field('pl_f_parent_phone', 'parent_phone', { type: 'tel' });
    field('pl_f_reason', 'reason', { area: true });
    errEl = el('div', { class: 'pl-err' }); errEl.style.display = 'none'; bd.appendChild(errEl);
    if (!editing) {
      bd.appendChild(el('div', { class: 'ck-lbl' }, [T('pl_assign_optional')]));
      const pick = el('div', { class: 'ck-pick' });
      activeMembers(null).forEach(m => {
        const chip = el('div', { class: 'ck-chip', dir: 'auto' }, [_nm(m) || '—']);
        chip.onclick = () => {
          const was = chip.classList.contains('sel');
          pick.querySelectorAll('.ck-chip').forEach(x => x.classList.remove('sel'));
          if (!was) { chip.classList.add('sel'); assignee = m.user_id; } else { assignee = null; }
          const ca = chip.closest('.ck-dlg')?.querySelector('.ck-dlg-foot .ck-btn.primary');
          if (ca) ca.disabled = !assignee;   // mock's locked state: Create & assign enabled only with a therapist
        };
        pick.appendChild(chip);
      });
      if (!pick.children.length) pick.appendChild(el('div', { class: 'ck-empty' }, [T('ck_no_eligible_members')]));
      bd.appendChild(pick);
      bd.appendChild(el('div', { class: 'ck-note' }, [T('pl_assign_hint')]));
    }
  }, editing
    ? [{ label: T('ck_cancel'), cls: 'ghost', onClick: c => c() },
       { label: T('pl_save_changes'), cls: 'primary', onClick: c => saveDraft(c, prefillDraft.id, f, showErr, onSaved) }]
    : [{ label: T('pl_save_unassigned'), cls: 'ghost', onClick: c => saveDraft(c, null, f, showErr, onSaved) },
       { label: T('ck_create_assign_btn'), cls: 'primary', onClick: async (c) => {
           _promoteNames(f);
           const name = (f.client_name || '').trim();
           if (!name || !assignee) { showErr(T('pl_err_name_req')); return; }
           const email = (f.parent_email || '').trim();
           if (email && !_PL_EMAIL_RE.test(email)) { showErr(T('pl_err_email')); return; }
           try {
             // create + assign in ONE transaction (parent fields + referral carried) — head_create_patient retired.
             const { data, error } = await supa().rpc('assign_patient_draft', { p_specialist: assignee, p_clinic: CS.overview.clinic_id, p_client_name: name, p_parent_name: (f.parent_name || '').trim() || null, p_parent_email: email || null, p_parent_phone: (f.parent_phone || '').trim() || null, p_reason: (f.reason || '').trim() || null, p_client_name_alt: (f.client_name_alt || '').trim() || null, p_parent_name_alt: (f.parent_name_alt || '').trim() || null });
             if (error) throw error; if (data && data.ok === false) throw new Error(data.error || 'assign_failed');
             toast(T('pl_toast_created_assigned'), 'success'); c(); await refreshDraftCount(CS.overview.clinic_id); await reloadOverview(); render();
           } catch (e) { console.error('❌ create+assign:', e); showErr(T('pl_err_save')); }
         } }]);
  // Create & assign starts DISABLED (no therapist picked yet) — the mock's shown state.
  if (!editing) { const backs = document.querySelectorAll('.ck-dlg-back'); const ca = backs[backs.length - 1]?.querySelector('.ck-dlg-foot .ck-btn.primary'); if (ca) ca.disabled = true; }
}

// Promotion (dual-language SC2): if only the alt-script field was filled, that value IS the
// canonical name — the canonical slot (client_name / parent_name) must never be empty when a name exists.
function _promoteNames(f) {
  if (!(f.client_name || '').trim() && (f.client_name_alt || '').trim()) { f.client_name = f.client_name_alt; f.client_name_alt = ''; }
  if (!(f.parent_name || '').trim() && (f.parent_name_alt || '').trim()) { f.parent_name = f.parent_name_alt; f.parent_name_alt = ''; }
}

// ── L2 draft helpers (direct-table writes under the head RLS; Amendment 2) ──
async function saveDraft(close, draftId, f, showErr, onSaved) {
  _promoteNames(f);
  const name = (f.client_name || '').trim();
  if (!name) { showErr(T('pl_err_name_req')); return; }
  const email = (f.parent_email || '').trim();
  if (email && !_PL_EMAIL_RE.test(email)) { showErr(T('pl_err_email')); return; }   // client layer (primary)
  const row = {
    client_name: name,
    client_name_alt: (f.client_name_alt || '').trim() || null,   // dual-language SC2
    parent_name: (f.parent_name || '').trim() || null,
    parent_name_alt: (f.parent_name_alt || '').trim() || null,   // dual-language SC2
    parent_email: email || null,
    parent_phone: (f.parent_phone || '').trim() || null,
    reason: (f.reason || '').trim() || null,
    updated_at: new Date().toISOString(),
  };
  try {
    if (draftId) { const { error } = await supa().from('patient_drafts').update(row).eq('id', draftId); if (error) throw error; }
    else { const { error } = await supa().from('patient_drafts').insert(Object.assign({}, row, { clinic_id: CS.overview.clinic_id, created_by: H().session?.id || null, status: 'draft' })); if (error) throw error; }
    toast(T('pl_toast_draft_saved'), 'success');
    close();
    await refreshDraftCount(CS.overview.clinic_id); render();
    if (typeof onSaved === 'function') onSaved();
  } catch (e) { console.error('❌ save draft:', e); showErr(T('pl_err_save')); }
}

async function refreshDraftCount(clinicId) {
  try { const { count } = await supa().from('patient_drafts').select('id', { count: 'exact', head: true }).eq('clinic_id', clinicId).eq('status', 'draft'); CS.unassignedCount = count ?? 0; }
  catch (e) { /* leave the prior count */ }
}

// The Unassigned pile — a popup over the cockpit (ckDialog overlay). Direct read under the head RLS.
function openDraftPile() {
  if (!CS.overview) return;
  let showArchived = false;
  ckDialog(T('pl_pile_title'), (bd) => {
    const list = el('div', {}); bd.appendChild(list);
    const tog = el('div', { class: 'pl-toggle-row' });
    const tbtn = el('button', { class: 'pl-toggle' }, [T('pl_show_archived')]);
    tbtn.onclick = () => { showArchived = !showArchived; tbtn.textContent = T(showArchived ? 'pl_hide_archived' : 'pl_show_archived'); loadList(); };
    tog.appendChild(tbtn); bd.appendChild(tog);
    async function loadList() {
      list.innerHTML = ''; list.appendChild(el('div', { class: 'ck-empty' }, ['…']));
      let q = supa().from('patient_drafts').select('*').eq('clinic_id', CS.overview.clinic_id);
      q = showArchived ? q.in('status', ['draft', 'assigned', 'archived']) : q.eq('status', 'draft');
      const { data, error } = await q.order('created_at', { ascending: true });
      list.innerHTML = '';
      if (error) { console.error('❌ load drafts:', error); list.appendChild(el('div', { class: 'ck-err' }, [T('pl_err_load')])); return; }
      const rows = data || [];
      if (!rows.length) { list.appendChild(el('div', { class: 'ck-empty' }, [T('pl_pile_empty')])); return; }
      rows.forEach(d => list.appendChild(draftCard(d, loadList)));
    }
    loadList();
  }, [{ label: T('pl_close'), cls: 'ghost', onClick: c => c() }]);
}

function draftCard(d, reload) {
  const arch = d.status !== 'draft';
  const card = el('div', { class: 'pl-draft' + (arch ? ' arch' : '') });
  const top = el('div', { class: 'pl-draft-top' });
  top.appendChild(el('div', { class: 'pl-draft-nm', dir: 'auto' }, [dn(d.client_name, d.client_name_alt) || '—']));
  if (arch) top.appendChild(el('span', { class: 'pl-arch-tag' }, [T(d.status === 'assigned' ? 'pl_tag_assigned' : 'pl_tag_archived')]));
  else { const days = Math.max(0, Math.floor((Date.now() - new Date(d.created_at).getTime()) / 86400000)); top.appendChild(el('span', { class: 'pl-days' + (days >= 14 ? ' warn' : '') }, [T('pl_days_waiting', { n: days })])); }
  card.appendChild(top);
  const bits = [dn(d.parent_name, d.parent_name_alt), d.parent_email, d.parent_phone].filter(Boolean).join(' · ');
  card.appendChild(el('div', { class: 'pl-hint' }, [bits || T('pl_no_contact')]));
  if (d.reason) card.appendChild(el('div', { class: 'pl-note' }, [d.reason]));
  if (!arch) {
    const acts = el('div', { class: 'pl-acts' });
    const asb = el('button', { class: 'ck-btn xs primary' }, [T('pl_assign')]); asb.onclick = () => openAssignPopup(d, reload); acts.appendChild(asb);
    const eb = el('button', { class: 'ck-btn xs ghost' }, [T('pl_edit')]); eb.onclick = () => createAssignDialog(d, reload); acts.appendChild(eb);
    const ab = el('button', { class: 'ck-btn xs ghost' }, [T('pl_archive')]); ab.onclick = () => archiveDraft(d.id, reload); acts.appendChild(ab);
    card.appendChild(acts);
  }
  return card;
}

async function archiveDraft(id, reload) {
  try { const { error } = await supa().from('patient_drafts').update({ status: 'archived', archived_at: new Date().toISOString(), updated_at: new Date().toISOString() }).eq('id', id); if (error) throw error; toast(T('pl_toast_archived'), 'success'); await refreshDraftCount(CS.overview.clinic_id); render(); reload(); }
  catch (e) { console.error('❌ archive draft:', e); toast(T('pl_err_save'), 'error'); }
}

// L3 — assign an EXISTING draft (pile [Assign]): member chips + carry-over + confirm → assign_patient_draft.
// Stacks over the pile; on success reloads the pile + overview (count/caseload change).
function openAssignPopup(draft, onDone) {
  let target = null;
  ckDialog(T('pl_assign_title', { name: dn(draft.client_name, draft.client_name_alt) || T('ck_client_word') }), (bd) => {
    bd.appendChild(el('div', { class: 'ck-lbl' }, [T('pl_assign_to')]));
    const pick = el('div', { class: 'ck-pick' });
    activeMembers(null).forEach(m => {
      const chip = el('div', { class: 'ck-chip', dir: 'auto' }, [_nm(m) || '—']);
      chip.onclick = () => { pick.querySelectorAll('.ck-chip').forEach(x => x.classList.remove('sel')); chip.classList.add('sel'); target = m.user_id; const cb = chip.closest('.ck-dlg')?.querySelector('.ck-dlg-foot .ck-btn.primary'); if (cb) cb.disabled = false; };
      pick.appendChild(chip);
    });
    if (!pick.children.length) pick.appendChild(el('div', { class: 'ck-empty' }, [T('ck_no_eligible_members')]));
    bd.appendChild(pick);
    const carry = el('div', { class: 'pl-carry' });
    carry.appendChild(el('div', { class: 'pl-carry-h' }, [T('pl_carry_title')]));
    const line = (k, v) => { const r = el('div', { class: 'pl-carry-line' }); r.appendChild(el('span', { class: 'pl-carry-k' }, [k])); r.appendChild(el('span', {}, [v])); carry.appendChild(r); };
    line(T('pl_carry_client'), dn(draft.client_name, draft.client_name_alt) || '—');
    const pbits = [dn(draft.parent_name, draft.parent_name_alt), draft.parent_email, draft.parent_phone].filter(Boolean).join(' · ');
    if (pbits) line(T('pl_carry_parent'), pbits);
    if (draft.reason) line(T('pl_carry_note'), T('pl_carry_note_v'));
    bd.appendChild(carry);
    bd.appendChild(el('div', { class: 'ck-note' }, [T('pl_assign_confirm_hint')]));
  }, [
    { label: T('ck_cancel'), cls: 'ghost', onClick: c => c() },
    { label: T('pl_assign_confirm'), cls: 'primary', onClick: async (c) => {
        if (!target) { toast(T('ck_toast_pick_therapist'), 'error'); return; }
        try { const { data, error } = await supa().rpc('assign_patient_draft', { p_specialist: target, p_draft_id: draft.id }); if (error) throw error; if (data && data.ok === false) throw new Error(data.error || 'assign_failed'); toast(T('pl_toast_assigned'), 'success'); c(); await refreshDraftCount(CS.overview.clinic_id); await reloadOverview(); render(); if (typeof onDone === 'function') onDone(); }
        catch (e) { console.error('❌ assign_patient_draft:', e); toast(T('pl_err_assign'), 'error'); }
      } },
  ]);
  const backs = document.querySelectorAll('.ck-dlg-back'); const cb = backs[backs.length - 1]?.querySelector('.ck-dlg-foot .ck-btn.primary'); if (cb) cb.disabled = true;
}

// ── L4 stat popups (overlay over the cockpit, never navigate; context in muted secondary text) ──
function _plDate(iso) { try { return _ckDate(new Date(iso), { month: 'short', year: 'numeric' }); } catch (e) { return ''; } }
function _plAv(name, cls) { return el('div', { class: 'ck-av' + (cls ? ' ' + cls : '') }, [(name || '?').trim().charAt(0).toUpperCase()]); }
function _plCountHdr(bd, n, key) { bd.appendChild(el('div', { class: 'ck-section' }, [T(key, { n: n })])); }

// Members — from the already-loaded overview (no query).
function openMembersPopup() {
  const ms = CS.overview?.members || [];
  ckDialog(T('pl_pop_members'), (bd) => {
    _plCountHdr(bd, ms.length, 'pl_n_members');
    if (!ms.length) { bd.appendChild(el('div', { class: 'ck-empty' }, [T('ck_member_empty')])); return; }
    ms.forEach(m => {
      const row = el('div', { class: 'ck-row' }); row.appendChild(_plAv(_nm(m)));
      const g = el('div', { class: 'ck-grow' });
      const nm = el('div', { class: 'ck-nm' }, [_bdi(_nm(m) || '—')]); if (m.role === 'head') nm.appendChild(badge(T('ck_badge_head'), 'b-head'));
      g.appendChild(nm);
      g.appendChild(el('div', { class: 'ck-sub' }, [T(m.role === 'head' ? 'clinic_role_head' : 'clinic_role_member') + ' · ' + T('clinic_tier_' + m.tier) + ' · ' + T('pl_n_patients', { n: m.patients ?? 0 })]));
      row.appendChild(g); bd.appendChild(row);
    });
  }, [{ label: T('pl_close'), cls: 'ghost', onClick: c => c() }]);
}

// Heads — the overview members filtered to role=head; "since" from joined_at.
function openHeadsPopup() {
  const hs = (CS.overview?.members || []).filter(m => m.role === 'head');
  ckDialog(T('pl_pop_heads'), (bd) => {
    _plCountHdr(bd, hs.length, 'pl_n_heads');
    if (!hs.length) { bd.appendChild(el('div', { class: 'ck-empty' }, ['—'])); return; }
    hs.forEach(m => {
      const row = el('div', { class: 'ck-row' }); row.appendChild(_plAv(_nm(m)));
      const g = el('div', { class: 'ck-grow' });
      g.appendChild(el('div', { class: 'ck-nm' }, [_bdi(_nm(m) || '—')]));
      g.appendChild(el('div', { class: 'ck-sub' }, [m.joined_at ? T('pl_head_since', { d: _plDate(m.joined_at) }) : T('clinic_role_head')]));
      row.appendChild(g); bd.appendChild(row);
    });
  }, [{ label: T('pl_close'), cls: 'ghost', onClick: c => c() }]);
}

// Patients — lazy via clinic_patients. Count header = LIST LENGTH (never echo the stat number, so a
// shared child appearing under two therapists never reads as a mismatch).
function openPatientsPopup() {
  ckDialog(T('pl_pop_patients'), (bd) => {
    let all = [], q = '', specFilter = null;   // specFilter null = All (SC4: filter by specialist_id, not the display name — no string collision)
    const search = el('input', { class: 'ck-inp', type: 'text', placeholder: T('pl_search_ph') }); search.style.marginBottom = '8px';
    search.oninput = () => { q = search.value.trim().toLowerCase(); renderList(); };
    bd.appendChild(search); withClear(search);   // F-107
    const chipsWrap = el('div', { class: 'ck-pick' }); chipsWrap.style.marginBottom = '8px'; bd.appendChild(chipsWrap);
    const hdr = el('div', { class: 'ck-section' }, ['']); bd.appendChild(hdr);
    const list = el('div', { class: 'pl-scroll' }); bd.appendChild(list);
    function buildChips() {
      chipsWrap.innerHTML = '';
      const mk = (label, val) => { const chip = el('div', { class: 'ck-chip' + (specFilter === val ? ' sel' : '') }, [label]); chip.onclick = () => { specFilter = val; buildChips(); renderList(); }; chipsWrap.appendChild(chip); };
      mk(T('pl_filter_all'), null);
      (CS.overview?.members || []).forEach(m => mk(_nm(m) || '—', m.user_id));   // SC4: chip value = specialist_id
    }
    function renderList() {
      const rows = all.filter(r => (!q || ((r.name || '') + ' ' + (r.name_alt || '')).toLowerCase().includes(q)) && (specFilter === null || r.specialist_id === specFilter));
      hdr.textContent = T('pl_n_patients', { n: rows.length });   // filtered length — never the stat number
      list.innerHTML = '';
      if (!rows.length) { list.appendChild(el('div', { class: 'ck-empty' }, [T('pl_no_match')])); return; }
      rows.forEach(r => {
        const row = el('div', { class: 'ck-row' }); row.appendChild(el('div', { class: 'ck-av sm' }, [r.kind === 'linked' ? '🧒' : '👤']));
        const g = el('div', { class: 'ck-grow' });
        const nm = el('div', { class: 'ck-nm' }, [_bdi(_nm(r) || '—')]); nm.appendChild(badge(T(r.kind === 'linked' ? 'pl_kind_linked' : 'pl_kind_pre'), r.kind === 'linked' ? 'b-full' : 'b-draft'));
        g.appendChild(nm);
        g.appendChild(el('div', { class: 'ck-sub' }, [T('pl_under', { name: dn(r.specialist_name, r.specialist_name_alt) || '—' })]));
        row.appendChild(g); list.appendChild(row);
      });
    }
    list.appendChild(el('div', { class: 'ck-empty' }, ['…']));
    (async () => {
      try { const { data, error } = await supa().rpc('clinic_patients'); if (error) throw error; all = (data || []).slice().sort((a, b) => _nm(a).localeCompare(_nm(b))); }   // SC4 sort-on-resolved
      catch (e) { console.error('❌ clinic_patients:', e); list.innerHTML = ''; hdr.textContent = ''; list.appendChild(el('div', { class: 'ck-err' }, [T('pl_err_load')])); return; }
      buildChips(); renderList();
    })();
  }, [{ label: T('pl_close'), cls: 'ghost', onClick: c => c() }]);
}

// e — reassign single
function reassignDialog(fromMember, childId, spId, name) {
  let target = activeMembers(fromMember)[0]?.user_id || null;
  ckDialog(T('ck_dlg_move_title', { name: name || T('ck_client_word') }), (bd) => {
    bd.appendChild(el('div', { class: 'ck-lbl' }, [T('ck_move_from_to', { name: memberName(fromMember) })]));
    bd.appendChild(memberPicker(id => target = id, target, fromMember));
    bd.appendChild(transferNote());
  }, [
    { label: T('ck_cancel'), cls: 'ghost', onClick: c => c() },
    { label: T('ck_confirm_move_btn'), cls: 'primary', onClick: async (c) => {
      if (!target) { toast(T('ck_toast_pick_therapist'), 'error'); return; }
      try { const { error } = await supa().rpc('reassign_patient', { p_clinic: CS.overview.clinic_id, p_sp_id: spId || null, p_child_id: childId || null, p_from: fromMember, p_to_member: target }); if (error) throw error; toast(T('ck_toast_reassigned'), 'success'); c(); await openMember({ user_id: fromMember, name: memberName(fromMember) }); }
      catch (e) { console.error('❌ reassign_patient:', e); toast(reassignErr(e), 'error'); }
    } },
  ]);
}
function reassignErr(e) { const m = (e && e.message) || ''; if (/23505|duplicate/i.test(m)) return T('ck_toast_already_treats'); return T('ck_toast_could_not_reassign'); }

// e — bulk leaving
function leavingDialog(fromMember) {
  let target = activeMembers(fromMember)[0]?.user_id || null;
  ckDialog(T('ck_dlg_leaving_title', { name: memberName(fromMember) }), (bd) => {
    bd.appendChild(el('div', { class: 'ck-lbl' }, [T('ck_reassign_whole_caseload_lbl')]));
    bd.appendChild(memberPicker(id => target = id, target, fromMember));
    bd.appendChild(el('div', { class: 'ck-note' }, [T('ck_leaving_note')]));
    bd.appendChild(transferNote());
  }, [
    { label: T('ck_cancel'), cls: 'ghost', onClick: c => c() },
    { label: T('ck_reassign_all_btn'), cls: 'primary', onClick: async (c) => {
      if (!target) { toast(T('ck_toast_pick_therapist'), 'error'); return; }
      try { const { error } = await supa().rpc('reassign_caseload_bulk', { p_clinic: CS.overview.clinic_id, p_from: fromMember, p_to_default: target }); if (error) throw error; toast(T('ck_toast_caseload_reassigned'), 'success'); c(); CS.member = null; CS.view = 'overview'; await reloadOverview(); render(); }
      catch (e) { console.error('❌ reassign_caseload_bulk:', e); toast(T('ck_toast_could_not_bulk_reassign'), 'error'); }
    } },
  ]);
}

// f + g + remove — manage view
function renderManage(body) {
  const o = CS.overview; if (!o) return;
  body.appendChild(el('div', { class: 'ck-meta' }, [
    CS.isOwner ? badge(T('ck_badge_owner'), 'b-owner') : badge(T('ck_badge_head'), 'b-head'),
    CS.isOwner ? ' ' + T('ck_manage_note_owner') : ' ' + T('ck_manage_note_head')]));
  const selfId = H().session?.id;
  (o.members || []).forEach(m => {
    const isSelf = m.user_id === selfId;
    const row = el('div', { class: 'ck-mrow' });
    const top = el('div', { class: 'ck-mrow-top' });
    const nm = el('div', { class: 'ck-nm' }, [_bdi(_nm(m) || '—')]); if (m.role === 'head') nm.appendChild(badge(T('ck_badge_head'), 'b-head'));
    top.appendChild(nm);
    if (CS.isOwner && !isSelf) {
      const rb = el('button', { class: 'ck-btn xs ' + (m.role === 'head' ? 'danger' : 'ghost') }, [m.role === 'head' ? T('ck_demote_btn') : T('ck_promote_btn')]);
      rb.onclick = () => doSetRole(m.user_id, m.role === 'head' ? 'member' : 'head'); top.appendChild(rb);
    }
    if (!isSelf && m.role !== 'head') {
      const rm = el('button', { class: 'ck-btn xs danger' }, [T('ck_remove_btn')]); rm.onclick = () => doRemove(m); top.appendChild(rm);
    }
    row.appendChild(top);
    const seg = el('div', { class: 'ck-seg' });
    ['schedule', 'clinical', 'full'].forEach(t => { const sp = el('span', { class: (m.tier === t ? 'on' : '') }, [T('clinic_tier_' + t)]); sp.onclick = () => { if (m.tier !== t) doSetTier(m.user_id, t); }; seg.appendChild(sp); });
    if (!CS.admin) row.appendChild(seg);   // C-5 C2: sharing tiers stay head-only (QA6 = A) — an admin with 'members' never sees the switch
    body.appendChild(row);
  });
  if (!CS.admin) _renderAdminsSection(body);   // C-5 C2 (QC2-1 = A): head-only — admin invites, removal and switches
}
// CLINIC-SETTINGS-IA SC1: the area pages that are real in SC1 (head only). People (SC2) and Money (the billing hub) are still their own views.
function renderArea(body) {
  const cid = CS.overview?.clinic_id;
  if (CS.area === 'msgcal') {
    body.appendChild(el('div', { class: 'ck-hint ck-area-lead' }, [T('cs_area_msgcal_d')]));
    const wa = el('div', { class: 'ck-wa-mount' }); body.appendChild(wa);
    mountWaCard(wa, { clinicId: cid, lang: H()._langNow });   // WhatsApp reminders card (WA-CLINIC-UI): head or owner only; idempotent, state in the card's own cache
  } else if (CS.area === 'profile') {
    body.appendChild(el('div', { class: 'ck-hint ck-area-lead' }, [T('cs_area_profile_d')]));
    const B = window.HUD_CLINIC_BRANDING;
    if (B && B.renderClinicBrandingCard) body.appendChild(B.renderClinicBrandingCard({ clinicId: cid, clinicName: CS.overview?.clinic_name, role: 'head', userId: H().session?.id }));
    else console.error('❌ clinic branding module missing');
  }
}
// ══ C-5 C2: clinic admins in Manage members (head-only) ══════════════════════
// Rows = clinic_roster (names, e-mails, invited e-mail; head-only) joined by member_id with clinic_admin_caps
// (member_id, status, capabilities; head-only, M2b members package). Switches show the capabilities that have a
// UI; a hidden one (contacts) is kept as it is on save. clinical is never offered (QA7 = B).
const CAP_UI = ['calendar', 'payments', 'billing', 'members', 'patients'].filter(c => CAP_LIVE.has(c));   // switches only for live capabilities
async function _loadAdmins() {
  CS.admins = { loading: true, rows: [] }; if (CS.view === 'manage') render();
  try {
    const cid = CS.overview?.clinic_id;
    const [ro, ca] = await Promise.all([supa().rpc('clinic_roster', { p_clinic_id: cid }), supa().rpc('clinic_admin_caps', { p_clinic: cid })]);
    if (ro.error) throw ro.error; if (ca.error) throw ca.error;
    const caps = Object.fromEntries((ca.data || []).map(r => [r.member_id, r]));
    CS.admins = { loading: false, rows: (ro.data || []).filter(m => m.member_role === 'admin' && (m.member_status === 'active' || m.member_status === 'invited'))
      .map(m => ({ id: m.member_id, user: m.member_user_id, status: m.member_status, name: m.member_name, email: m.member_email || m.member_invited_email, registered: !!m.member_user_id, caps: (caps[m.member_id] && caps[m.member_id].capabilities) || [] })) };
  } catch (e) { console.error('❌ clinic admins:', e); CS.admins = { loading: false, rows: [], err: true }; }
  if (CS.view === 'manage') render();
}
function _renderAdminsSection(body) {
  const A = CS.admins || { loading: true, rows: [] };
  body.appendChild(el('div', { class: 'ck-section' }, [T('ck_admins_section')]));
  const inv = el('div', { class: 'ck-mrow ck-admin-invite' });
  inv.appendChild(el('div', { class: 'ck-nm' }, [T('ck_admin_invite_section')]));
  inv.appendChild(el('div', { class: 'ck-hint' }, [T('ck_admin_invite_hint')]));
  const line = el('div', { class: 'ck-admin-invite-line' });
  const em = el('input', { type: 'email', class: 'ck-input', placeholder: T('clinic_invite_email_ph'), dir: 'ltr' });
  const send = el('button', { class: 'ck-btn primary sm' }, [T('clinic_invite_send_btn')]);
  send.onclick = async () => {
    const email = (em.value || '').trim();
    if (!email) { toast(T('clinic_invite_email_required'), 'error'); return; }
    send.disabled = true;
    const { error } = await supa().rpc('invite_clinic_member', { p_clinic_id: CS.overview.clinic_id, p_email: email, p_role: 'admin' });
    send.disabled = false;
    if (error) {
      if (error.code === 'HU001') toast(T('ck_admin_invite_hu001'), 'error', 6000);
      else if (error.code === '23505' || /already/i.test(error.message || '')) toast(T('clinic_invite_dup'), 'info');
      else { console.error('❌ invite_clinic_member (admin):', error); toast(T('clinic_invite_failed'), 'error'); }
      return;
    }
    em.value = '';
    // C-5 C3: the invite row exists; now the e-mail (the server re-checks the head and the pending row; never throws).
    const H = window.HUD || {};
    const mail = H.sendAdminInviteEmail ? await H.sendAdminInviteEmail(CS.overview.clinic_id, email) : { ok: false, error: 'no bridge' };
    if (mail && mail.ok) toast(T('clinic_invite_unregistered_state', { email }), 'info', 5000);
    else { console.error('❌ admin invite e-mail:', mail && (mail.status || mail.error)); toast(T('ck_admin_invite_mail_failed'), 'error', 6000); }
    _loadAdmins();
  };
  line.appendChild(em); line.appendChild(send); inv.appendChild(line);
  body.appendChild(inv);
  if (A.loading) { body.appendChild(_skeletonBody()); return; }
  if (A.err) { body.appendChild(el('div', { class: 'ck-err' }, [T('clinic_roster_error')])); return; }
  if (!A.rows.length) { body.appendChild(el('div', { class: 'ck-hint ck-admins-empty' }, [T('ck_admins_empty')])); return; }
  A.rows.forEach(a => body.appendChild(_adminRow(a)));
}
function _adminRow(a) {
  const row = el('div', { class: 'ck-mrow ck-admin-row' });
  const top = el('div', { class: 'ck-mrow-top' });
  const nm = el('div', { class: 'ck-nm' }, [_bdi(a.name || a.email || '—')]);
  if (a.status === 'invited') nm.appendChild(badge(a.registered ? T('clinic_status_invited') : T('clinic_status_pending_signup'), 'b-draft'));
  top.appendChild(nm);
  if (a.status === 'invited') {
    if (a.email) {
      // C3-L2: the invite row exists; the e-mail is what failed or was lost. The server re-checks the head and the pending row.
      const rs = el('button', { class: 'ck-btn xs ghost ck-admin-resend' }, [T('ck_admin_resend')]);
      rs.onclick = async () => {
        rs.disabled = true;
        const H = window.HUD || {};
        const mail = H.sendAdminInviteEmail ? await H.sendAdminInviteEmail(CS.overview.clinic_id, a.email) : { ok: false, error: 'no bridge' };
        rs.disabled = false;
        if (mail && mail.ok) toast(T('ck_admin_resent'), 'success');
        else { console.error('❌ admin invite e-mail (resend):', mail && (mail.status || mail.error)); toast(T('ck_admin_invite_mail_failed'), 'error', 6000); }
      };
      top.appendChild(rs);
    }
    const cx = el('button', { class: 'ck-btn xs ghost' }, [T('clinic_invite_cancel')]);
    cx.onclick = () => ckDialog(T('clinic_invite_cancel'), bd => bd.appendChild(el('div', { class: 'ck-note' }, [T('clinic_invite_cancel_confirm', { email: a.email || '' })])), [
      { label: T('pay_cancel'), cls: 'ghost', onClick: c => c() },
      { label: T('clinic_invite_cancel'), cls: 'danger', onClick: async c => {
        const { data, error } = await supa().rpc('cancel_clinic_invite', { p_member_id: a.id });
        if (error || data !== true) { console.error('❌ cancel_clinic_invite (admin):', error); toast(T('clinic_invite_cancel_failed'), 'error'); return; }
        c(); toast(T('clinic_invite_cancelled'), 'success'); _loadAdmins(); } }]);
    top.appendChild(cx);
  } else {
    const rm = el('button', { class: 'ck-btn xs danger' }, [T('ck_remove_btn')]);
    rm.onclick = () => ckDialog(T('ck_dlg_remove_title', { name: a.name || a.email || '' }), bd => bd.appendChild(el('div', { class: 'ck-note' }, [T('ck_admin_remove_note')])), [
      { label: T('pay_cancel'), cls: 'ghost', onClick: c => c() },
      { label: T('ck_remove_btn'), cls: 'danger', onClick: async c => {
        const { error } = await supa().rpc('remove_member', { p_clinic: CS.overview.clinic_id, p_user: a.user });
        if (error) { console.error('❌ remove_member (admin):', error); toast(T('ck_toast_could_not_remove'), 'error'); return; }
        c(); _loadAdmins(); } }]);
    top.appendChild(rm);
  }
  row.appendChild(top);
  if (a.email && a.name) row.appendChild(el('div', { class: 'ck-sub ck-admin-mail' }, [a.email]));
  row.appendChild(el('div', { class: 'ck-dlg-subh' }, [T('ck_admin_caps_label')]));
  const grid = el('div', { class: 'ck-caps' });
  CAP_UI.forEach(cap => {
    const on = a.caps.includes(cap);
    const sw = el('button', { class: 'ck-cap' + (on ? ' on' : ''), role: 'switch', 'aria-checked': on ? 'true' : 'false', 'data-cap': cap },
      [el('span', { class: 'ck-cap-tog' }, [el('span', { class: 'ck-cap-knob' })]), el('span', { class: 'ck-cap-txt' }, [el('span', { class: 'ck-cap-l' }, [T('ck_cap_' + cap)]), el('span', { class: 'ck-cap-d' }, [T('ck_cap_' + cap + '_d')])])]);
    sw.onclick = async () => {
      if (sw.disabled) return; sw.disabled = true;
      const want = on ? a.caps.filter(c => c !== cap) : a.caps.concat([cap]);   // hidden ones (contacts) ride along unchanged
      const { data, error } = await supa().rpc('set_admin_capabilities', { p_member_id: a.id, p_caps: want });
      sw.disabled = false;
      if (error) { console.error('❌ set_admin_capabilities:', error); toast(T('ck_cap_failed'), 'error'); return; }
      a.caps = Array.isArray(data) ? data : want; toast(T('ck_cap_saved'), 'success'); render();
    };
    grid.appendChild(sw);
  });
  row.appendChild(grid);
  return row;
}
async function doSetRole(userId, newRole) {
  try { const { error } = await supa().rpc('set_member_role', { p_clinic: CS.overview.clinic_id, p_user: userId, p_new_role: newRole }); if (error) throw error; toast(T('ck_toast_role_updated'), 'success'); await reloadOverview(); render(); }
  catch (e) { console.error('❌ set_member_role:', e); const msg = (e && e.message) || ''; toast(/owner/i.test(msg) ? T('ck_toast_owner_only_roles') : T('ck_toast_could_not_update_role'), 'error'); }
}
async function doSetTier(userId, tier) {
  try { const { error } = await supa().rpc('update_member_tier', { p_clinic: CS.overview.clinic_id, p_user: userId, p_tier: tier }); if (error) throw error; toast(T('ck_toast_tier_updated'), 'success'); await reloadOverview(); render(); }
  catch (e) { console.error('❌ update_member_tier:', e); toast(T('ck_toast_could_not_update_tier'), 'error'); }
}
function doRemove(m) {
  if ((m.patients || 0) > 0) { toast(T('ck_toast_reassign_first', { n: m.patients }), 'error', 5000); return; }
  ckDialog(T('ck_dlg_remove_title', { name: _nm(m) || T('ck_member_word') }), (bd) => { bd.appendChild(el('div', { class: 'ck-note' }, [T('ck_remove_note')])); }, [
    { label: T('ck_cancel'), cls: 'ghost', onClick: c => c() },
    { label: T('ck_remove_btn'), cls: 'danger', onClick: async (c) => {
      try { const { error } = await supa().rpc('remove_member', { p_clinic: CS.overview.clinic_id, p_user: m.user_id }); if (error) throw error; toast(T('ck_toast_member_removed'), 'success'); c(); await reloadOverview(); render(); }
      catch (e) { console.error('❌ remove_member:', e); const msg = (e && e.message) || ''; toast(/reassign/i.test(msg) ? T('ck_toast_reassign_caseload_first') : T('ck_toast_could_not_remove'), 'error'); }
    } },
  ]);
}
async function reloadOverview() { await loadOverview(); }

// ══ D3 — clinic calendar + attendance (clinic_calendar + mark_attendance) ════
// Full-width week view (ruling 1). Two modes off the SAME clinic_calendar RPC:
// time-grid (default) + attendance-review strip. One color per member. Read-only
// render; a head marks/overrides attendance on PAST appts via mark_attendance.
const MEMBER_COLORS = ['#0d9488', '#7c3aed', '#0284c7', '#f59e0b', '#db2777', '#0891b2', '#4f46e5', '#ea580c'];
function memberColor(id) { const ms = CS.overview?.members || []; const i = ms.findIndex(m => m.user_id === id); return MEMBER_COLORS[(i < 0 ? 0 : i) % MEMBER_COLORS.length]; }
// mirrors the app's _aptVisualState treatment (opacity + strike + pill) for the 4 live attendance values
const ATT = {
  attended: { glyph: '✓', labelKey: 'apt_attendance_attended', cls: 'a-att', opacity: '.72', strike: false },
  no_show: { glyph: '✗', labelKey: 'apt_attendance_no_show', cls: 'a-no', opacity: '.66', strike: false },
  patient_cancelled: { glyph: '⊘', labelKey: 'apt_attendance_patient_cancelled', cls: 'a-pc', opacity: '.5', strike: true },
  therapist_cancelled: { glyph: '⊘', labelKey: 'apt_attendance_clinic_cancelled', cls: 'a-tc', opacity: '.5', strike: true },
};
function attMeta(v) { return ATT[v] || { glyph: '', labelKey: 'ck_unmarked', cls: 'a-un', opacity: null, strike: false }; }
// B-status: payment badge map (head-only; from clinic_calendar.payment_status). glyph + colour class.
const PAY = {
  awaiting:      { glyph: '💳', labelKey: 'pay_awaiting',     cls: 'p-aw' },
  follow_up:     { glyph: '↻',  labelKey: 'pay_follow_up',    cls: 'p-fu' },
  paid:          { glyph: '✓',  labelKey: 'pay_paid',         cls: 'p-paid' },
  waived:        { glyph: '⊘',  labelKey: 'pay_waived',       cls: 'p-wv' },
  not_billable:  { glyph: '—',  labelKey: 'pay_not_billable', cls: 'p-nb' },
};
function payMeta(v) { return PAY[v] || null; }
// B2.5 — status dot colour for the 3-lane rung (pills collapse to one saturated dot): attendance first
// (attended green / no-show orange / cancelled slate), else payment (awaiting amber / follow-up blue /
// paid green / waived slate). Null → no dot. Saturated hexes (not the pale pill backgrounds) so a 6px dot reads.
function _ckStatusDot(r) {
  const a = r.attendance;
  if (a === 'attended') return '#16a34a';
  if (a === 'no_show') return '#ea580c';
  if (a === 'patient_cancelled' || a === 'therapist_cancelled') return '#94a3b8';
  const ps = r.payment_status;
  if (ps === 'awaiting') return '#f59e0b';
  if (ps === 'follow_up') return '#0284c7';
  if (ps === 'paid') return '#16a34a';
  if (ps === 'waived') return '#94a3b8';
  return null;
}
// B2.5 — fine pointer (mouse) → hover borrows; coarse (touch) → hover never fires, first tap borrows.
function _ckFinePointer() { try { return !(window.matchMedia && window.matchMedia('(hover: none)').matches); } catch (_) { return true; } }
// C-2 — the cockpit's width band, same thresholds as the notes sheet: phone <768 · tablet 768–1023 · desktop ≥1024.
// Tablet takes the desktop day view (columns per therapist); only phone switches to the chip switcher.
function _ckBand() { const w = window.innerWidth; return w < 768 ? 'phone' : w < 1024 ? 'tablet' : 'desktop'; }
// B-status queue aging: days since attendance_marked_at (the enter-queue moment, Q1). fresh <7 / warn 7–21 / stale >21.
function agingDays(iso) { if (!iso) return null; const ms = Date.now() - new Date(iso).getTime(); return ms < 0 ? 0 : Math.floor(ms / 86400000); }
function agingCls(d) { return d == null ? 'fresh' : d > 21 ? 'stale' : d >= 7 ? 'warn' : 'fresh'; }
// Payments queue (Option B): money helpers. charged is SERVER-computed in clinic_payment_queue
// (round(billed_price*COALESCE(charge_fraction,1),2)); the UI never re-derives it.
function _ils(n) { try { return '₪' + Number(n).toLocaleString('en-US'); } catch (_) { return '₪' + n; } }
// Trailing amount block. Unclassified (billed_price NULL) → "set type first" (amber). Partial no-show
// (charge_fraction 0.5 / 0) → the charged figure + a small tag.
function _amtBlock(r) {
  if (r.billed_price == null) return el('div', { class: 'ck-amt unset' }, [T('pay_set_type_first')]);
  const amt = el('div', { class: 'ck-amt' }, [_ils(r.charged != null ? r.charged : 0)]);
  const nf = r.charge_fraction == null ? 1 : Number(r.charge_fraction);
  if (nf === 0.5) amt.appendChild(el('span', { class: 'ck-amt-tag' }, [T('pay_charged_frac', { x: Number(r.billed_price).toLocaleString('en-US') })]));
  else if (nf === 0) amt.appendChild(el('span', { class: 'ck-amt-tag' }, [T('pay_charged_none')]));
  return amt;
}
// Session-type chip for the badges row. Unclassified → amber chip that opens the SAME classification
// dialog the calendar tap uses (onApptTap); the queue is single-clinic so r.clinic_id = the active clinic.
function _typeChip(r, onChanged) {
  if (r.billed_price == null || !r.session_type_id) {
    const chip = el('span', { class: 'ck-badge ck-unclass' }, [T('pay_type_unset_chip')]);
    if (!can('calendar')) return chip;   // C-5 C2: setting the type is the calendar's (QA2 = A)
    chip.onclick = () => { r.clinic_id = r.clinic_id || (CS.overview && CS.overview.clinic_id) || null; onApptTap(r, onChanged); };
    return chip;
  }
  return el('span', { class: 'ck-badge ck-type' }, [r.session_type_name || T('pay_type_unset')]);
}
// light pastel from a member's hex (parity with _aptTintBg: hex + alpha). fill='1F' (~12%), wash='0A' (~4%).
function memberTint(id, alpha) { const c = memberColor(id); return /^#[0-9a-fA-F]{6}$/.test(c) ? c + alpha : c; }
const DAY_KEYS = ['cal_day_su', 'cal_day_mo', 'cal_day_tu', 'cal_day_we', 'cal_day_th', 'cal_day_fr', 'cal_day_sa']; // reuse the app's weekday dict
// C3/B1 (2026-09-08): UN-PINNED. The cockpit now reads the shared HUD.CAL geometry (56px/hr) at RENDER
// time (`window.HUD.CAL`, bridged from index.html:2782 — {SLOT_H:14,HOUR_H:56,GRID_H:1344,GUTTER:48}).
// The old CK_* fork (96px/hr) is retired per the skill invariant "both calendars use HUD.CAL; never fork
// the numbers." Read at render time (not module load) because window.HUD may not exist at module init;
// `_ckCal()` falls back to literal HUD.CAL values if the bridge isn't ready at first paint. The lane-packer
// (HUD._calLayoutDay) was always shared. The gutter stays 48 — matches the .ck-grid-gut/.ck-nowline CSS.
function _ckCal() { return (H() && H().CAL) || { SLOT_H: 14, HOUR_H: 56, GRID_H: 1344, GUTTER: 48 }; }
let _ckNowInt = null;              // now-line refresh interval (cleared on close)
let _ckBorrowCollapse = null;      // B2.5 — collapse fn of the currently hover/tap-borrowed event (one at a time)

function ymd(d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); }
// Sunday-first week (Israel work week Sun–Thu; matches the app's existing mini-calendar). 7 columns, nothing hidden.
// DEFERRED app-wide: locale-aware week start (Mon-first for non-IL locales) — applies to every mini-calendar too;
// solve once, everywhere, when international users exist (see clinics-2026-08-19/phase3-d4-polish-backlog.md).
function weekStartDay(d) { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); x.setHours(0, 0, 0, 0); return x; }
function addDays(ymdStr, n) { const d = new Date(ymdStr + 'T00:00:00'); d.setDate(d.getDate() + n); return ymd(d); }
function parseHM(t) { if (t == null) return null; const m = String(t).match(/(\d{1,2}):(\d{2})/); return m ? (+m[1]) * 60 + (+m[2]) : null; }
function dayIndex(ymdStr, weekStart) { const a = new Date(weekStart + 'T00:00:00'), b = new Date(ymdStr + 'T00:00:00'); return Math.round((b - a) / 86400000); }
function nowMinutes() { const n = new Date(); return n.getHours() * 60 + n.getMinutes(); }
// C-1: the ONE started/ended rule, shared with the specialist app and mirroring the SQL helpers (HUD._apptHasEnded /
// _apptHasStarted: the session's own zone, end = end_time / the range's 2nd HH:MM / start + 60).
// C-2: a time range inside text ("09:00 – 09:45") as an LTR-isolated span, so Hebrew never reverses it.
function _tr(t) { return t ? el('span', { class: 'ck-tr' }, [t]) : null; }
// F-101: the "therapist, date, time, title" line of a payment/worklist row as ISOLATED pieces (names and titles are data of either script; a date in the app language).
// Separators are drawn by CSS between the pieces (never as text), so a wrapped line never strands one and a Hebrew page never moves one across a name.
function _ckRowDate(ymdStr) {   // the app-language date for a YYYY-MM-DD; the raw string when it does not parse
  try {
    const day = new Date(String(ymdStr).slice(0, 10) + 'T00:00:00');
    if (isNaN(day.getTime())) return String(ymdStr || '');
    return _ckDate(day, day.getFullYear() === new Date().getFullYear() ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (e) { return String(ymdStr || ''); }
}
function _ckRowMeta(r, opts) {   // opts: { dn: true } resolves member names through dn(); { title: false } / { time: false } leave that piece out
  // returns [line, title?]: the line holds therapist, date and time and never wraps (phone: one line); the title is its own item (phone: always its own second line, so it can never collide with the time range)
  const pieces = [];
  const add = (node) => { if (node) pieces.push(el('span', { class: 'ck-mp' }, [node])); };
  add(_bdi((opts && opts.dn ? dn(r.member_name, r.member_name_alt) : r.member_name) || ''));
  if (r.date) add(_bdi(_ckRowDate(r.date)));
  if (!(opts && opts.time === false)) add(_tr(r.time));
  const out = [el('span', { class: 'ck-ml' }, pieces)];
  if (r.title && !(opts && opts.title === false)) out.push(el('span', { class: 'ck-mt' }, [_bdi(r.title)]));
  return out;
}
function isPastAppt(row) { return H()._apptHasEnded(row); }
function hasStarted(row) { return H()._apptHasStarted(row); }

function _ckInitialView(w) { return w < 768 ? 'day' : 'week'; }
async function openCalendar() {
  CS.view = 'calendar'; CS.member = null; CS.client = null; CS.artifact = null; CS.err = null;
  if (!CS.cal) CS.cal = { weekStart: ymd(weekStartDay(new Date())), mode: 'grid', view: 'week', hidden: {}, rows: [], picked: null, dayDate: null, activeMember: null };
  // F-69b (supersedes D1 ruling 4 on phones): a fresh open lands on DAY on a phone (<768), WEEK on tablet and desktop.
  CS.cal.view = _ckInitialView(window.innerWidth);
  CS.cal.search = null;   // COCKPIT-CAL-SEARCH: a fresh open never resumes a search
  if (CS.cal.view === 'day') { const t = ymd(new Date()); CS.cal.dayDate = _ckDayInWeek(t) ? t : CS.cal.weekStart; }
  CS.cal._scrolled = false;  // B4: a FRESH calendar open lands on the first-session default; every in-view update after preserves scroll
  await loadCalendar(CS.cal.weekStart);
}
// F-48 guard: a load that a newer load (another week / tab / month) has overtaken while in flight is dropped on
// arrival, so a slow older response can never paint its rows under the newer label.
const _ckLoadSeq = { cal: 0, pay: 0, po: 0 };
async function loadCalendar(weekStart) {
  const seq = ++_ckLoadSeq.cal;
  CS.cal.weekStart = weekStart; CS.loading = true; CS.err = null; render();
  let rows = null, err = null;
  try { const { data, error } = await supa().rpc('clinic_calendar', { p_week_start: weekStart }); if (error) throw error; rows = data || []; }
  catch (e) { console.error('❌ clinic_calendar:', e); err = T('ck_err_load_calendar'); }
  if (seq !== _ckLoadSeq.cal) return;
  if (err) CS.err = err; else CS.cal.rows = rows;
  // SC-B4c door A: the untyped/unmarked indicator for the DISPLAYED month (month of weekStart). Passive
  // count → no log (STABLE). Re-fetched every load so a fix drops the chip on goBack. Cheap (~0.3ms).
  // C-1: the week that contains TODAY counts today's month (a week starting in the previous month used to count
  // that month — e.g. Sun Sep 27 → September — so this month's ended-unmarked sessions were missing from the chip).
  const _today = ymd(new Date()), _wkEnd = ymd(new Date(new Date(weekStart + 'T00:00:00').getTime() + 6 * 864e5));
  const _chipMonth = (_today >= weekStart && _today <= _wkEnd) ? _monthStart(new Date(_today + 'T00:00:00')) : _monthStart(new Date(weekStart + 'T00:00:00'));
  let counts = null, countsOk = false;
  try { const { data: c } = await supa().rpc('clinic_worklist_counts', { p_clinic: CS.overview?.clinic_id, p_month: _chipMonth }); counts = c || null; countsOk = true; } catch (e) { counts = null; }
  if (seq !== _ckLoadSeq.cal) return;
  CS.cal.counts = counts; if (countsOk) CS.cal.countsMonth = _chipMonth;
  CS.loading = false; render();
}
function shiftWeek(delta) { CS.cal.picked = null; loadCalendar(addDays(CS.cal.weekStart, delta * 7)); } // B3: stepping clears the picked-day marker
// D1 — day-view navigation. loadCalendar still fetches by WEEK (no RPC change); a day-step / jump that lands
// outside the loaded week re-fetches that week, else re-renders in place (B4 preserves scroll).
function _ckDayInWeek(ds) { const i = dayIndex(ds, CS.cal.weekStart); return i >= 0 && i < 7; }
function shiftDay(delta) { const d = addDays(CS.cal.dayDate, delta); CS.cal.dayDate = d; if (_ckDayInWeek(d)) render(); else loadCalendar(ymd(weekStartDay(new Date(d + 'T00:00:00')))); }
function _ckGoDay(ds) { CS.cal.view = 'day'; CS.cal.dayDate = ds; if (_ckDayInWeek(ds)) render(); else loadCalendar(ymd(weekStartDay(new Date(ds + 'T00:00:00')))); }
function _ckEnterDayMode() { const t = ymd(new Date()); _ckGoDay(_ckDayInWeek(t) ? t : CS.cal.weekStart); } // Week→Day toggle: today if in view, else the loaded week's first day
function visibleRows() { return (CS.cal.rows || []).filter(r => !CS.cal.hidden[r.member] && dayIndex(r.date, CS.cal.weekStart) >= 0 && dayIndex(r.date, CS.cal.weekStart) < 7); }

// ── C3/B3 — cockpit-local date picker. A PORT of the app's C2 picker (_openCalPicker), NOT bridged to it
// (§7b: cockpit is bundled, the app picker is inline in index.html on un-bridged globals). Week-view-only
// contract: pick a day → load the week CONTAINING it and mark that day in the header. Body-mounted fixed
// popover at z 1500 (between the cockpit overlay 1400 and dialogs 1600) so .ck-shell{overflow:hidden} can't
// clip it. RTL: nav glyphs are swapped once by _isRtl (matches the adjacent week-nav arrows + the app
// picker) — a single manual flip, no CSS transform on top, so no double-flip. No has-event dots (the cockpit
// only holds the current week's rows; cross-month dots would need a fetch the greenlight forbids). No new
// strings (reuses cal_day_*/cal_today). No RPC change (loadCalendar already takes any weekStart).
let _ckPickerEl = null, _ckPickerCleanup = null;
function _closeCkPicker() {
  if (_ckPickerCleanup) { try { _ckPickerCleanup(); } catch (_) {} }
  _ckPickerCleanup = null;
  if (_ckPickerEl && _ckPickerEl.parentNode) _ckPickerEl.remove();
  _ckPickerEl = null;
}
function _ckPickerGrid(y, m, onPick) {
  const todayStr = ymd(new Date()), picked = CS.cal && CS.cal.picked;
  const dIM = new Date(y, m + 1, 0).getDate(), fD = new Date(y, m, 1).getDay();
  const g = el('div', { class: 'ck-picker-grid' });
  DAY_KEYS.forEach(k => g.appendChild(el('div', { class: 'ck-picker-dl' }, [T(k)])));
  for (let i = 0; i < fD; i++) g.appendChild(el('div', { class: 'ck-picker-day empty' }));
  for (let day = 1; day <= dIM; day++) {
    const ds = y + '-' + String(m + 1).padStart(2, '0') + '-' + String(day).padStart(2, '0');
    const cell = el('div', { class: 'ck-picker-day' });
    cell.appendChild(el('div', { class: 'ck-picker-num' + (ds === todayStr ? ' today' : '') + (ds === picked ? ' selected' : '') }, [String(day)]));
    cell.onclick = e => { e.stopPropagation(); onPick(ds); };
    g.appendChild(cell);
  }
  return g;
}
function _openCkPicker(anchorEl) {
  _closeCkPicker();
  const _rtl = !!(H()._isRtl && H()._isRtl());
  const base = new Date((CS.cal && CS.cal.weekStart ? CS.cal.weekStart : ymd(weekStartDay(new Date()))) + 'T00:00:00');
  let py = base.getFullYear(), pm = base.getMonth();
  const onPick = ds => { _closeCkPicker(); if (CS.cal.view === 'day') { _ckGoDay(ds); } else { CS.cal.picked = ds; loadCalendar(ymd(weekStartDay(new Date(ds + 'T00:00:00')))); } }; // D1 ruling 6: day mode → that day
  const pop = el('div', { class: 'ck-picker' });
  const hd = el('div', { class: 'ck-picker-hd' });
  const prev = el('button', { class: 'ck-picker-nav' }, ['‹']);
  const lbl = el('span', { class: 'ck-picker-mo' }, []);
  const next = el('button', { class: 'ck-picker-nav' }, ['›']);
  const todayBtn = el('button', { class: 'ck-picker-today' }, [T('cal_today')]);
  hd.appendChild(prev); hd.appendChild(lbl); hd.appendChild(next); hd.appendChild(todayBtn);
  pop.appendChild(hd);
  const gridWrap = el('div', {}); pop.appendChild(gridWrap);
  const _paint = () => {
    lbl.textContent = _ckDate(new Date(py, pm, 1), { month: 'long', year: 'numeric' });
    gridWrap.replaceChildren(_ckPickerGrid(py, pm, onPick));
  };
  const _flip = d => { pm += d; if (pm < 0) { pm = 11; py--; } else if (pm > 11) { pm = 0; py++; } _paint(); };
  prev.onclick = e => { e.stopPropagation(); _flip(-1); };
  next.onclick = e => { e.stopPropagation(); _flip(1); };
  todayBtn.onclick = e => { e.stopPropagation(); onPick(ymd(new Date())); };
  _paint();
  document.body.appendChild(pop);
  // Anchor under the label, RTL-aware, clamped into the viewport.
  const r = anchorEl.getBoundingClientRect();
  const pw = pop.offsetWidth || 300, ph = pop.offsetHeight || 320;
  let left = _rtl ? (r.right - pw) : r.left;
  left = Math.max(8, Math.min(left, window.innerWidth - pw - 8));
  let top = r.bottom + 6;
  if (top + ph > window.innerHeight - 8) top = Math.max(8, r.top - ph - 6);
  pop.style.left = left + 'px'; pop.style.top = top + 'px';
  _ckPickerEl = pop;
  // Dismiss: outside-pointerdown (capture, next tick so the opening click doesn't close it), Escape, resize.
  const onDown = ev => { if (!pop.contains(ev.target) && ev.target !== anchorEl && !anchorEl.contains(ev.target)) _closeCkPicker(); };
  const onKey = ev => { if (ev.key === 'Escape') { ev.stopPropagation(); _closeCkPicker(); } };  // capture + stop → closes the picker, not the cockpit
  const onResize = () => _closeCkPicker();
  setTimeout(() => { if (_ckPickerEl === pop) document.addEventListener('pointerdown', onDown, true); }, 0);
  document.addEventListener('keydown', onKey, true);
  window.addEventListener('resize', onResize);
  _ckPickerCleanup = () => { document.removeEventListener('pointerdown', onDown, true); document.removeEventListener('keydown', onKey, true); window.removeEventListener('resize', onResize); };
}

// F-48 — fixed date slot: the nav bars are grids [‹][slot][›]([This week]), so the arrows never move when the label
// changes length. The slot width is the WIDEST label the bar can show, measured once per (kind, language) in an
// off-screen copy of the label (same class, so the real font and the ▾ caret count) and cached. A language switch
// keys a new entry; until the web fonts have loaded the result isn't cached (fallback metrics would stick).
const _ckSlotCache = {};
const _ckWeekLbl = (ws) => { const we = new Date(ws.getTime() + 6 * 864e5); const f = d => _ckDate(d, { month: 'short', day: 'numeric' }); return f(ws) + ' – ' + f(we); };
const _ckDayLbl = (d) => _ckDate(d, { weekday: 'short', month: 'short', day: 'numeric' });
function _ckSlotPx(kind) {
  const key = kind + '|' + (H()._langNow || '');
  if (_ckSlotCache[key]) return _ckSlotCache[key];
  const labels = [];
  const y = new Date().getFullYear();
  if (kind === 'week') { for (let d = new Date(y, 0, 1); d.getFullYear() <= y; d = new Date(d.getTime() + 864e5)) if (d.getDay() === 0) labels.push(_ckWeekLbl(d)); }
  else if (kind === 'day') { for (let m = 0; m < 12; m++) for (let wd = 0; wd < 7; wd++) labels.push(_ckDayLbl(new Date(y, m, 22 + ((wd - new Date(y, m, 22).getDay() + 7) % 7)))); }
  else { for (let m = 0; m < 12; m++) labels.push(_monthLabel(y + '-' + String(m + 1).padStart(2, '0') + '-01')); }
  const host = (_overlay && _overlay.querySelector('.ck-shell')) || _overlay || document.body;   // inside the shell: its font + dir
  // the SAME tag as the real label: button rules (e.g. the RTL font) differ from a div's
  const probe = el(kind === 'month' ? 'div' : 'button', { class: 'ck-cal-wk', style: 'position:absolute;visibility:hidden;inset-inline-start:-9999px;top:0;width:auto;min-width:0;overflow:visible' });
  const txt = el('span', {}, ['']); probe.appendChild(txt);
  if (kind !== 'month') probe.appendChild(el('span', { class: 'ck-cal-wk-caret' }, ['▾']));
  host.appendChild(probe);
  let w = 0; for (const s of labels) { txt.textContent = s; w = Math.max(w, probe.getBoundingClientRect().width); }
  probe.remove();
  const px = Math.ceil(w) + 2;
  _ckSlotCache[key] = px;   // re-measured after any late web-font load (loadingdone below)
  return px;
}
// A web font (e.g. Heebo on the first Hebrew label) can finish loading after a measurement: drop the cache and
// re-measure the open bars in place (no render(): a repaint would reset the calendar's scroll).
if (typeof document !== 'undefined' && document.fonts && document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', () => {
  for (const k in _ckSlotCache) delete _ckSlotCache[k];
  if (_overlay) _overlay.querySelectorAll('[data-ck-slot]').forEach(b => b.style.setProperty('--ck-slot', _ckSlotPx(b.dataset.ckSlot) + 'px'));
});

function renderCalendarView(body) {
  const cal = CS.cal; if (!cal) return;
  const isPhone = _ckBand() === 'phone';
  // legend / per-member filter — shown in week (both form factors) and desktop-day; HIDDEN on phone-day
  // (D2: the chip switcher IS the roster there).
  if (!(isPhone && cal.view === 'day')) {
    const legend = el('div', { class: 'ck-cal-legend' });
    (CS.overview?.members || []).forEach(m => {
      const on = !cal.hidden[m.user_id];
      const chip = el('div', { class: 'ck-cchip' + (on ? '' : ' off') });
      chip.appendChild(el('span', { class: 'ck-sw', style: 'background:' + memberColor(m.user_id) }));
      chip.appendChild(_bdi(_nm(m) || '—'));
      chip.onclick = () => { cal.hidden[m.user_id] = on; render(); };
      legend.appendChild(chip);
    });
    body.appendChild(legend);
  }
  // toolbar: nav (day- or week-aware) + Week/Day toggle + (week only) grid/strip toggle
  const bar = el('div', { class: 'ck-cal-bar' });
  const nav = el('div', { class: 'ck-cal-nav' });
  const isDay = cal.view === 'day';
  const prev = el('button', { class: 'ck-btn xs ghost' }, ['‹']); prev.onclick = () => isDay ? shiftDay(-1) : shiftWeek(-1);
  const today = el('button', { class: 'ck-btn xs ghost' }, [T('ck_this_week')]); today.onclick = () => { if (isDay) { _ckGoDay(ymd(new Date())); } else { cal.picked = null; loadCalendar(ymd(weekStartDay(new Date()))); } };
  const next = el('button', { class: 'ck-btn xs ghost' }, ['›']); next.onclick = () => isDay ? shiftDay(1) : shiftWeek(1);
  const ws = new Date(cal.weekStart + 'T00:00:00');
  // The label is the date-picker trigger. Day mode → the focused day; week mode → the week range.
  const wkBtn = el('button', { class: 'ck-cal-wk' });
  const _lblTxt = isDay ? _ckDayLbl(new Date(cal.dayDate + 'T00:00:00')) : _ckWeekLbl(ws);
  nav.dataset.ckSlot = isDay ? 'day' : 'week'; nav.style.setProperty('--ck-slot', _ckSlotPx(nav.dataset.ckSlot) + 'px');   // F-48
  wkBtn.appendChild(el('span', { class: 'ck-cal-wk-t' }, [_lblTxt]));
  wkBtn.appendChild(el('span', { class: 'ck-cal-wk-caret' }, ['▾']));
  wkBtn.onclick = (e) => { e.stopPropagation(); if (_ckPickerEl) { _closeCkPicker(); return; } _openCkPicker(wkBtn); }; // toggle
  nav.appendChild(prev); nav.appendChild(wkBtn); nav.appendChild(next); nav.appendChild(today);
  bar.appendChild(nav);
  // Week/Day toggle — D2: on BOTH form factors.
  {
    const vseg = el('div', { class: 'ck-seg' });
    [['week', T('ck_view_week')], ['day', T('ck_view_day')]].forEach(([v, lbl]) => {
      const sp = el('span', { class: (cal.view === v ? 'on' : '') }, [lbl]);
      sp.onclick = () => { if (cal.view === v) return; if (v === 'day') _ckEnterDayMode(); else { cal.view = 'week'; render(); } };
      vseg.appendChild(sp);
    });
    bar.appendChild(vseg);
  }
  // grid/strip toggle — WEEK view only (day view is time-grid only; the strip stays week-scoped, ruling 8).
  if (!isDay) {
    const seg = el('div', { class: 'ck-seg' });
    [['grid', T('ck_time_grid')], ['strip', T('ck_attendance_review')]].forEach(([mode, lbl]) => {
      const sp = el('span', { class: (cal.mode === mode ? 'on' : '') }, [lbl]); sp.onclick = () => { if (cal.mode !== mode) { cal.mode = mode; render(); } }; seg.appendChild(sp);
    });
    bar.appendChild(seg);
  }
  // SC-B4c door A: untyped/unmarked indicator for the DISPLAYED month; hidden when both 0; two tappable segments.
  const wc = cal.counts || {};
  if ((wc.untyped || 0) || (wc.unmarked || 0)) {
    const mo = cal.countsMonth;   // C-1: the month the chip counted (today's month for the current week) — the tap opens the same month
    const chip = el('div', { class: 'ck-wl-chip' });
    if (wc.untyped) { const s1 = el('span', { class: 'ck-wl-seg' }, [T('pay_wl_chip_untyped', { n: wc.untyped })]); s1.onclick = () => openWorklist({ kind: 'untyped', month: mo, origin: 'calendar', title: T('pay_wl_title_untyped') }); chip.appendChild(s1); }
    if (wc.untyped && wc.unmarked) chip.appendChild(el('span', { class: 'ck-wl-dot' }, ['·']));
    if (wc.unmarked) { const s2 = el('span', { class: 'ck-wl-seg' }, [T('pay_wl_chip_unmarked', { n: wc.unmarked })]); s2.onclick = () => openWorklist({ kind: 'unmarked', month: mo, origin: 'calendar', title: T('pay_wl_title_unmarked') }); chip.appendChild(s2); }
    bar.appendChild(chip);
  }
  if (_ckSrchMount(body, bar)) return;   // COCKPIT-CAL-SEARCH: the search bar replaces the toolbar; "Show all" replaces the grid with the list

  if (cal.view === 'day') { (isPhone ? _ckRenderDayMobile : _ckRenderDayDesktop)(body); return; } // D1 desktop / D2 phone
  const rows = visibleRows();
  if (cal.mode === 'strip') renderAttStrip(body, rows); else renderTimeGrid(body, rows);
}

// ── COCKPIT-CAL-SEARCH — clinic-wide search over every specialist's appointments, all time (rpc clinic_calendar_search; same head/admin 'calendar' gate as clinic_calendar).
// The builders (dropdown, full list, keyboard, fit), the date parsing and the CSS are the member calendar's (#257), reached through the HUD bridge with a row adapter: the SPECIALIST leads each row,
// the second line is patient + title. The server matches; the client only draws and highlights. A tap on a hit keeps the current view (Week stays Week, Day stays Day) and opens the session dialog.
let _ckSrchT = null, _ckSrchSeq = 0;
const _CK_DOT = String.fromCharCode(0xB7);
function _ckSrchSub(r) { return [dn(r.patient_name, r.patient_name_alt), r.title].filter(Boolean).join(' ' + _CK_DOT + ' '); }
const _ckSrchAd = {
  title: r => dn(r.member_name, r.member_name_alt) || '—',
  dot: r => memberColor(r.member),
  panSub: _ckSrchSub,
  ddSub: _ckSrchSub,
};
// the query the server accepts (at most 120 characters, 6 terms, 60 per term); anything else is shown as "no results" without a request
function _ckSrchQuery(q) {
  const t = String(q || '').trim();
  if (!t) return { empty: true };
  const terms = t.toLowerCase().split(/\s+/);
  if (t.length > 120 || terms.length > 6 || terms.some(x => x.length > 60)) return { bad: true, q: t };
  return { q: t };
}
function _ckSrchRun() {
  const s = CS.cal && CS.cal.search; if (!s) return;
  const v = _ckSrchQuery(s.q);
  const seq = ++_ckSrchSeq;
  if (v.empty) { s.res = null; s.resQ = ''; _ckSrchPaint(); return; }
  if (v.bad) { s.res = { up: [], past: [] }; s.resQ = v.q; _ckSrchPaint(); return; }
  const dq = H()._calSearchDate(v.q);
  const args = { p_q: v.q, p_from: null, p_to: null, p_today: ymd(new Date()), p_day: dq ? dq.d : null, p_month: dq ? dq.mo + 1 : null, p_year: dq ? dq.y : null };
  const done = (data, error) => {
    if (seq !== _ckSrchSeq || !CS.cal || CS.cal.search !== s) return;   // a newer keystroke, or the search was closed: this reply is stale
    if (error) { console.error('❌ clinic_calendar_search:', error); s.res = { up: [], past: [] }; }
    else s.res = { up: (data && data.up) || [], past: (data && data.past) || [] };
    s.resQ = v.q;
    _ckSrchPaint();
  };
  supa().rpc('clinic_calendar_search', args).then(({ data, error }) => done(data, error), e => done(null, e));
}
// fills the dropdown (or the full list) in place from the stored reply; the field and the bar are never rebuilt per key
function _ckSrchFillPanel(p, s) {
  const res = s.res ? H()._calSearchWrap(s.resQ, s.res.up, s.res.past) : null;
  if (res) H()._calSearchPanel(p, null, s.resQ, _ckSrchPick, { res, ad: _ckSrchAd }); else p.replaceChildren();
}
function _ckSrchFillDd(d, s) {
  const res = s.res ? H()._calSearchWrap(s.resQ, s.res.up, s.res.past) : null;
  if (!res) { d.replaceChildren(); d.style.display = 'none'; H()._calSddCtl.items = []; return; }
  H()._calSearchDd(d, null, s.resQ, { onPick: _ckSrchPick, onAll: _ckSrchAll, res, ad: _ckSrchAd });
}
function _ckSrchPaint() {
  const s = CS.cal && CS.cal.search; if (!s) return;
  if (s.full) { const p = document.querySelector('.ck-sres'); if (p) _ckSrchFillPanel(p, s); return; }
  const d = document.querySelector('.ck-scal .cal-sdd'); if (d) _ckSrchFillDd(d, s);
}
function _ckSrchOpen() {
  CS.cal.search = { q: '', full: false, res: null, resQ: '' };
  render();
  const i = document.querySelector('.ck-scal .cal-sinput'); if (i) i.focus();   // focus inside the tap, so a phone raises the keyboard
}
function _ckSrchClose() { clearTimeout(_ckSrchT); ++_ckSrchSeq; if (CS.cal) CS.cal.search = null; H()._calSddCtl.items = []; render(); }
function _ckSrchAll() { const s = CS.cal && CS.cal.search; if (!s) return; s.full = true; H()._calSddCtl.items = []; render(); }
async function _ckSrchPick(hit) {
  const cal = CS.cal; if (!cal) return;
  clearTimeout(_ckSrchT); ++_ckSrchSeq; cal.search = null; H()._calSddCtl.items = [];
  const ds = hit.date;
  if (cal.view === 'day') cal.dayDate = ds; else cal.picked = ds;   // the view is never switched: Day = that day, Week = the week of that date with the day marked
  let p = null;
  if (_ckDayInWeek(ds)) render(); else p = loadCalendar(ymd(weekStartDay(new Date(ds + 'T00:00:00'))));
  if (p) await p;
  if (CS.cal !== cal) return;
  const row = (cal.rows || []).find(r => r.id === hit.id);
  if (row) { row.clinic_id = row.clinic_id || (CS.overview && CS.overview.clinic_id) || null; onApptTap(row); }
}
// the toolbar icon, and the search bar that replaces the toolbar while the search is open (back + field + clear), with the dropdown anchored under it
function _ckSrchIcon() {
  const b = el('button', { class: 'ck-sic', type: 'button', 'aria-label': T('cal_search_btn') });
  b.innerHTML = H()._CAL_SEARCH_SVG; b.onclick = _ckSrchOpen; return b;
}
function _ckSrchBar(s) {   // returns the bar element
  const bar = el('div', { class: 'ck-sbar' });
  const back = el('button', { class: 'ck-sback', type: 'button', 'aria-label': T('btn_back') });
  back.innerHTML = H()._CAL_BACK_SVG; back.onclick = _ckSrchClose;
  const wrap = el('div', { class: 'cal-sfield' });
  const inp = el('input', { class: 'cal-sinput', type: 'text', placeholder: T('cal_search_ph'), 'aria-label': T('cal_search_btn'), dir: 'auto', autocomplete: 'off', autocapitalize: 'off' });
  inp.setAttribute('spellcheck', 'false'); inp.setAttribute('enterkeyhint', 'search'); inp.value = s.q || '';
  wrap.appendChild(inp); bar.appendChild(back); bar.appendChild(wrap);
  let pend = false;
  const run = () => { pend = false; if (CS.cal && CS.cal.search) CS.cal.search.q = inp.value; _ckSrchRun(); };
  inp.addEventListener('input', () => { pend = true; clearTimeout(_ckSrchT); _ckSrchT = setTimeout(run, 250); });
  inp.addEventListener('keydown', e => {
    if (e.key === 'Escape') { e.stopPropagation(); _ckSrchClose(); return; }
    const c = H()._calSddCtl;
    if (s.full || !c.items.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); H()._calSddSetAct(c.act + (e.key === 'ArrowDown' ? 1 : -1)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (pend) { clearTimeout(_ckSrchT); run(); } const it = c.items[c.act]; if (it) it.click(); }
  });
  withClear(inp, { now: () => { clearTimeout(_ckSrchT); run(); } });
  requestAnimationFrame(() => { if (inp.isConnected && document.activeElement !== inp) { inp.focus(); try { inp.setSelectionRange(inp.value.length, inp.value.length); } catch (_) {} } });   // a background repaint keeps the field focused
  return bar;
}
// mounts the toolbar, or the search bar + dropdown / full list in its place; returns true when the full list replaces the grid
function _ckSrchMount(body, bar) {
  const s = CS.cal.search;
  if (!s) { bar.appendChild(_ckSrchIcon()); body.appendChild(bar); return false; }
  const scal = el('div', { class: 'ck-scal' });
  scal.appendChild(_ckSrchBar(s));
  body.appendChild(scal);
  if (s.full) {
    const p = el('div', { class: 'cal-sres ck-sres' }); body.appendChild(p);
    _ckSrchFillPanel(p, s);
    return true;
  }
  const dd = el('div', { class: 'cal-sdd' }); scal.appendChild(dd); H()._calSddFitBind(dd, body);   // the dropdown may use the room down to the bottom of the calendar frame (not just the 48px bar's box)
  _ckSrchFillDd(dd, s);
  return false;
}

function renderTimeGrid(body, rows) {
  // parity with the app's _renderWeekView: 56px/hr (SLOT_H=14/15min, C3-unpinned), full 24h scroll body,
  // default scroll = first session −1hr, today column tint + date circle, now-line in TODAY's column only,
  // light member tint + 3px inline-start edge + navy text.
  const CAL = _ckCal(); const SLOT15 = CAL.SLOT_H, GRID_H = CAL.GRID_H, HOUR_H = CAL.HOUR_H, TIME_COL_W = CAL.GUTTER;
  if (_ckBorrowCollapse) { try { _ckBorrowCollapse(); } catch (_) {} }  // B2.5: collapse any borrow from a prior render
  const todayStr = ymd(new Date()), weekStart = CS.cal.weekStart;
  const scroll = el('div', { class: 'ck-grid-scroll' });
  // header row (aligned to columns)
  const hdr = el('div', { class: 'ck-grid-hdr' });
  hdr.appendChild(el('div', { class: 'ck-grid-corner' }));
  for (let di = 0; di < 7; di++) {
    const ds = addDays(weekStart, di), isToday = ds === todayStr;
    const dh = el('div', { class: 'ck-grid-dh ck-dh-tap' }); dh.onclick = () => _ckGoDay(ds); // D1: tap a day header → day view
    dh.appendChild(el('div', { class: 'ck-dh-wd' }, [T(DAY_KEYS[di])]));
    dh.appendChild(el('div', { class: 'ck-dh-dt' + (isToday ? ' today' : '') + (ds === CS.cal.picked ? ' picked' : '') }, [String(new Date(ds + 'T00:00:00').getDate())])); // B3: mark the picked day
    hdr.appendChild(dh);
  }
  // scroll body — hdr goes INSIDE so it scrolls horizontally with the grid (sticky keeps it atop on vscroll)
  const scrollBody = el('div', { class: 'ck-grid-body' });
  scrollBody.appendChild(hdr);
  const grid = el('div', { class: 'ck-grid', style: 'height:' + GRID_H + 'px' });
  const gut = el('div', { class: 'ck-grid-gut' });
  for (let h = 0; h < 24; h++) gut.appendChild(el('div', { class: 'ck-grid-hr', style: 'top:' + (h * 4 * SLOT15) + 'px' }, [String(h).padStart(2, '0') + ':00']));
  grid.appendChild(gut);
  for (let di = 0; di < 7; di++) {
    const ds = addDays(weekStart, di), isToday = ds === todayStr;
    const col = el('div', { class: 'ck-grid-col' + (isToday ? ' today' : '') });
    for (let h = 0; h < 24; h++) col.appendChild(el('div', { class: 'ck-grid-line', style: 'top:' + (h * 4 * SLOT15) + 'px' }));
    const evs = rows.filter(r => dayIndex(r.date, weekStart) === di).map(r => { const s = parseHM(r.time); const e = parseHM(r.end_time) ?? (s != null ? s + 60 : null); return s != null && e != null ? { r, s, e } : null; }).filter(Boolean);
    layoutDay(evs);
    evs.forEach(ev => {
      const top = (ev.s / 15) * SLOT15, hgt = Math.max(SLOT15, ((ev.e - ev.s) / 15) * SLOT15);
      const lanes = ev.lanes, w = 100 / lanes, left = ev.lane * w, m = attMeta(ev.r.attendance), c = memberColor(ev.r.member);
      const _origLeft = 'calc(' + left + '% + 1px)', _origWidth = 'calc(' + w + '% - 3px)';
      // B2.5 WIDTH LADDER (keyed on lane count): 1–2 lanes = full (name+time+pills) · 3 lanes = pills collapse
      // to one status dot, name+time keep · 4+ lanes = BAR MODE (member colour-bar + tint only, no text). CSS
      // drives visibility per class; the 3px inline-start colour-bar survives every rung. Height still gates
      // which parts exist in the DOM (time≥28, pills≥40) so borrow only reveals what fits.
      const laneCls = lanes >= 4 ? ' ck-ev-bar' : lanes === 3 ? ' ck-ev-l3' : ' ck-ev-l1';
      const box = el('div', { class: 'ck-ev' + laneCls, style: 'top:' + top + 'px;height:' + (hgt - 2) + 'px;inset-inline-start:' + _origLeft + ';width:' + _origWidth + ';background:' + memberTint(ev.r.member, '1F') + ';border-color:' + c + ';border-inline-start-color:' + c + (m.opacity ? ';opacity:' + m.opacity : '') });
      const _pm = can('payments') ? payMeta(ev.r.payment_status) : null;   // C-5 C2: capability, never the data
      const _t = el('div', { class: 'ck-ev-t' + (m.strike ? ' strike' : ''), dir: 'auto' }, [dn(ev.r.patient_name, ev.r.patient_name_alt) || '—']);
      const _sd = _ckStatusDot(ev.r); if (_sd) _t.appendChild(el('span', { class: 'ck-ev-dot', style: 'background:' + _sd }, [])); // CSS shows only at 3 lanes
      if (_pm) _t.appendChild(el('span', { class: 'ck-ev-pdot ' + _pm.cls, title: T(_pm.labelKey) }, []));                        // CSS shows at 1–2 lanes / borrow
      box.appendChild(_t);
      if (hgt >= 28) box.appendChild(el('div', { class: 'ck-ev-tm' + (m.strike ? ' strike' : '') }, [(ev.r.time || '').split('–')[0].trim()])); // collapsed start-time (hidden on borrow)
      // B2.5 borrow-only detail — always in the DOM, display:none until borrowed (the grown white card reveals them).
      const _startHM = (ev.r.time || '').split('–')[0].trim(), _endHM = (ev.r.end_time || '').split('–').pop().trim();
      box.appendChild(el('div', { class: 'ck-ev-range' + (m.strike ? ' strike' : '') }, [_startHM + (_endHM ? ' – ' + _endHM : '')]));
      if (ev.r.member_name) box.appendChild(el('div', { class: 'ck-ev-mem', dir: 'auto' }, [dn(ev.r.member_name, ev.r.member_name_alt)]));
      // Pills built ALWAYS now (clipped in short collapsed boxes; revealed when borrow grows the card).
      if (m.glyph) box.appendChild(el('span', { class: 'ck-ev-pill ' + m.cls }, [m.glyph]));
      if (_pm) box.appendChild(el('span', { class: 'ck-ev-pill ' + _pm.cls }, [_pm.glyph]));
      const _origTitle = (ev.r.time || '') + ' · ' + (dn(ev.r.patient_name, ev.r.patient_name_alt) || '') + ' · ' + (dn(ev.r.member_name, ev.r.member_name_alt) || '')
                  + (ev.r.attendance_reason ? ' · ' + T('apt_attendance_reason_label') + ': ' + ev.r.attendance_reason : '')
                  + (_pm ? ' · ' + T(_pm.labelKey) : '');
      box.title = _origTitle;
      const _origBg = memberTint(ev.r.member, '1F');
      // B2.5 HOVER/TAP-BORROW: hover (fine pointer) or first tap (touch) expands the event to its column's full
      // width and turns it into an OPAQUE white card (member colour = left bar + subtle tint edge, NOT the fill),
      // z-lifted with a real shadow so siblings are fully occluded; full untruncated detail restored (patient,
      // time range, member name, pills) — the card grows vertically if 42px can't fit it, and the native tooltip
      // is dropped (the card IS the tooltip now). Leaving / tapping-elsewhere collapses; a click while borrowed
      // opens the attendance drawer (first tap borrows, second acts). ≤180ms ease-out (CSS transition on .ck-ev).
      let _outside = null;
      const _collapse = () => {
        if (!box.classList.contains('ck-ev-borrow')) return;
        box.classList.remove('ck-ev-borrow');
        box.style.insetInlineStart = _origLeft; box.style.width = _origWidth; box.style.zIndex = '';
        box.style.background = _origBg; box.style.boxShadow = ''; box.style.opacity = (m.opacity || '');
        box.style.height = (hgt - 2) + 'px'; box.style.minHeight = '';
        box.title = _origTitle;
        if (_outside) { document.removeEventListener('pointerdown', _outside, true); _outside = null; }
        if (_ckBorrowCollapse === _collapse) _ckBorrowCollapse = null;
      };
      const _borrow = () => {
        if (box.classList.contains('ck-ev-borrow')) return;
        if (_ckBorrowCollapse) _ckBorrowCollapse();          // only one borrowed at a time
        box.classList.add('ck-ev-borrow');
        box.style.insetInlineStart = '1px'; box.style.width = 'calc(100% - 2px)'; box.style.zIndex = '20';
        box.style.background = '#fff'; box.style.opacity = '1';        // solid — occlude siblings (kills the tint bleed)
        box.style.boxShadow = '0 6px 18px rgba(13,148,136,.16), 0 3px 8px rgba(0,0,0,.14), inset 5px 0 0 0 ' + memberTint(ev.r.member, '22'); // subtle member tint edge
        box.style.height = 'auto'; box.style.minHeight = (hgt - 2) + 'px'; // grow to fit content, float over the grid
        box.removeAttribute('title');                                 // the card is the tooltip now
        _ckBorrowCollapse = _collapse;
        _outside = (e) => { if (!box.contains(e.target)) _collapse(); };  // tap-elsewhere collapses (touch)
        setTimeout(() => document.addEventListener('pointerdown', _outside, true), 0);
      };
      if (_ckFinePointer()) { box.onmouseenter = _borrow; box.onmouseleave = _collapse; }
      box.onclick = (e) => { e.stopPropagation(); if (!box.classList.contains('ck-ev-borrow')) { _borrow(); return; } onApptTap(ev.r); };
      col.appendChild(box);
    });
    // B1/B2 — now-line lives in TODAY's column ONLY (skill §6): a short rule spanning just this column,
    // with a dot at its inline-start (via .ck-nowline::before). Self-clearing 60s refresh.
    if (isToday) {
      const nl = el('div', { class: 'ck-nowline', id: '_ckNowLine' }); nl.style.top = (nowMinutes() / 15 * SLOT15) + 'px';
      col.appendChild(nl);
      if (_ckNowInt) clearInterval(_ckNowInt);
      _ckNowInt = setInterval(() => { const e2 = document.getElementById('_ckNowLine'); if (e2) e2.style.top = (nowMinutes() / 15 * SLOT15) + 'px'; else { clearInterval(_ckNowInt); _ckNowInt = null; } }, 60000);
    }
    grid.appendChild(col);
  }
  scrollBody.appendChild(grid); scroll.appendChild(scrollBody); body.appendChild(scroll);
  if (!rows.length) body.appendChild(el('div', { class: 'ck-empty' }, [T('ck_calendar_empty')]));
  const _startsMin = rows.map(r => parseHM(r.time)).filter(v => v != null);
  _ckApplyScroll(scrollBody, _startsMin.length ? Math.min(..._startsMin) : 8 * 60, SLOT15, HOUR_H);
}

// B4 — shared scroll preservation (save on scroll, restore on re-render; first-session default only on a
// fresh calendar open). Used by BOTH the week time-grid and the day-view grid. rAF fires before paint → no flash.
function _ckApplyScroll(scrollBody, firstMin, SLOT15, HOUR_H) {
  scrollBody.addEventListener('scroll', () => { CS.cal._scrollTop = scrollBody.scrollTop; }, { passive: true });
  requestAnimationFrame(() => {
    const maxTop = Math.max(0, scrollBody.scrollHeight - scrollBody.clientHeight);
    if (CS.cal._scrolled && CS.cal._scrollTop != null) {
      scrollBody.scrollTop = Math.max(0, Math.min(maxTop, CS.cal._scrollTop));
    } else {
      scrollBody.scrollTop = Math.max(0, Math.min(maxTop, ((firstMin / 15) * SLOT15) - HOUR_H));
      CS.cal._scrolled = true;
    }
  });
}

// per-cluster lane packing (proper side-by-side overlaps)
function layoutDay(evs) {
  // C0: delegate to the shared packer (HUD._calLayoutDay). Sort in place first (prior behaviour) so
  // evs[i] ↔ packed[i]; col→lane, totalCols→lanes. Same cluster/greedy result as the old inline version.
  evs.sort((a, b) => a.s - b.s || a.e - b.e);
  const packed = H()._calLayoutDay(evs.map(ev => ({ start: ev.s, end: ev.e })));
  evs.forEach((ev, i) => { ev.lane = packed[i].col; ev.lanes = packed[i].totalCols; });
}

// D1 — day-view event box: FULL detail always (name · time range · status pills), no ladder, no borrow
// (rulings 1, 7). Overlap within the member column still lane-packs (shared packer). Click → attendance drawer.
function _ckDayBox(ev, SLOT15) {
  const top = (ev.s / 15) * SLOT15, hgt = Math.max(SLOT15, ((ev.e - ev.s) / 15) * SLOT15);
  const w = 100 / ev.lanes, left = ev.lane * w, m = attMeta(ev.r.attendance), c = memberColor(ev.r.member), _pm = can('payments') ? payMeta(ev.r.payment_status) : null;   // C-5 C2
  const box = el('div', { class: 'ck-ev ck-ev-day', style: 'top:' + top + 'px;height:' + (hgt - 2) + 'px;inset-inline-start:calc(' + left + '% + 1px);width:calc(' + w + '% - 3px);background:' + memberTint(ev.r.member, '1F') + ';border-color:' + c + ';border-inline-start-color:' + c + (m.opacity ? ';opacity:' + m.opacity : '') });
  const _t = el('div', { class: 'ck-ev-t' + (m.strike ? ' strike' : ''), dir: 'auto' }, [dn(ev.r.patient_name, ev.r.patient_name_alt) || '—']);
  if (_pm) _t.appendChild(el('span', { class: 'ck-ev-pdot ' + _pm.cls, title: T(_pm.labelKey) }, []));
  box.appendChild(_t);
  const _s = (ev.r.time || '').split('–')[0].trim(), _e = (ev.r.end_time || '').split('–').pop().trim();
  box.appendChild(el('div', { class: 'ck-ev-tm' + (m.strike ? ' strike' : '') }, [_s + (_e ? ' – ' + _e : '')]));
  if (m.glyph) box.appendChild(el('span', { class: 'ck-ev-pill ' + m.cls }, [m.glyph]));
  if (_pm) box.appendChild(el('span', { class: 'ck-ev-pill ' + _pm.cls }, [_pm.glyph]));
  box.title = (ev.r.time || '') + ' · ' + (dn(ev.r.patient_name, ev.r.patient_name_alt) || '') + (_pm ? ' · ' + T(_pm.labelKey) : '');
  box.onclick = () => onApptTap(ev.r);
  return box;
}

// D1 — DESKTOP day view: a column per member (roster order). Header = avatar (member colour) + name + that
// day's count. Empty members: shown "no sessions today" when the TOTAL active roster ≤4, folded entirely at
// 5+ (ruling 2 — measured on the total roster, so the filter only REMOVES columns, never conjures empties).
// Now-line spans all columns only when the day is today (ruling 5). No RPC change — filters the weekly rows.
function _ckRenderDayDesktop(body) {
  const CAL = _ckCal(); const SLOT15 = CAL.SLOT_H, GRID_H = CAL.GRID_H, HOUR_H = CAL.HOUR_H;
  if (_ckBorrowCollapse) { try { _ckBorrowCollapse(); } catch (_) {} }
  const cal = CS.cal, todayStr = ymd(new Date()), day = cal.dayDate;
  const roster = CS.overview?.members || [];
  const showEmpties = roster.length <= 4;   // ruling 2: threshold on TOTAL active roster
  const dayRows = (cal.rows || []).filter(r => r.date === day);
  const cols = roster.filter(m => !cal.hidden[m.user_id]).map(m => {
    const evs = dayRows.filter(r => r.member === m.user_id).map(r => { const s = parseHM(r.time); const e = parseHM(r.end_time) ?? (s != null ? s + 60 : null); return s != null && e != null ? { r, s, e } : null; }).filter(Boolean);
    return { m, evs };
  }).filter(x => x.evs.length > 0 || showEmpties);   // filter removes columns; empties only when showEmpties

  const scroll = el('div', { class: 'ck-grid-scroll' });
  const hdr = el('div', { class: 'ck-grid-hdr' });
  hdr.appendChild(el('div', { class: 'ck-grid-corner' }));
  cols.forEach(({ m, evs }) => {
    const dh = el('div', { class: 'ck-grid-dh ck-day-dh' });
    dh.appendChild(el('div', { class: 'ck-day-av', style: 'background:' + memberColor(m.user_id) }, [(_nm(m) || '?').trim().charAt(0).toUpperCase()]));
    dh.appendChild(el('div', { class: 'ck-day-nm', title: _nm(m) || '', dir: 'auto' }, [_nm(m) || '—']));
    const n = evs.length;
    dh.appendChild(el('div', { class: 'ck-day-ct' }, [n === 0 ? T('ck_day_no_sessions') : n === 1 ? T('ck_day_session_one') : T('ck_day_sessions', { n })]));
    hdr.appendChild(dh);
  });
  const scrollBody = el('div', { class: 'ck-grid-body' });
  scrollBody.appendChild(hdr);
  const grid = el('div', { class: 'ck-grid', style: 'height:' + GRID_H + 'px' });
  const gut = el('div', { class: 'ck-grid-gut' });
  for (let h = 0; h < 24; h++) gut.appendChild(el('div', { class: 'ck-grid-hr', style: 'top:' + (h * 4 * SLOT15) + 'px' }, [String(h).padStart(2, '0') + ':00']));
  grid.appendChild(gut);
  cols.forEach(({ evs }) => {
    const col = el('div', { class: 'ck-grid-col' });
    for (let h = 0; h < 24; h++) col.appendChild(el('div', { class: 'ck-grid-line', style: 'top:' + (h * 4 * SLOT15) + 'px' }));
    layoutDay(evs);
    evs.forEach(ev => col.appendChild(_ckDayBox(ev, SLOT15)));
    grid.appendChild(col);
  });
  // now-line spans ALL member columns, only when the day is today (ruling 5); self-clearing 60s refresh.
  if (day === todayStr) {
    const nl = el('div', { class: 'ck-nowline', id: '_ckNowLine', style: 'inset-inline-start:' + CAL.GUTTER + 'px;inset-inline-end:0;top:' + (nowMinutes() / 15 * SLOT15) + 'px' });
    grid.appendChild(nl);
    if (_ckNowInt) clearInterval(_ckNowInt);
    _ckNowInt = setInterval(() => { const e2 = document.getElementById('_ckNowLine'); if (e2) e2.style.top = (nowMinutes() / 15 * SLOT15) + 'px'; else { clearInterval(_ckNowInt); _ckNowInt = null; } }, 60000);
  }
  scrollBody.appendChild(grid); scroll.appendChild(scrollBody); body.appendChild(scroll);
  if (!cols.length) body.appendChild(el('div', { class: 'ck-empty' }, [T('ck_calendar_empty')]));
  const starts = dayRows.map(r => parseHM(r.time)).filter(v => v != null);
  _ckApplyScroll(scrollBody, starts.length ? Math.min(...starts) : 8 * 60, SLOT15, HOUR_H);
}

// D2 — PHONE day view (Option B): a horizontal CHIP SWITCHER (avatar + first name + · count per member;
// counts always visible, 0-session members KEEP their chip; active highlighted) above ONE full-width member
// column. Tap a chip or horizontal-swipe the grid switches member. Now-line on the single column when today.
function _ckRenderDayMobile(body) {
  const CAL = _ckCal(); const SLOT15 = CAL.SLOT_H, GRID_H = CAL.GRID_H, HOUR_H = CAL.HOUR_H;
  if (_ckBorrowCollapse) { try { _ckBorrowCollapse(); } catch (_) {} }
  const cal = CS.cal, todayStr = ymd(new Date()), day = cal.dayDate;
  const roster = CS.overview?.members || [];
  const dayRows = (cal.rows || []).filter(r => r.date === day);
  const countFor = uid => dayRows.filter(r => r.member === uid).length;
  // Active member: keep the current one if still valid, else first with sessions today, else first in roster.
  let active = cal.activeMember;
  if (!active || !roster.some(m => m.user_id === active)) active = (roster.find(m => countFor(m.user_id) > 0) || roster[0])?.user_id || null;
  cal.activeMember = active;
  // chip switcher (ALL roster members — phone-day ignores the filter; 0-session chips kept)
  const chips = el('div', { class: 'ck-mchips' });
  roster.forEach(m => {
    const on = m.user_id === active;
    const chip = el('div', { class: 'ck-mchip' + (on ? ' on' : '') });
    chip.appendChild(el('span', { class: 'ck-mchip-av', style: 'background:' + memberColor(m.user_id) }, [(_nm(m) || '?').trim().charAt(0).toUpperCase()]));
    chip.appendChild(el('span', { class: 'ck-mchip-nm', dir: 'auto' }, [(_nm(m) || '—').split(' ')[0]]));
    chip.appendChild(el('span', { class: 'ck-mchip-ct' }, ['· ' + countFor(m.user_id)]));
    chip.onclick = () => { if (cal.activeMember !== m.user_id) { cal.activeMember = m.user_id; render(); } };
    chips.appendChild(chip);
  });
  body.appendChild(chips);
  // single full-width member column
  const am = roster.find(m => m.user_id === active);
  const evs = dayRows.filter(r => r.member === active).map(r => { const s = parseHM(r.time); const e = parseHM(r.end_time) ?? (s != null ? s + 60 : null); return s != null && e != null ? { r, s, e } : null; }).filter(Boolean);
  const scroll = el('div', { class: 'ck-grid-scroll' });
  const hdr = el('div', { class: 'ck-grid-hdr' });
  hdr.appendChild(el('div', { class: 'ck-grid-corner' }));
  const dh = el('div', { class: 'ck-grid-dh ck-day-dh' });
  if (am) {
    dh.appendChild(el('div', { class: 'ck-day-av', style: 'background:' + memberColor(active) }, [(_nm(am) || '?').trim().charAt(0).toUpperCase()]));
    dh.appendChild(el('div', { class: 'ck-day-nm', dir: 'auto' }, [_nm(am) || '—']));
    const n = evs.length;
    dh.appendChild(el('div', { class: 'ck-day-ct' }, [n === 0 ? T('ck_day_no_sessions') : n === 1 ? T('ck_day_session_one') : T('ck_day_sessions', { n })]));
  }
  hdr.appendChild(dh);
  const scrollBody = el('div', { class: 'ck-grid-body' });
  scrollBody.appendChild(hdr);
  const grid = el('div', { class: 'ck-grid', style: 'height:' + GRID_H + 'px' });
  const gut = el('div', { class: 'ck-grid-gut' });
  for (let h = 0; h < 24; h++) gut.appendChild(el('div', { class: 'ck-grid-hr', style: 'top:' + (h * 4 * SLOT15) + 'px' }, [String(h).padStart(2, '0') + ':00']));
  grid.appendChild(gut);
  const col = el('div', { class: 'ck-grid-col' });
  for (let h = 0; h < 24; h++) col.appendChild(el('div', { class: 'ck-grid-line', style: 'top:' + (h * 4 * SLOT15) + 'px' }));
  layoutDay(evs);
  evs.forEach(ev => col.appendChild(_ckDayBox(ev, SLOT15)));
  if (day === todayStr) {   // now-line on the single column (ruling 5)
    const nl = el('div', { class: 'ck-nowline', id: '_ckNowLine' }); nl.style.top = (nowMinutes() / 15 * SLOT15) + 'px';
    col.appendChild(nl);
    if (_ckNowInt) clearInterval(_ckNowInt);
    _ckNowInt = setInterval(() => { const e2 = document.getElementById('_ckNowLine'); if (e2) e2.style.top = (nowMinutes() / 15 * SLOT15) + 'px'; else { clearInterval(_ckNowInt); _ckNowInt = null; } }, 60000);
  }
  grid.appendChild(col);
  scrollBody.appendChild(grid); scroll.appendChild(scrollBody); body.appendChild(scroll);
  _ckWireMemberSwipe(scrollBody, roster, active);
  const starts = evs.map(e => e.s);
  _ckApplyScroll(scrollBody, starts.length ? Math.min(...starts) : 8 * 60, SLOT15, HOUR_H);
}

// D2 — horizontal swipe on the phone day grid switches the active member (roster order; RTL-aware). Passive
// listeners so vertical scroll still works; only a decisive horizontal swipe switches (|dx|>60 & > vertical).
function _ckWireMemberSwipe(scrollEl, roster, active) {
  let sx = 0, sy = 0;
  scrollEl.addEventListener('touchstart', e => { sx = e.touches[0].clientX; sy = e.touches[0].clientY; }, { passive: true });
  scrollEl.addEventListener('touchend', e => {
    const dx = e.changedTouches[0].clientX - sx, dy = e.changedTouches[0].clientY - sy;
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    const idx = roster.findIndex(m => m.user_id === active); if (idx < 0) return;
    const rtl = !!(H()._isRtl && H()._isRtl());
    let ni = (dx < 0) ? idx + 1 : idx - 1; if (rtl) ni = (dx < 0) ? idx - 1 : idx + 1; // swipe toward next member
    if (ni < 0 || ni >= roster.length) return;
    CS.cal.activeMember = roster[ni].user_id; render();
  }, { passive: true });
}

function renderAttStrip(body, rows) {
  const leg = el('div', { class: 'ck-att-legend' });
  leg.appendChild(el('span', { class: 'ck-badge a-un' }, [T('ck_unmarked')]));
  Object.values(ATT).forEach(a => leg.appendChild(el('span', { class: 'ck-badge ' + a.cls }, [a.glyph + ' ' + T(a.labelKey)])));
  body.appendChild(leg);
  const todayStr = ymd(new Date()), grid = el('div', { class: 'ck-strip' });
  for (let di = 0; di < 7; di++) {
    const ds = addDays(CS.cal.weekStart, di), col = el('div', { class: 'ck-strip-col' });
    const _sdh = el('div', { class: 'ck-strip-dh ck-dh-tap' + (ds === todayStr ? ' today' : '') + (ds === CS.cal.picked ? ' picked' : '') }, [T(DAY_KEYS[di]) + ' ' + new Date(ds + 'T00:00:00').getDate()]); // B3: mark the picked day
    _sdh.onclick = () => _ckGoDay(ds); // D1: tap a day header → day view
    col.appendChild(_sdh);
    const evs = rows.filter(r => dayIndex(r.date, CS.cal.weekStart) === di).sort((a, b) => (parseHM(a.time) ?? 0) - (parseHM(b.time) ?? 0));
    if (!evs.length) col.appendChild(el('div', { class: 'ck-strip-empty' }, ['—']));
    evs.forEach(r => {
      const m = attMeta(r.attendance), past = isPastAppt(r), c = memberColor(r.member);
      const card = el('div', { class: 'ck-acard' + (past && r.attendance == null ? ' unmarked' : ''), style: 'border-inline-start-color:' + c + ';background:' + memberTint(r.member, '0A') + (m.opacity ? ';opacity:' + m.opacity : '') });
      const t = el('div', { class: 'ck-acard-t' });
      t.appendChild(el('b', { class: m.strike ? 'strike' : '' }, [(r.time || '').split('–')[0].trim() || '·']));
      t.appendChild(document.createTextNode(' '));
      t.appendChild(el('span', { class: m.strike ? 'strike' : '', dir: 'auto' }, [dn(r.patient_name, r.patient_name_alt) || '—']));
      card.appendChild(t);
      // C-1: no "unmarked" badge on a session that hasn't ended (nothing to mark yet)
      const _bw = el('div', { class: 'ck-acard-b' }, (r.attendance == null && !past) ? [] : [el('span', { class: 'ck-badge ' + m.cls }, [(m.glyph ? m.glyph + ' ' : '') + T(m.labelKey)])]);
      const _pm = can('payments') ? payMeta(r.payment_status) : null;   // B-status: dual badge — payment beside attendance (C-5 C2: capability-gated)
      if (_pm) _bw.appendChild(el('span', { class: 'ck-badge ' + _pm.cls }, [_pm.glyph + ' ' + T(_pm.labelKey)]));
      card.appendChild(_bw);
      if (r.attendance_reason) card.appendChild(el('div', { class: 'ck-acard-r', title: r.attendance_reason }, [r.attendance_reason]));
      card.onclick = () => onApptTap(r);
      col.appendChild(card);
    });
    grid.appendChild(col);
  }
  body.appendChild(grid);
}

// D4-1 + A: shared save (every value button, reason-blur, Clear) — mark(+reason) + D3.3 propagation.
// Returns true on success; the CALLER owns dialog close, local-r update, and the calendar reload.
// A: reason lives only on the three non-attended values; blank/whitespace → null (mirrors the RPC's NULLIF(btrim)).
// NOTE: reads r.attendance as the PREV value (for _reconcileAttendanceLocal) — caller must mutate r AFTER this resolves.
// C-1 QA-09: a head-set payment (paid / follow_up / waived / awaiting / not_billable) makes the server refuse with
// hint 'payment_confirm'; the head confirms, and the same call is re-sent with p_reset_payment:true (payment returns
// to the seed, the charge decision clears). A cancel/Esc saves nothing. 'payment_locked' (not a head) → explain.
function _ckConfirm(title, body, okLabel) {
  return new Promise(resolve => {
    let ok = false;
    ckDialog(title, (bd) => { bd.appendChild(el('div', { class: 'ck-mh' }, [body])); },
      [{ label: T('btn_cancel'), cls: 'ghost', onClick: c => c() }, { label: okLabel, cls: 'primary', onClick: c => { ok = true; c(); } }],
      () => resolve(ok));
  });
}
async function _ckSaveAttendance(r, val, reason) {
  const reasonVal = (val === 'no_show' || val === 'patient_cancelled' || val === 'therapist_cancelled') ? ((reason || '').trim() || null) : null;
  try {
    let { error } = await supa().rpc('mark_attendance', { p_appointment_id: r.id, p_status: val, p_reason: reasonVal });
    if (error && error.hint === 'payment_confirm') {
      if (!(await _ckConfirm(T('ck_paid_change_title'), T('ck_paid_change_body'), T('ck_paid_change_confirm')))) return false;
      ({ error } = await supa().rpc('mark_attendance', { p_appointment_id: r.id, p_status: val, p_reason: reasonVal, p_reset_payment: true }));
    }
    if (error && error.hint === 'payment_locked') { console.error('❌ mark_attendance:', error); toast(T('apt_attendance_payment_locked'), 'error'); return false; }
    if (error) throw error;
    const mine = r.member === H().session?.id;
    const flip = (r.attendance === 'patient_cancelled' || r.attendance === 'therapist_cancelled') !== (val === 'patient_cancelled' || val === 'therapist_cancelled');
    if (mine) { try { await H()._reconcileAttendanceLocal(r.id, r.attendance, val); } catch (e2) { console.error('❌ reconcile:', e2); } }
    else if (flip) { H().toast(T('apt_google_deferred_toast', { name: r.member_name || T('ck_the_therapist_fallback') }), 'info', 6000); }
    toast(T('ck_toast_attendance_saved'), 'success');
    return true;
  } catch (e) { console.error('❌ mark_attendance:', e); toast(/finalized/i.test((e && e.message) || '') ? T('pay_lock_finalized') : (/permitted/i.test((e && e.message) || '') ? T('ck_toast_not_allowed_mark') : T('ck_toast_could_not_save_attendance')), 'error'); return false; }
}
function _ckMarkTime(iso) { try { const d = new Date(iso); return _ckDate(d, { month: 'short', day: 'numeric' }) + ' ' + _ckTime(d, { hour: '2-digit', minute: '2-digit' }); } catch (_) { return ''; } }

// B-status: head-only payment write (set_payment_status). Returns true on success; caller owns r-update + refresh.
async function _ckSavePayment(r, val, note) {
  const noteVal = (note || '').trim() || null;
  try {
    const { error } = await supa().rpc('set_payment_status', { p_appointment_id: r.id, p_status: val, p_note: noteVal }); if (error) throw error;
    toast(T('ck_pay_saved'), 'success');
    return true;
  } catch (e) { console.error('❌ set_payment_status:', e); toast(/permitted/i.test((e && e.message) || '') ? T('ck_toast_not_allowed_mark') : T('ck_pay_could_not_save'), 'error'); return false; }
}

// B-status: the payment control (5 targets + note + recorded-by), shared by the appt-tap dialog and the queue's ⋯.
// Note prompted on follow_up + waived. A cancelled session offers only not_billable / waived (C-1 ruling 1). Writes via
// set_payment_status; mutates r in place; calls afterSave() (fire-and-forget refresh; never blocks the dialog).
function _appendPaymentSection(bd, r, afterSave) {
  bd.appendChild(el('div', { class: 'ck-dlg-subh' }, [T('ck_pay_section')]));
  const payRow = el('div', { class: 'ck-attrow' });
  const paySub = el('div', { class: 'ck-submark' });
  const payNote = el('textarea', { class: 'ck-reason', placeholder: T('ck_pay_note_ph'), rows: '2', maxlength: '280' });
  // C-1 ruling 1: a cancelled session is never billable — offer only not_billable / waived (set_payment_status
  // refuses the rest with 22023). The current state still shows in the subtitle.
  const _cancelled = r.attendance === 'patient_cancelled' || r.attendance === 'therapist_cancelled';
  const PVALS = [['paid', 'pay_paid'], ['follow_up', 'pay_follow_up'], ['waived', 'pay_waived'], ['not_billable', 'pay_not_billable'], ['awaiting', 'pay_awaiting']]
    .filter(([v]) => !_cancelled || v === 'not_billable' || v === 'waived');
  const pbtns = [];
  const _isPayNote = v => v === 'follow_up' || v === 'waived';
  function _renderPayBtns() { pbtns.forEach(({ b, v }) => { const pm = payMeta(v); b.className = 'ck-attbtn' + (r.payment_status === v ? ' on ' + (pm ? pm.cls : '') : ''); }); }
  function _renderPaySub() {
    if (r.payment_status) {
      const pm = payMeta(r.payment_status);
      let s = pm.glyph + ' ' + T(pm.labelKey);
      if (r.payment_marked_at) { const by = r.payment_marked_by === H().session?.id ? T('apt_attendance_marked_by_you') : T('apt_attendance_marked_by_other'); s += ' · ' + T('ck_pay_marked_subtitle', { time: _ckMarkTime(r.payment_marked_at), by }); }
      else if (r.payment_marked_by === null) s += ' · ' + T('ck_pay_auto');   // EXPLICIT null = seeded auto; absent field (calendar pre-follow-up-migration) → state only

      paySub.textContent = s;
    } else paySub.textContent = T('ck_pay_none');
  }
  function _renderPayNote() {
    if (_isPayNote(r.payment_status)) { payNote.style.display = 'block'; if (document.activeElement !== payNote) payNote.value = r.payment_note || ''; }
    else { payNote.style.display = 'none'; payNote.value = ''; }
  }
  async function _payCommit(val) {
    const ok = await _ckSavePayment(r, val, payNote.value);
    if (!ok) return;
    r.payment_status = val;
    r.payment_note = (payNote.value || '').trim() || null;
    r.payment_marked_at = new Date().toISOString();
    r.payment_marked_by = H().session?.id;
    _renderPayBtns(); _renderPaySub(); _renderPayNote();
    if (afterSave) afterSave();   // fire-and-forget (caller does not await)
  }
  PVALS.forEach(([v, k]) => { const pm = payMeta(v); const b = el('button', { class: 'ck-attbtn' + (r.payment_status === v ? ' on ' + pm.cls : '') }, [(pm.glyph ? pm.glyph + ' ' : '') + T(k)]); b.onclick = () => _payCommit(v); pbtns.push({ b, v }); payRow.appendChild(b); });
  bd.appendChild(payRow);
  payNote.addEventListener('blur', () => { if (!r.payment_status) return; const nv = (payNote.value || '').trim() || null; if (nv === (r.payment_note || null)) return; _payCommit(r.payment_status); });
  bd.appendChild(payNote);
  bd.appendChild(paySub);
  _renderPayNote(); _renderPaySub();
}

// B-status: the queue-row ⋯ (change state without leaving the queue). Reuses the shared payment section.
function _payChangeDialog(r) {
  ckDialog(T('ck_pay_section') + ' — ' + (r.patient_name || T('ck_client_word')), (bd, close) => {
    bd.appendChild(el('div', { class: 'ck-note ck-meta' }, _ckRowMeta(r, { title: false, time: false })));
    _appendPaymentSection(bd, r, () => loadPaymentQueue());
  }, [{ label: T('btn_close'), cls: 'ghost', onClick: c => c() }]);   // QA-11: a footer that only closes says Close
}

// i1/i2: a head may mark/override attendance. Value-gate: FUTURE appts (date>today; same-day markable) offer
// ONLY the two cancellations; outcomes appear once the date arrives. Aligned to the therapist popup control
// (full-word labels, "Clear", "Last marked … by …") — one attendance control, two doors.
function onApptTap(r, onChanged) {
  if (!can('calendar')) return;   // C-5 C2: the session dialog is the calendar's door
  const future = !hasStarted(r);   // C-1: outcomes once the session has STARTED (same rule as mark_attendance)
  // full-word labels reuse the app's Hebrew-ready attendance strings; therapist_cancelled → the head's wording.
  const VALS = [['attended', 'apt_attendance_attended'], ['patient_cancelled', 'apt_attendance_patient_cancelled'], ['no_show', 'apt_attendance_no_show'], ['therapist_cancelled', 'apt_attendance_clinic_cancelled']];
  const vals = future ? VALS.filter(([v]) => v === 'patient_cancelled' || v === 'therapist_cancelled') : VALS;
  const _isReason = v => v === 'no_show' || v === 'patient_cancelled' || v === 'therapist_cancelled';
  ckDialog(T('apt_attendance_label') + ' — ' + (dn(r.patient_name, r.patient_name_alt) || T('ck_client_word')), (bd, close) => {
    bd.appendChild(el('div', { class: 'ck-note ck-meta' }, _ckRowMeta(r, { dn: true, title: false })));
    // ── SC-B2b: session type section (head), ABOVE attendance. Reuse CS.billing if loaded
    // (billing_start_month + session_types + members[] for the name hint), else one member-RPC
    // fetch for {billing_start_month, types} (no members → no name, generic "Set by the clinic").
    // Hidden for solo/pre-start rows. Change via nested ckDialog → clinic_set_appointment_type;
    // {unchanged:true} is a quiet no-op. Does NOT touch _ckSaveAttendance/_appendPaymentSection.
    if (r.clinic_id) {
      const typeSec = el('div', {});
      typeSec.appendChild(el('div', { class: 'ck-dlg-subh' }, [T('pay_type_row_label')]));
      const typeRow = el('div', { class: 'ck-attrow', style: { cursor: 'pointer', display: 'block' } });
      const typeVal = el('b', {});
      const typeHint = el('div', { class: 'ck-submark' });
      typeRow.appendChild(typeVal); typeRow.appendChild(typeHint);
      typeSec.appendChild(typeRow);
      typeSec.appendChild(el('div', { class: 'ck-dlg-sep' }));
      typeSec.style.display = 'none';
      bd.appendChild(typeSec);
      let _cat = null, _cbusy = false;
      const _hub = CS.billing || null;
      const _typeNameOf = id => { const src = (_cat && _cat.types) || (_hub && _hub.session_types) || []; const t = src.find(x => x.id === id); return t ? t.name : null; };
      function _hintFor() {
        if (r.classified_by == null) return T('pay_type_hint_auto');
        if (r.classified_by === H().session?.id) return T('pay_type_hint_you');
        const mem = ((_hub && _hub.members) || []).find(m => m.user_id === r.classified_by);
        return mem ? T('pay_type_hint_by', { name: mem.name || '' }) : T('pay_type_hint_clinic');
      }
      function _renderType() {
        typeVal.textContent = _typeNameOf(r.session_type_id) || T('pay_type_unset');
        if (r.session_type_id) { typeHint.style.display = 'block'; typeHint.textContent = _hintFor(); }
        else { typeHint.style.display = 'none'; typeHint.textContent = ''; }
      }
      typeRow.onclick = () => {
        if (_cbusy) return;
        const types = (_cat && _cat.types) || ((_hub && _hub.session_types) || []).filter(t => t.active);
        ckDialog(T('pay_type_drawer_title'), (pb, pclose) => {
          types.forEach(t => {
            const cur = t.id === r.session_type_id;
            const opt = el('div', { class: 'ck-attbtn' + (cur ? ' on' : ''), style: { display: 'flex', justifyContent: 'space-between', cursor: 'pointer', marginBottom: '6px' } }, [el('span', {}, [t.name]), ...(cur ? [el('span', {}, ['✓'])] : [])]);
            opt.onclick = async () => {
              if (cur || _cbusy) return;   // reject a same-tick second dispatch BEFORE pclose()
              _cbusy = true;
              pclose();
              try {
                const { data, error } = await supa().rpc('clinic_set_appointment_type', { p_clinic: r.clinic_id, p_appointment_id: r.id, p_session_type_id: t.id });
                if (error) throw error;
                if (!(data && data.unchanged)) { r.session_type_id = data.session_type_id; r.classified_by = data.classified_by; _renderType(); toast(T('pay_type_saved')); if (onChanged) onChanged(); }
              } catch (e) {
                toast(/finalized/i.test((e && e.message) || '') ? T('pay_lock_finalized') : (e && e.code === '23514' ? T('pay_type_err_invalid') : (e && e.code === '22023' ? T('pay_type_err_prestart') : T('pay_toast_error'))), 'error');
              }
              _cbusy = false;
            };
            pb.appendChild(opt);
          });
        }, [{ label: T('pay_cancel'), cls: 'ghost', onClick: c => c() }]);
      };
      function _showIfBillable(start) { if (!start || (r.date || '') < start) return; typeSec.style.display = 'block'; _renderType(); }
      if (_hub && _hub.billing_settings) { _showIfBillable(_hub.billing_settings.billing_start_month); }
      else {
        supa().rpc('clinic_session_types_for_member', { p_clinic: r.clinic_id }).then(({ data, error }) => {
          if (!typeSec.isConnected) return;
          if (error) { console.error('❌ session types for member:', error); return; }
          _cat = data || {}; _showIfBillable(_cat.billing_start_month);
        }, e => console.error('❌ session types for member:', e));
      }
    }
    const row = el('div', { class: 'ck-attrow' });
    const reason = el('textarea', { class: 'ck-reason', placeholder: T('apt_attendance_reason_placeholder'), rows: '2', maxlength: '280' });
    const sub = el('div', { class: 'ck-submark' });
    const btns = [];
    function _renderBtns() { btns.forEach(({ b, v }) => { b.className = 'ck-attbtn' + (r.attendance === v ? ' on ' + attMeta(v).cls : ''); }); }
    function _renderSub() {
      if (r.attendance_marked_at) { const by = r.attendance_marked_by === H().session?.id ? T('apt_attendance_marked_by_you') : T('apt_attendance_marked_by_other'); sub.textContent = T('apt_attendance_marked_subtitle', { time: _ckMarkTime(r.attendance_marked_at), by }); }
      else sub.textContent = T('apt_attendance_not_marked');
    }
    function _renderReason() {
      if (_isReason(r.attendance)) { reason.style.display = 'block'; if (document.activeElement !== reason) reason.value = r.attendance_reason || ''; }
      else { reason.style.display = 'none'; reason.value = ''; }
    }
    // Refresh r's billing facts + the surface behind the dialog after a change. Calendar context
    // (a calendar tap, onChanged undefined): loadCalendar the shown week — byte-identical to before.
    // Worklist context (onChanged set): fetch the row's week WITHOUT disturbing CS.cal, then hand off
    // to onChanged (re-fetch the list + refresh whichever door opened it).
    async function _refreshBg() {
      const wk = ymd(weekStartDay(new Date((r.date || ymd(new Date())) + 'T00:00:00')));
      let rows;
      if (CS.cal && CS.cal.weekStart === wk) { await loadCalendar(wk); rows = CS.cal.rows || []; }
      else { const { data } = await supa().rpc('clinic_calendar', { p_week_start: wk }); rows = data || []; }
      const _fresh = rows.find(x => x.id === r.id);
      if (_fresh) { r.charge_fraction = _fresh.charge_fraction == null ? null : Number(_fresh.charge_fraction); r.charge_decided_by = _fresh.charge_decided_by; r.billed_price = _fresh.billed_price; r.payment_status = _fresh.payment_status; r.payment_marked_by = _fresh.payment_marked_by; r.payment_marked_at = _fresh.payment_marked_at; }
      if (typeof _rebuildBilling === 'function') _rebuildBilling();
      if (onChanged) onChanged();
    }
    // A: one save path — reads r.attendance as PREV, then mutates r locally, refreshes the dialog + the grid/strip behind it.
    async function _commit(val, reasonText) {
      const statusChanged = (val !== r.attendance);
      const reasonVal = _isReason(val) ? ((reasonText || '').trim() || null) : null;
      const ok = await _ckSaveAttendance(r, val, reasonText);
      if (!ok) return;
      if (val === null) { r.attendance = null; r.attendance_marked_at = null; r.attendance_marked_by = null; r.attendance_reason = null; }
      else { r.attendance = val; if (statusChanged) { r.attendance_marked_at = new Date().toISOString(); r.attendance_marked_by = H().session?.id; } r.attendance_reason = reasonVal; }
      _renderBtns(); _renderSub(); _renderReason();
      // SC-B3b: the SC-B3a reset trigger may change charge_fraction/payment_status server-side on a
      // transition OFF no_show; _refreshBg re-syncs those onto r + rebuilds the billing box (chips
      // vanish once attendance≠no_show) and, in worklist context, refreshes the list + origin door.
      await _refreshBg();
      if (!_isReason(val)) close(); // attended / clear → nothing more to type, close
    }
    vals.forEach(([v, k]) => { const b = el('button', { class: 'ck-attbtn' + (r.attendance === v ? ' on ' + attMeta(v).cls : '') }, [T(k)]); b.onclick = () => _commit(v, reason.value); btns.push({ b, v }); row.appendChild(b); });
    bd.appendChild(row);
    // reason-only blur: same status + new reason → RPC won't re-stamp marked_at/by (v_status_changed=false).
    reason.addEventListener('blur', () => { if (!_isReason(r.attendance)) return; const nv = (reason.value || '').trim() || null; if (nv === (r.attendance_reason || null)) return; _commit(r.attendance, reason.value); });
    bd.appendChild(reason);
    bd.appendChild(sub);
    if (future) bd.appendChild(el('div', { class: 'ck-note' }, [T('apt_attendance_future')]));
    _renderReason(); _renderSub();

    // ── SC-B3b: no-show charge chips + consequence line, grouped WITH the payment section in a
    // rebuildable box so a charge write (or an attendance transition) re-renders both in place.
    const billingBox = el('div');
    let _chargeBusy = false;
    bd.appendChild(billingBox);
    function _noshowConseq(frac, price, half) {
      if (frac === 0) return T('pay_noshow_conseq_waived');
      return T('pay_noshow_conseq_charge', { v: _money2(frac === 0.5 ? half : price) });
    }
    async function _pickCharge(frac) {
      if (_chargeBusy) return; _chargeBusy = true;   // busy-guard against double-tap (SC-B2bh pattern)
      try {
        const { data, error } = await supa().rpc('clinic_set_noshow_charge', { p_clinic: r.clinic_id, p_appointment_id: r.id, p_fraction: frac });
        if (error) throw error;
        if (!(data && data.unchanged)) {   // {unchanged:true} → quiet no-op
          r.charge_fraction = Number(data.charge_fraction); r.payment_status = data.payment_status; r.charge_decided_by = data.charge_decided_by;
          r.payment_marked_by = H().session?.id; r.payment_marked_at = new Date().toISOString();   // the RPC froze the ledger
          _rebuildBilling(); toast(T('pay_noshow_saved'), 'success');
          _refreshBg(); _refreshPayCount(false);   // fire-and-forget refresh (calendar week or worklist origin)
        }
      } catch (e) {
        toast(/finalized/i.test((e && e.message) || '') ? T('pay_lock_finalized') : (e && e.code === '22023' ? T('pay_noshow_err') : T('pay_toast_error')), 'error');
        _rebuildBilling();   // revert the chip selection to r's actual state
      }
      _chargeBusy = false;
    }
    function _renderChargeSection(box) {
      box.appendChild(el('div', { class: 'ck-dlg-subh' }, [T('pay_noshow_charge_title')]));
      if (r.billed_price == null) {   // unclassified → no price / no billing row → disabled chips + hint (B-Q3)
        const dr = el('div', { class: 'ck-attrow' });
        [T('pay_noshow_full', { v: '—' }), T('pay_noshow_half', { v: '—' }), T('pay_noshow_none')]
          .forEach(lbl => dr.appendChild(el('button', { class: 'ck-attbtn', disabled: 'true', style: { opacity: '.5', cursor: 'not-allowed' } }, [lbl])));
        box.appendChild(dr);
        box.appendChild(el('div', { class: 'ck-note' }, [T('pay_noshow_set_type_first')]));
        return;
      }
      const price = Number(r.billed_price) || 0;
      // display only — the canonical charged amount is derived server-side as round(billed_price*charge_fraction,2) (B-Q6); SC-B4 must NOT read the chip label.
      const half = Math.round(price * 0.5 * 100) / 100;
      const frac = r.charge_fraction == null ? 1 : Number(r.charge_fraction);   // B-Q4: undecided → In full selected (no write)
      const dr = el('div', { class: 'ck-attrow' });
      [[1, T('pay_noshow_full', { v: _money2(price) })], [0.5, T('pay_noshow_half', { v: _money2(half) })], [0, T('pay_noshow_none')]]
        .forEach(([f, lbl]) => { const b = el('button', { class: 'ck-attbtn' + (frac === f ? ' on ' + (f === 0 ? 'a-waive' : 'a-charge') : '') }, [lbl]); b.onclick = () => _pickCharge(f); dr.appendChild(b); });
      box.appendChild(dr);
      box.appendChild(el('div', { class: 'ck-submark' }, [_noshowConseq(frac, price, half)]));
    }
    function _rebuildBilling() {
      billingBox.textContent = '';
      // clinic_id gate: a SOLO no-show (no clinic, no billing concept) shows no charge section — a
      // "set type first" hint there would be a dead end (solo sessions can't be typed).
      if (!can('payments')) return;   // C-5 C2 (QM2b1-2): no charge / payment section without 'payments' — read, never inferred from r
      if (r.attendance === 'no_show' && r.clinic_id) _renderChargeSection(billingBox);
      // B-status: PAYMENT (head-only) — shared section (also used by the queue's ⋯). Fire-and-forget refresh.
      if (r.attendance || r.payment_status) { billingBox.appendChild(el('div', { class: 'ck-dlg-sep' })); _appendPaymentSection(billingBox, r, () => { loadCalendar(CS.cal.weekStart); _refreshPayCount(false); }); }
    }
    _rebuildBilling();
  }, [
    ...(r.attendance ? [{ label: T('apt_attendance_clear'), cls: 'danger', onClick: async c => { const ok = await _ckSaveAttendance(r, null, null); if (ok) { r.attendance = null; r.attendance_marked_at = null; r.attendance_marked_by = null; r.attendance_reason = null; await loadCalendar(CS.cal.weekStart); } c(); } }] : []),
    { label: T('btn_close'), cls: 'ghost', onClick: c => c() },   // QA-11
  ]);
}

// ══ B-status: payment queue (3 tabs) ═════════════════════════════════════════
function _monthStart(d) { return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-01'; }
function _shiftMonth(ymStr, delta) { const d = new Date(ymStr + 'T00:00:00'); d.setMonth(d.getMonth() + delta); return _monthStart(d); }
function _monthLabel(ymStr) { try { return _ckDate(new Date(ymStr + 'T00:00:00'), { month: 'long', year: 'numeric' }); } catch (_) { return ymStr; } }

async function openPayments() {
  CS.view = 'payments'; CS.member = null; CS.client = null; CS.artifact = null; CS.err = null;
  if (!CS.pay) CS.pay = { tab: 'unprocessed', month: _monthStart(new Date()), rows: [] };
  await loadPaymentQueue();
}
// Refresh the "Unprocessed (n)" header + overview Payments-card count out of band (after a write from any tab/surface).
async function _refreshPayCount(rerender) {
  try { const { data } = await supa().rpc('clinic_payment_queue', { p_view: 'unprocessed' }); if (Array.isArray(data)) CS.payUnprocessed = data.length; } catch (_) { /* count is best-effort */ }
  if (rerender && _overlay) render();
}
async function loadPaymentQueue() {
  const seq = ++_ckLoadSeq.pay;   // F-48 guard
  CS.loading = true; CS.err = null; render();
  try {
    const args = { p_view: CS.pay.tab };
    if (CS.pay.tab === 'processed') args.p_month = CS.pay.month;
    const { data, error } = await supa().rpc('clinic_payment_queue', args);
    if (seq !== _ckLoadSeq.pay) return;
    if (error) throw error;
    CS.pay.rows = data || [];
    if (CS.pay.tab === 'unprocessed') CS.payUnprocessed = CS.pay.rows.length;   // free on this tab
    else _refreshPayCount(true);   // other tabs: refresh header + overview count out of band (stale-count fix)
  } catch (e) { if (seq !== _ckLoadSeq.pay) return; console.error('❌ clinic_payment_queue:', e); CS.err = T('ck_err_load_payments'); }
  CS.loading = false; render();
}

// ══ F-91: search + filter for the cockpit lists (payments x3 tabs, worklist). CLIENT-SIDE on the rows already loaded: no new request, nothing re-sorted. ══
// State lives on the list's own state object (CS.pay.flt / CS.worklist.flt) so it survives a tab switch (the type and therapist choices reset with the tab).
// Money-derived pieces (the "Not set" chip, the to-collect recompute) are gated on can('payments'), never on a null field (C-5: a masked field reads as "nothing recorded").
function _ckFltNew() { return { q: '', from: '', to: '', types: new Set(), mem: '' }; }
function _ckFltOn(f) { return !!(f.q.trim() || f.from || f.to || f.types.size || f.mem); }
function _ckFltPass(f, r, withTypes) {
  const q = f.q.trim().toLowerCase();
  if (q && !(((r.patient_name || '') + ' ' + (r.patient_name_alt || '')).toLowerCase().includes(q))) return false;   // patient_name_alt arrives with the F-91 migration; absent before it
  const d = String(r.date || '').slice(0, 10);
  if (f.from && (!d || d < f.from)) return false;
  if (f.to && (!d || d > f.to)) return false;
  if (withTypes && f.types.size && !f.types.has(r.session_type_id || '__none')) return false;
  if (f.mem && String(r.member) !== f.mem) return false;
  return true;
}
// What order the server actually returned, stated in words (the migration changes the Processed order: the caption reports what is true, whichever side shipped first).
function _ckSortCaption(rows, tab) {
  if (tab === 'unprocessed' || tab === 'follow_up') return T('ck_sort_unprocessed');
  const ds = (rows || []).map(r => String(r.date || '').slice(0, 10)).filter(Boolean);
  if (ds.length < 2) return '';
  const a = ds[0], b = ds[ds.length - 1];
  return a > b ? T('ck_sort_processed_new') : a < b ? T('ck_sort_processed_old') : '';
}
function _ckFilterBar(f, rows, opt) {   // opt: { types:boolean, onChange:fn }
  const bar = el('div', { class: 'ck-flt' });
  const narrow = window.innerWidth < 480;
  const changed = () => { bar.sync(); opt.onChange(); };
  const top = el('div', { class: 'ck-flt-r' });
  const q = el('input', { class: 'ck-inp ck-flt-q', type: 'text', placeholder: T('ck_flt_search_ph'), 'aria-label': T('ck_flt_search_ph'), dir: 'auto' }); q.value = f.q;
  let tm = null;
  q.oninput = () => { f.q = q.value; clearTimeout(tm); tm = setTimeout(changed, 120); };
  q.onkeydown = (e) => { if (e.key === 'Escape' && q.value) { e.stopPropagation(); q.value = ''; f.q = ''; changed(); } };
  top.appendChild(q);
  withClear(q, { now: () => { clearTimeout(tm); f.q = ''; changed(); } });   // F-107: the x also resets at once (Esc keeps working)
  const more = el('div', { class: 'ck-flt-more' });
  const mk = (cls, kids) => el('label', { class: cls }, kids);
  const from = el('input', { class: 'ck-inp ck-flt-d', type: 'date', 'aria-label': T('ck_flt_from') }); from.value = f.from; from.onchange = () => { f.from = from.value; changed(); };
  const to = el('input', { class: 'ck-inp ck-flt-d', type: 'date', 'aria-label': T('ck_flt_to') }); to.value = f.to; to.onchange = () => { f.to = to.value; changed(); };
  more.appendChild(mk('ck-flt-l', [T('ck_flt_from') + ' ', from])); more.appendChild(mk('ck-flt-l', [T('ck_flt_to') + ' ', to]));
  const mems = new Map(); (rows || []).forEach(r => { if (r.member != null && !mems.has(String(r.member))) mems.set(String(r.member), r.member_name || ''); });
  const msel = el('select', { class: 'ck-inp ck-flt-m', 'aria-label': T('ck_flt_all_therapists') });
  msel.appendChild(el('option', { value: '' }, [T('ck_flt_all_therapists')]));
  [...mems.entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1]))).forEach(([id, nm]) => { const o = el('option', { value: id }, [nm || '—']); msel.appendChild(o); });
  msel.value = f.mem; msel.onchange = () => { f.mem = msel.value; changed(); };
  if (mems.size > 1) more.appendChild(msel);
  const chips = el('div', { class: 'ck-flt-chips' });
  const types = new Map(); let anyNone = false;
  (rows || []).forEach(r => { if (r.session_type_id) types.set(String(r.session_type_id), r.session_type_name || ''); else anyNone = true; });
  const drawChips = () => {
    chips.replaceChildren();
    if (!opt.types) return;
    const add = (id, label) => { const c = el('span', { class: 'ck-flt-chip' + (f.types.has(id) ? ' on' : ''), role: 'button', tabindex: '0', 'aria-pressed': f.types.has(id) ? 'true' : 'false' }, [label]);
      const tog = () => { f.types.has(id) ? f.types.delete(id) : f.types.add(id); drawChips(); changed(); };
      c.onclick = tog; c.onkeydown = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); tog(); } }; chips.appendChild(c); };
    [...types.entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1]))).forEach(([id, nm]) => add(id, nm || '—'));
    if (anyNone) add('__none', T('ck_flt_type_none'));
  };
  drawChips(); more.appendChild(chips);
  const foot = el('div', { class: 'ck-flt-foot' });
  const cnt = el('span', { class: 'ck-flt-cnt', dir: 'auto', 'aria-live': 'polite' });   // dir=auto: the line takes the direction of its own text (an English fallback reads LTR inside an RTL page)
  const clr = el('a', { class: 'ck-flt-clr', role: 'button', tabindex: '0' }, [T('ck_flt_clear')]);
  const doClear = () => { Object.assign(f, _ckFltNew()); q.value = ''; from.value = ''; to.value = ''; msel.value = ''; drawChips(); changed(); };
  clr.onclick = doClear; clr.onkeydown = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); doClear(); } };
  foot.appendChild(cnt); foot.appendChild(clr);
  if (narrow) {   // a phone keeps the search always visible and puts the rest behind one button
    const tg = el('button', { class: 'ck-btn xs ghost ck-flt-tg', type: 'button', 'aria-expanded': 'false' }, [T('ck_flt_filters')]);
    const setOpen = (o) => { more.style.display = o ? '' : 'none'; tg.setAttribute('aria-expanded', o ? 'true' : 'false'); };
    tg.onclick = () => setOpen(more.style.display === 'none'); top.appendChild(tg); setOpen(_ckFltOn({ ...f, q: '' }));
  }
  bar.appendChild(top); bar.appendChild(more); bar.appendChild(foot);
  bar.setCount = (n, m) => { cnt.textContent = T('ck_flt_count', { n, m }); };
  bar.sync = () => { clr.style.visibility = _ckFltOn(f) ? 'visible' : 'hidden'; };
  bar.sync();
  return bar;
}

function renderPaymentsView(body) {
  const p = CS.pay; if (!p) return;
  const tabs = el('div', { class: 'ck-seg ck-pay-tabs' });
  [['unprocessed', 'ck_pay_unprocessed'], ['follow_up', 'ck_pay_tab_followup'], ['processed', 'ck_pay_tab_processed']].forEach(([tab, k]) => {
    const on = p.tab === tab;
    const sp = el('span', { class: on ? 'on' : '' }, [T(k) + (tab === 'unprocessed' && CS.payUnprocessed ? ' (' + CS.payUnprocessed + ')' : '')]);
    sp.onclick = () => { if (p.tab !== tab) { p.tab = tab; if (p.flt) { p.flt.types.clear(); p.flt.mem = ''; } loadPaymentQueue(); } };   // F-91: the type + therapist choices belong to the tab
    tabs.appendChild(sp);
  });
  body.appendChild(tabs);
  if (p.tab === 'processed') {
    const bar = el('div', { class: 'ck-cal-nav ck-pay-month' });
    const prev = el('button', { class: 'ck-btn xs ghost' }, ['‹']); prev.onclick = () => { p.month = _shiftMonth(p.month, -1); loadPaymentQueue(); };
    const next = el('button', { class: 'ck-btn xs ghost' }, ['›']); next.onclick = () => { p.month = _shiftMonth(p.month, 1); loadPaymentQueue(); };
    bar.dataset.ckSlot = 'month'; bar.style.setProperty('--ck-slot', _ckSlotPx('month') + 'px');   // F-48
    bar.appendChild(prev); bar.appendChild(el('div', { class: 'ck-cal-wk' }, [el('span', { class: 'ck-cal-wk-t' }, [_monthLabel(p.month)])])); bar.appendChild(next);
    body.appendChild(bar);
  }
  const rows = p.rows || [];
  if (!rows.length) { const ek = p.tab === 'processed' ? 'ck_pay_empty_processed' : p.tab === 'follow_up' ? 'ck_pay_empty_followup' : 'ck_pay_empty_unprocessed'; body.appendChild(el('div', { class: 'ck-empty' }, [T(ek)])); return; }
  // F-91: search + filter on the loaded rows. The "Not set" type chip and the to-collect total need can('payments') (read explicitly, never inferred from a null field).
  const f = p.flt || (p.flt = _ckFltNew()), withTypes = can('payments');
  const fbar = _ckFilterBar(f, rows, { types: withTypes, onChange: () => paint() });
  body.appendChild(fbar);
  const cap = _ckSortCaption(rows, p.tab); if (cap) body.appendChild(el('div', { class: 'ck-flt-sort' }, [cap]));
  const host = el('div', { class: 'ck-flt-list' }); body.appendChild(host);
  function paint() {
    const shown = rows.filter(r => _ckFltPass(f, r, withTypes));
    fbar.setCount(shown.length, rows.length);
    host.replaceChildren();
    if (p.tab === 'unprocessed' && can('payments')) {   // Option B: running total to collect (classified rows only; unclassified noted separately); follows the filter
      const classified = shown.filter(r => r.billed_price != null && r.charged != null);
      const sum = classified.reduce((a, r) => a + Number(r.charged || 0), 0);
      const unclassified = shown.length - classified.length;
      const bar = el('div', { class: 'ck-paytotal' });
      bar.appendChild(el('span', { class: 'ck-pt-amt' }, [_ils(sum)]));
      bar.appendChild(el('span', { class: 'ck-pt-lbl' }, [T('pay_to_collect', { n: classified.length })]));
      if (unclassified > 0) bar.appendChild(el('span', { class: 'ck-pt-un' }, [T('pay_unclassified_more', { n: unclassified })]));
      host.appendChild(bar);
    }
    if (!shown.length) { host.appendChild(_ckFltEmpty(f, () => { Object.assign(f, _ckFltNew()); render(); })); return; }
    // RPC already orders (follow_up first / session date) — DO NOT re-sort client-side.
    shown.forEach(r => host.appendChild(p.tab === 'processed' ? _procRow(r) : _queueRow(r)));
  }
  paint();
}
function _ckFltEmpty(f, clear) {
  const e = el('div', { class: 'ck-empty' }, [T('ck_flt_empty') + ' ']);
  const a = el('a', { class: 'ck-flt-clr', role: 'button', tabindex: '0' }, [T('ck_flt_clear')]); a.onclick = clear; a.onkeydown = (ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); clear(); } };
  e.appendChild(a); return e;
}

// Unprocessed / Follow-up row — Variant A: aging pill · patient/member/date · attendance chip · note · Mark paid + ⋯
function _queueRow(r) {
  const row = el('div', { class: 'ck-payrow' + (r.payment_status === 'follow_up' ? ' fu' : '') });
  const d = agingDays(r.attendance_marked_at);
  const age = el('div', { class: 'ck-age ' + agingCls(d) });
  age.appendChild(el('b', {}, [d == null ? '—' : String(d)]));
  age.appendChild(el('span', { class: 'ck-age-u' }, [T('ck_pay_days_unit')]));
  row.appendChild(age);
  const grow = el('div', { class: 'ck-grow' });
  grow.appendChild(el('div', { class: 'ck-nm' }, [_bdi(r.patient_name || '—'), ' ', el('span', { class: 'ck-sub ck-meta' }, [el('span', { class: 'ck-sub-dot' }, ['· ']), ..._ckRowMeta(r)])]));
  const chips = el('div', { class: 'ck-chips' });
  const am = attMeta(r.attendance); if (r.attendance) chips.appendChild(el('span', { class: 'ck-badge ' + am.cls }, [(am.glyph ? am.glyph + ' ' : '') + T(am.labelKey)]));
  const pm = payMeta(r.payment_status); if (pm) chips.appendChild(el('span', { class: 'ck-badge ' + pm.cls }, [pm.glyph + ' ' + T(pm.labelKey)]));
  chips.appendChild(_typeChip(r, () => loadPaymentQueue()));   // Option B: session type as a chip (amber "set type" when unclassified)
  grow.appendChild(chips);
  if (r.payment_note) grow.appendChild(el('div', { class: 'ck-payrow-note' }, ['✎ ' + r.payment_note]));
  row.appendChild(grow);
  row.appendChild(_amtBlock(r));   // Option B: bold ₪ amount-to-collect, trailing block before the actions
  const acts = el('div', { class: 'ck-acts' });
  const paid = el('button', { class: 'ck-btn xs primary' }, [T('ck_pay_mark_paid')]);
  if (r.billed_price == null) { paid.disabled = true; paid.title = T('pay_set_type_first'); }   // unclassified → can't collect until classified
  paid.onclick = async () => { paid.disabled = true; const ok = await _ckSavePayment(r, 'paid', r.payment_note); if (ok) loadPaymentQueue(); else paid.disabled = false; };
  acts.appendChild(paid);
  const more = el('button', { class: 'ck-btn xs ghost' }, ['⋯']); more.title = T('ck_pay_change'); more.onclick = () => _payChangeDialog(r);
  acts.appendChild(more);
  row.appendChild(acts);
  return row;
}

// Processed row — state · patient/member/date · note · who+when ('auto' when payment_marked_by null). V5 polish:
// a ⋯ menu (NO Mark-paid primary) reopens the shared change dialog — the month report is where mistakes get spotted,
// and it gives the auto·not_billable late-fee override its natural home (⋯ → Awaiting). The re-pull moves the row
// to whichever tab its new state belongs to (an awaiting/follow_up state drops out of Processed).
function _procRow(r) {
  const row = el('div', { class: 'ck-payrow proc' });
  const pm = payMeta(r.payment_status);
  if (pm) row.appendChild(el('span', { class: 'ck-badge ' + pm.cls }, [pm.glyph + ' ' + T(pm.labelKey)]));
  const grow = el('div', { class: 'ck-grow' });
  grow.appendChild(el('div', { class: 'ck-nm' }, [_bdi(r.patient_name || '—'), ' ', el('span', { class: 'ck-sub ck-meta' }, [el('span', { class: 'ck-sub-dot' }, ['· ']), ..._ckRowMeta(r)])]));
  if (r.payment_note) grow.appendChild(el('div', { class: 'ck-payrow-note' }, ['✎ ' + r.payment_note]));
  const _pchips = el('div', { class: 'ck-chips' }); _pchips.appendChild(_typeChip(r, () => loadPaymentQueue())); grow.appendChild(_pchips);
  row.appendChild(grow);
  row.appendChild(_amtBlock(r));   // Option B: amount on Processed too (reconciliation; no Mark-paid here)
  // C-7: name and date-time are separate bidi-isolated units (<bdi>, direction from content), so a Latin name in a
  // Hebrew UI can't pull the day number away from its month.
  const who = r.auto ? [T('ck_pay_auto')] : [el('bdi', {}, [r.marked_by_name || T('apt_attendance_marked_by_other')])]
    .concat(r.payment_marked_at ? [el('bdi', { class: 'ck-dt' }, [_ckMarkTime(r.payment_marked_at)])] : []);   // two lines: name, then date-time
  row.appendChild(el('div', { class: 'ck-payrow-who' }, who));
  const more = el('button', { class: 'ck-btn xs ghost' }, ['⋯']); more.title = T('ck_pay_change'); more.onclick = () => _payChangeDialog(r);
  row.appendChild(more);
  return row;
}

// ══ SC-B4b: head PAYOUTS view (month-scoped; exactly one clinic_payouts call per open/month) ══
// Pull-based like the queue: clinic_payouts loads on open + on month change (the RPC logs once). Expand/
// collapse is pure client state (CS.payouts.expanded) → NO re-call. Money never leaves this head view.
async function openPayouts() {
  CS.view = 'payouts'; CS.member = null; CS.client = null; CS.artifact = null; CS.err = null;
  if (!CS.payouts) CS.payouts = { month: _monthStart(new Date()), data: null, expanded: {} };
  await loadPayouts(CS.payouts.month);
}
async function loadPayouts(month) {
  const seq = ++_ckLoadSeq.po;   // F-48 guard
  CS.payouts.month = month; CS.payouts.data = null; CS.payouts.expanded = {};
  CS.loading = true; CS.err = null; render();
  try {
    const { data, error } = await supa().rpc('clinic_payouts', { p_clinic: CS.overview?.clinic_id, p_month: month });
    if (seq !== _ckLoadSeq.po) return;
    if (error) throw error;
    CS.payouts.data = data;
    const ct = data?.clinic_totals || {};
    CS.payoutsCatchNet = (ct.unclassified_billable || 0) + (ct.unmarked || 0);   // keep the overview badge in sync
  } catch (e) { if (seq !== _ckLoadSeq.po) return; console.error('❌ clinic_payouts:', e); CS.err = T('pay_err_load'); }
  CS.loading = false; render();
  // SC-B5b: the per-specialist status pill (draft/confirmed/finalized) shows ONLY when the clinic uses the
  // review toggle. review_toggle lives on CS.billing (billing hub); background-load it once (no spinner, cached)
  // so the pill can gate on the real setting instead of guessing — pills paint a beat later on first open.
  if (!CS.billing) {
    supa().rpc('clinic_billing_hub', { p_clinic: CS.overview?.clinic_id }).then(
      ({ data, error }) => { if (!error && data) { CS.billing = data; if (CS.view === 'payouts') render(); } }, () => {});
  }
}
// stacked-bar segment order + colors (held teal / cancelled amber / no-show red)
const PO_SEG = [['held', '#0d9488'], ['cancelled', '#f59e0b'], ['noshow', '#dc2626']];

function renderPayoutsView(body) {
  const st = CS.payouts; if (!st) return;
  // month navigator (RTL-aware, mirrors the processed-queue nav)
  const nav = el('div', { class: 'ck-cal-nav ck-pay-month' });
  const prev = el('button', { class: 'ck-btn xs ghost' }, ['‹']); prev.onclick = () => loadPayouts(_shiftMonth(st.month, -1));
  const next = el('button', { class: 'ck-btn xs ghost' }, ['›']); next.onclick = () => loadPayouts(_shiftMonth(st.month, 1));
  nav.dataset.ckSlot = 'month'; nav.style.setProperty('--ck-slot', _ckSlotPx('month') + 'px');   // F-48
  nav.appendChild(prev); nav.appendChild(el('div', { class: 'ck-cal-wk' }, [el('span', { class: 'ck-cal-wk-t' }, [_monthLabel(st.month)])])); nav.appendChild(next);
  body.appendChild(nav);
  const d = st.data; if (!d) return;   // CS.loading spinner covers the fetch
  const ct = d.clinic_totals || {};
  const specs = d.specialists || [];
  // stat grid (tabular-nums on every numeric cell via .num)
  const grid = el('div', { class: 'ck-po-grid' });
  [[T('pay_stat_held'), ct.held, false], [T('pay_stat_cancelled'), ct.cancelled, false], [T('pay_stat_noshow'), ct.noshow, false],
   [T('pay_stat_unmarked'), ct.unmarked, false], [T('pay_stat_revenue'), _money2(ct.revenue), true], [T('pay_stat_payouts'), _money2(ct.payouts), true]]
    .forEach(([l, v, money]) => { const b = el('div', { class: 'ck-po-stat' }); b.appendChild(el('div', { class: 'ck-po-stat-n num' }, [money ? v : String(v ?? 0)])); b.appendChild(el('div', { class: 'ck-po-stat-l' }, [l])); grid.appendChild(b); });
  body.appendChild(grid);
  // catch-net banner — three independent lines, each only when non-zero (nopay computed client-side per build notes)
  const nopay = specs.filter(s => s.terms == null).length;
  const m = st.month;
  const warns = [];   // SC-B4c door B: untyped/unmarked lines open the clinic-wide worklist; nopay stays plain
  if (ct.unclassified_billable) warns.push({ t: T('pay_catch_unclassified', { n: ct.unclassified_billable }), go: () => openWorklist({ kind: 'untyped', month: m, origin: 'payouts', title: T('pay_wl_title_untyped') }) });
  if (ct.unmarked) warns.push({ t: T('pay_catch_unmarked', { n: ct.unmarked }), go: () => openWorklist({ kind: 'unmarked', month: m, origin: 'payouts', title: T('pay_wl_title_unmarked') }) });
  if (nopay) warns.push({ t: T('pay_catch_nopay', { n: nopay }) });
  if (warns.length) { const wb = el('div', { class: 'ck-po-warn' }); warns.forEach(o => { const line = el('div', { class: 'ck-po-warn-l' + (o.go ? ' ck-row-click' : '') }, ['⚠ ' + o.t]); if (o.go) { line.appendChild(el('span', { class: 'ck-chev' }, [' ›'])); line.onclick = o.go; } wb.appendChild(line); }); body.appendChild(wb); }
  // SC-B6b: finalize / reopen action (head). Gated to MIRROR the server guards — explanatory line, never a dead button.
  // review_toggle + billing_start come from CS.billing (lazy-loaded in loadPayouts → this re-renders when it lands).
  const _bs = CS.billing && CS.billing.billing_settings;
  if (_bs) {
    const _start = _bs.billing_start_month ? String(_bs.billing_start_month).slice(0, 10) : null;
    const _cur = _monthStart(new Date());
    const _finalized = specs.length > 0 && specs.every(s => s.status === 'finalized');
    const finbar = el('div', { class: 'ck-po-finbar' });
    if (_finalized) {
      finbar.appendChild(el('span', { class: 'ck-badge p-fin' }, [T('pay_status_finalized')]));
      const rb = el('button', { class: 'ck-btn secondary sm' }, [T('pay_reopen_btn')]);
      rb.onclick = () => _reopenConfirm(m, specs);
      finbar.appendChild(rb);
    } else if (!_start || m < _start) {
      finbar.appendChild(el('div', { class: 'ck-po-finnote' }, [T('pay_finalize_before_start')]));
    } else if (m >= _cur) {
      finbar.appendChild(el('div', { class: 'ck-po-finnote' }, [T('pay_finalize_not_ended')]));
    } else {
      const fb = el('button', { class: 'ck-btn primary sm' }, [T('pay_finalize_btn')]);
      fb.onclick = () => _finalizePreflight(m, specs, ct);
      finbar.appendChild(fb);
    }
    body.appendChild(finbar);
  }
  // chart
  body.appendChild(_payoutsChart(specs));
  // empty-month hint (no activity of any kind)
  if (!ct.held && !ct.cancelled && !ct.noshow && !ct.unmarked) body.appendChild(el('div', { class: 'ck-hint' }, [T('pay_no_sessions')]));
  // header row (labels the count columns; grid matches _payoutRow; numeric heads right-aligned via .ck-po-c/.ck-po-amt)
  const hdr = el('div', { class: 'ck-po-row ck-po-hdr' });
  hdr.appendChild(el('span', { class: 'ck-po-chev' }, ['']));
  hdr.appendChild(el('div', { class: 'ck-po-nm' }, [T('pay_col_specialist')]));
  hdr.appendChild(el('div', { class: 'ck-po-c' }, [T('pay_th_held')]));
  hdr.appendChild(el('div', { class: 'ck-po-c' }, [T('pay_th_cxl')]));
  hdr.appendChild(el('div', { class: 'ck-po-c' }, [T('pay_th_ns')]));
  hdr.appendChild(el('div', { class: 'ck-po-amt' }, [T('pay_th_payout')]));
  body.appendChild(hdr);
  // per-specialist rows (expand in place)
  specs.forEach(s => _payoutRow(body, s));
}

function _payoutsChart(specs) {
  const card = el('div', { class: 'ck-po-chart-card' });
  const legend = el('div', { class: 'ck-po-legend' });
  [[T('pay_stat_held'), '#0d9488'], [T('pay_stat_cancelled'), '#f59e0b'], [T('pay_stat_noshow'), '#dc2626']]
    .forEach(([l, c]) => { const s = el('span', { class: 'ck-po-leg' }); s.appendChild(el('i', { style: { background: c } })); s.appendChild(el('span', {}, [l])); legend.appendChild(s); });
  card.appendChild(legend);
  const chart = el('div', { class: 'ck-po-chart' });
  const totals = specs.map(s => (s.counts.held || 0) + (s.counts.cancelled || 0) + (s.counts.noshow || 0));
  const max = Math.max(1, ...totals);
  specs.forEach((s, i) => {
    const grp = el('div', { class: 'ck-po-bargrp' });
    const wrap = el('div', { class: 'ck-po-barwrap' });
    const tot = totals[i];
    if (tot > 0) {   // BQ-4: zero-activity specialists render an empty slot with just their name
      const bar = el('div', { class: 'ck-po-bar', style: { height: Math.max(6, Math.round(tot / max * 100)) + '%' } });
      PO_SEG.forEach(([k, c]) => { const n = s.counts[k] || 0; if (n > 0) bar.appendChild(el('div', { class: 'ck-po-seg', style: { height: (n / tot * 100) + '%', background: c } })); });
      wrap.appendChild(bar);
    }
    grp.appendChild(wrap);
    grp.appendChild(el('div', { class: 'ck-po-barlbl', title: s.display_name || '', dir: 'auto' }, [s.display_name || '—']));
    chart.appendChild(grp);
  });
  card.appendChild(chart);
  return card;
}

function _termsSummary(terms) {
  if (!terms) return '';
  let base = terms.model === 'fixed'
    ? T('pay_terms_fixed')
    : (terms.vat_split === 'after' ? T('pay_terms_pct_after', { n: terms.default_percent }) : T('pay_terms_pct_before', { n: terms.default_percent }));
  if ((terms.overrides || []).length) base += ' ' + T('pay_terms_exceptions');
  return base;
}

function _payoutRow(body, s) {
  const open = !!CS.payouts.expanded[s.user_id];
  const row = el('div', { class: 'ck-po-row ck-row-click' + (open ? ' open' : '') });
  row.appendChild(el('span', { class: 'ck-po-chev' }, [open ? '▾' : '▸']));
  const nmCell = el('div', { class: 'ck-po-nm', dir: 'auto' }, [s.display_name || '—']);   // C-6: the truncating cell takes the name's direction
  // SC-B5b: month-confirm status pill — only when the clinic uses the review toggle (else it's meaningless noise).
  // confirmed/finalized can only exist when review is on (the confirm RPC gates on it), so they're always safe;
  // 'draft' shows "Waiting" only under review. review_toggle from CS.billing (lazy-loaded in loadPayouts).
  const _reviewOn = !!(CS.billing && CS.billing.billing_settings && CS.billing.billing_settings.review_toggle);
  if (_reviewOn || s.status === 'finalized') {   // finalized is terminal + real → always shown; waiting/confirmed only under review
    const _st = s.status || 'draft';
    const _pcls = _st === 'confirmed' ? 'p-conf' : (_st === 'finalized' ? 'p-fin' : 'p-wait');
    const _pkey = _st === 'confirmed' ? 'pay_status_confirmed' : (_st === 'finalized' ? 'pay_status_finalized' : 'pay_status_waiting');
    nmCell.appendChild(el('span', { class: 'ck-badge ' + _pcls, style: { marginInlineStart: '6px' } }, [T(_pkey)]));
  }
  row.appendChild(nmCell);
  row.appendChild(el('div', { class: 'ck-po-c num' }, [String(s.counts.held || 0)]));
  row.appendChild(el('div', { class: 'ck-po-c num' }, [String(s.counts.cancelled || 0)]));
  row.appendChild(el('div', { class: 'ck-po-c num' }, [String(s.counts.noshow || 0)]));
  const amt = el('div', { class: 'ck-po-amt num' });
  if (s.terms == null) {   // "set up pay" — visually distinct from a real ₪0 (build notes)
    const link = el('a', { class: 'ck-po-setup' }, [T('pay_setup_pay')]);
    link.onclick = (e) => { e.stopPropagation(); _payoutsSetUpPay(s); };
    amt.appendChild(link);
  } else {
    amt.appendChild(el('b', {}, [_money2(s.payout)]));
  }
  // SC-B6c: send-pay-email affordance on a FINALIZED row with a real payout. Once emailed_at is set → "Sent {date}"
  // (resend behind a confirm). No affordance for no-pay (terms null) rows — nothing to pay.
  if (s.status === 'finalized' && s.terms != null) {
    const mail = el('div', { class: 'ck-po-mail' });
    if (s.emailed_at) {
      const sent = el('a', { class: 'ck-po-sent' }, [T('pay_email_sent_at', { date: _fmtEmailedDate(s.emailed_at) })]);
      sent.onclick = (e) => { e.stopPropagation(); _sendPayEmail(s, true); };   // resend behind confirm
      mail.appendChild(sent);
    } else {
      const send = el('a', { class: 'ck-po-mailbtn' }, ['✉ ' + T('pay_email_send')]);
      send.onclick = (e) => { e.stopPropagation(); _sendPayEmail(s, false); };
      mail.appendChild(send);
    }
    amt.appendChild(mail);
  }
  row.appendChild(amt);
  row.onclick = () => { CS.payouts.expanded[s.user_id] = !open; render(); };
  body.appendChild(row);
  if (open) body.appendChild(_payoutBreakdown(s));
}

function _payoutBreakdown(s) {
  const box = el('div', { class: 'ck-po-exp' });
  const m = CS.payouts.month;
  (s.breakdown || []).forEach(l => {
    const label = (l.attendance === 'no_show' && Number(l.unit_charge) === 0)
      ? T('pay_bd_noshow_free', { n: l.n, type: l.type_name })
      : (l.attendance === 'no_show'
          ? T('pay_bd_noshow_charged', { n: l.n, type: l.type_name, unit: _money2(l.unit_charge) })
          : T('pay_bd_at', { n: l.n, type: l.type_name, unit: _money2(l.unit_charge) }));
    // door C: priced line → that specialist's 'type' verification list
    box.appendChild(_poLine(label, _money2(l.subtotal), false, () => openWorklist({ kind: 'type', sessionTypeId: l.session_type_id, month: m, userId: s.user_id, origin: 'payouts', title: l.type_name || T('pay_wl_title_type') })));
  });
  // muted, non-money reconcile lines (header counts ↔ money lines) — door C: tap → that specialist's list.
  if (s.counts && s.counts.unclassified_billable > 0) box.appendChild(_poLine(T('pay_bd_untyped', { n: s.counts.unclassified_billable }), '', true, () => openWorklist({ kind: 'untyped', month: m, userId: s.user_id, origin: 'payouts', title: T('pay_wl_title_untyped') })));
  if (s.counts && s.counts.unmarked > 0) box.appendChild(_poLine(T('pay_bd_unmarked', { n: s.counts.unmarked }), '', true, () => openWorklist({ kind: 'unmarked', month: m, userId: s.user_id, origin: 'payouts', title: T('pay_wl_title_unmarked') })));
  const gross = _poLine(T('pay_bd_gross'), _money2(s.gross)); gross.classList.add('sec'); box.appendChild(gross);
  if (s.vat_step != null) box.appendChild(_poLine(T('pay_bd_aftervat', { v: Math.round((CS.payouts.data?.vat_rate || 0) * 100) }), _money2(s.vat_step), true));
  if (s.terms == null) {
    const r = el('div', { class: 'ck-po-line sec' });
    r.appendChild(el('span', { class: 'ck-po-warn-l' }, [T('pay_setup_pay_hint')]));
    const btn = el('button', { class: 'ck-btn secondary xs' }, [T('pay_set_up')]);
    btn.onclick = (e) => { e.stopPropagation(); _payoutsSetUpPay(s); };
    r.appendChild(btn); box.appendChild(r);
  } else {
    const r = _poLine(_termsSummary(s.terms), _money2(s.payout)); r.classList.add('total'); box.appendChild(r);
  }
  return box;
}
function _poLine(l, v, muted, go) {
  const r = el('div', { class: 'ck-po-line' + (muted ? ' muted' : '') + (go ? ' ck-row-click' : '') });
  r.appendChild(el('span', {}, [l]));
  const right = el('span', { class: 'num', style: { display: 'inline-flex', alignItems: 'center', gap: '6px' } }, [v]);
  if (go) { right.appendChild(el('span', { class: 'ck-chev' }, ['›'])); r.onclick = go; }
  r.appendChild(right);
  return r;
}

// "set up pay" → the SC-B1 pay modal. Needs CS.billing (catalog + pay rows) → lazy-load it; on save, refresh payouts.
async function _payoutsSetUpPay(s) {
  if (!CS.billing) { await loadBilling(); render(); }
  const pay = (CS.billing?.specialist_pay || []).find(p => p.user_id === s.user_id) || null;
  _payModal({ user_id: s.user_id, name: s.display_name, role: 'member' }, pay, () => loadPayouts(CS.payouts.month));
}

// ── SC-B6b: finalize pre-flight (the prototype's "Finalize" popup) ──────────────
// Four warning lines (unclassified / unmarked deep-link into SC-B4c worklists; nopay + unconfirmed NAME the
// specialists) + per-specialist payout totals, then "Finalize & lock". Server enforces the same guards.
function _finalizePreflight(month, specs, ct) {
  const reviewOn = !!(CS.billing && CS.billing.billing_settings && CS.billing.billing_settings.review_toggle);
  ckDialog(T('pay_finalize_title', { month: _monthLabel(month) }), (bd, close) => {
    bd.appendChild(el('div', { class: 'ck-hint' }, [T('pay_finalize_intro')]));
    // four warning lines — unclassified / unmarked deep-link into the SC-B4c worklists (closing this dialog first)
    const warns = [];
    if (ct.unclassified_billable) warns.push({ t: T('pay_catch_unclassified', { n: ct.unclassified_billable }), go: () => { close(); openWorklist({ kind: 'untyped', month, origin: 'payouts', title: T('pay_wl_title_untyped') }); } });
    if (ct.unmarked) warns.push({ t: T('pay_catch_unmarked', { n: ct.unmarked }), go: () => { close(); openWorklist({ kind: 'unmarked', month, origin: 'payouts', title: T('pay_wl_title_unmarked') }); } });
    const nopay = specs.filter(s => s.terms == null).map(s => s.display_name || '—');
    if (nopay.length) warns.push({ t: T('pay_finalize_warn_nopay', { names: nopay.join(', ') }) });
    if (reviewOn) { const unconf = specs.filter(s => s.status !== 'confirmed' && s.status !== 'finalized').map(s => s.display_name || '—'); if (unconf.length) warns.push({ t: T('pay_finalize_warn_unconfirmed', { names: unconf.join(', ') }) }); }
    if (warns.length) { const wb = el('div', { class: 'ck-po-warn', style: { marginTop: '10px' } }); warns.forEach(o => { const line = el('div', { class: 'ck-po-warn-l' + (o.go ? ' ck-row-click' : '') }, ['⚠ ' + o.t]); if (o.go) { line.appendChild(el('span', { class: 'ck-chev' }, [' ›'])); line.onclick = o.go; } wb.appendChild(line); }); bd.appendChild(wb); }
    // per-specialist payout totals
    bd.appendChild(el('div', { class: 'ck-dlg-subh', style: { marginTop: '12px' } }, [T('pay_finalize_totals')]));
    let total = 0;
    specs.forEach(s => {
      const r = el('div', { class: 'ck-po-line' });
      r.appendChild(el('span', { dir: 'auto' }, [s.display_name || '—']));
      if (s.terms == null) { r.appendChild(el('span', { class: 'ck-po-warn-l' }, [T('pay_setup_pay')])); }
      else { total += Number(s.payout || 0); r.appendChild(el('span', { class: 'num' }, [_money2(s.payout)])); }
      bd.appendChild(r);
    });
    const tr = _poLine(T('pay_finalize_total'), _money2(total)); tr.classList.add('total'); bd.appendChild(tr);
  }, [
    { label: T('pay_cancel'), cls: 'ghost', onClick: (close) => close() },
    { label: T('pay_finalize_confirm'), cls: 'primary', onClick: (close) => _doFinalize(month, close) }
  ]);
}
async function _doFinalize(month, close) {
  try {
    const { error } = await supa().rpc('clinic_finalize_month', { p_clinic: CS.overview?.clinic_id, p_month: month });
    if (error) throw error;
    close(); toast(T('pay_finalize_toast'), 'success'); loadPayouts(month);
  } catch (e) { console.error('❌ clinic_finalize_month:', e); toast(/finalize a month/i.test((e && e.message) || '') ? T('pay_finalize_not_ended') : T('pay_finalize_err'), 'error'); }
}
// ── SC-B6b: reopen (head-only, confirm). Warns if a pay email was already sent (SC-B6c: the REAL marker). ──
function _reopenConfirm(month, specs) {
  const emailed = (specs || []).some(s => s.emailed_at);   // SC-B6c: clinic_payouts surfaces emailed_at per specialist
  ckDialog(T('pay_reopen_title', { month: _monthLabel(month) }), (bd) => {
    bd.appendChild(el('div', { class: 'ck-hint' }, [T('pay_reopen_intro')]));
    if (emailed) bd.appendChild(el('div', { class: 'ck-po-warn', style: { marginTop: '10px' } }, [el('div', { class: 'ck-po-warn-l' }, ['⚠ ' + T('pay_reopen_warn_emailed')])]));
  }, [
    { label: T('pay_cancel'), cls: 'ghost', onClick: (close) => close() },
    { label: T('pay_reopen_confirm'), cls: 'danger', onClick: (close) => _doReopen(month, close) }
  ]);
}
async function _doReopen(month, close) {
  try {
    const { error } = await supa().rpc('clinic_reopen_month', { p_clinic: CS.overview?.clinic_id, p_month: month });
    if (error) throw error;
    close(); toast(T('pay_reopen_toast'), 'success'); loadPayouts(month);
  } catch (e) { console.error('❌ clinic_reopen_month:', e); toast(T('pay_reopen_err'), 'error'); }
}

// ── SC-B6c: send the pay email (head). Resend goes behind a confirm; the endpoint marks emailed_at only on a
// successful Resend send, so we re-fetch payouts to render the REAL "Sent {date}" marker (never optimistic). ──
function _fmtEmailedDate(iso) { try { return _ckDate(new Date(iso), { month: 'short', day: 'numeric' }); } catch (e) { return ''; } }
function _sendPayEmail(s, isResend) {
  if (isResend) {
    return ckDialog(T('pay_email_resend_title'), (bd) => { bd.appendChild(el('div', { class: 'ck-hint' }, [T('pay_email_resend_body')])); }, [
      { label: T('pay_cancel'), cls: 'ghost', onClick: (close) => close() },
      { label: T('pay_email_resend_confirm'), cls: 'primary', onClick: (close) => { close(); _doSendPayEmail(s); } }
    ]);
  }
  _doSendPayEmail(s);
}
async function _doSendPayEmail(s) {
  try {
    const sess = (await supa().auth.getSession()).data.session;
    const tok = sess && sess.access_token;
    if (!tok) throw new Error('no session');
    const resp = await fetch('/api/send-invite', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + tok },
      body: JSON.stringify({ type: 'pay_summary', clinicId: CS.overview?.clinic_id, userId: s.user_id, month: CS.payouts.month })
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(data.error || 'send failed');
    toast(T('pay_email_sent_toast'), 'success');
    loadPayouts(CS.payouts.month);   // refresh → the row now shows "Sent {date}" from the persisted emailed_at
  } catch (e) { console.error('❌ pay email:', e); toast(T('pay_email_err'), 'error'); }
}

// ══ SC-B4c: session worklists — ONE list component, THREE doors (calendar chip / catch-net / breakdown) ══
// opts: { kind:'untyped'|'unmarked'|'type', month, userId?, sessionTypeId?, title, origin:'payouts'|'calendar' }
async function openWorklist(opts) {
  CS.view = 'worklist'; CS.member = null; CS.client = null; CS.artifact = null; CS.err = null;
  CS.worklist = Object.assign({ userId: null, sessionTypeId: null, data: null }, opts);
  await loadWorklist();
}
async function loadWorklist() {
  const w = CS.worklist; CS.loading = true; CS.err = null; render();
  try {
    const { data, error } = await supa().rpc('clinic_session_worklist', {
      p_clinic: CS.overview?.clinic_id, p_month: w.month, p_kind: w.kind,
      p_session_type_id: w.sessionTypeId || null, p_user_id: w.userId || null });
    if (error) throw error; w.data = data;
  } catch (e) { console.error('❌ clinic_session_worklist:', e); CS.err = T('pay_err_load'); }
  CS.loading = false; render();
}
// After a successful row change: re-fetch THIS list (+ its count pill) and silently refresh whichever
// door opened it, so its number is fresh on goBack (payouts catch-net/breakdown, or the calendar chip).
function _worklistChanged() {
  loadWorklist();
  const w = CS.worklist; if (!w) return;
  if (w.origin === 'payouts' && CS.payouts) {
    supa().rpc('clinic_payouts', { p_clinic: CS.overview?.clinic_id, p_month: CS.payouts.month }).then(
      ({ data }) => { if (data) { CS.payouts.data = data; const ct = data.clinic_totals || {}; CS.payoutsCatchNet = (ct.unclassified_billable || 0) + (ct.unmarked || 0); } }, () => {});
  } else if (w.origin === 'calendar' && CS.cal) {
    supa().rpc('clinic_worklist_counts', { p_clinic: CS.overview?.clinic_id, p_month: _monthStart(new Date(CS.cal.weekStart + 'T00:00:00')) }).then(
      ({ data }) => { CS.cal.counts = data || null; }, () => {});
  }
}
// Row action: reuse the full session dialog (SC-B2b type drawer + SC-B3b attendance picker + charge).
// Fetch the appt's week row so onApptTap has every field it needs; hand it _worklistChanged as onChanged.
async function _openSessionFromWorklist(s) {
  if (!can('calendar')) return;   // C-5 C2
  try {
    const wk = ymd(weekStartDay(new Date((s.date || ymd(new Date())) + 'T00:00:00')));
    const { data, error } = await supa().rpc('clinic_calendar', { p_week_start: wk });
    if (error) throw error;
    const full = (data || []).find(x => x.id === s.id);
    if (!full) { toast(T('pay_toast_error'), 'error'); return; }
    onApptTap(full, _worklistChanged);
  } catch (e) { console.error('❌ worklist open session:', e); toast(T('pay_toast_error'), 'error'); }
}
function renderWorklistView(body) {
  const w = CS.worklist; if (!w) return;
  const d = w.data; if (!d) return;   // CS.loading spinner covers the fetch
  const sessions = d.sessions || [];
  const head = el('div', { class: 'ck-wl-head' });
  head.appendChild(el('span', { class: 'ck-wl-countpill' }, [String(d.count || 0)]));
  const hintKey = w.kind === 'untyped' ? 'pay_wl_hint_untyped' : w.kind === 'unmarked' ? 'pay_wl_hint_unmarked' : 'pay_wl_hint_type';
  head.appendChild(el('span', { class: 'ck-wl-hint' }, [T(hintKey)]));
  body.appendChild(head);
  if (!sessions.length) {   // R6: completion state, never a blank
    const emptyKey = w.kind === 'untyped' ? 'pay_wl_empty_untyped' : w.kind === 'unmarked' ? 'pay_wl_empty_unmarked' : 'pay_wl_empty_type';
    body.appendChild(el('div', { class: 'ck-wl-empty' }, [T(emptyKey)]));
    return;
  }
  // F-91: search + filter (client, date, therapist) on the loaded sessions. No type chips: every row of the untyped list is "not set" by definition, and the
  // amount column exists only for kind 'type' (payouts capability); the bar adds no money of its own.
  const f = w.flt || (w.flt = _ckFltNew());
  const fbar = _ckFilterBar(f, sessions, { types: false, onChange: () => paint() });
  body.appendChild(fbar);
  const cap = _ckSortCaption(sessions, 'worklist'); if (cap) body.appendChild(el('div', { class: 'ck-flt-sort' }, [cap]));
  const host = el('div', { class: 'ck-flt-list' }); body.appendChild(host);
  function paint() {
    const shown = sessions.filter(s => _ckFltPass(f, s, false));
    fbar.setCount(shown.length, sessions.length);
    host.replaceChildren();
    if (!shown.length) { host.appendChild(_ckFltEmpty(f, () => { Object.assign(f, _ckFltNew()); render(); })); return; }
    shown.forEach(s => host.appendChild(_wlRow(w, s)));
  }
  paint();
}
function _wlRow(w, s) {
  {
    const row = el('div', { class: 'ck-wl-row ck-row-click' });
    row.appendChild(el('div', { class: 'ck-wl-when' }, [_bdi(_ckRowDate(s.date)), el('span', { class: 'ck-sub' }, [_tr(s.time)])]));
    const mid = el('div', { class: 'ck-grow' });
    mid.appendChild(el('div', { class: 'ck-nm' }, [_bdi(s.patient_name || '—')]));
    mid.appendChild(el('div', { class: 'ck-sub' }, [_bdi(s.member_name || ''), (s.session_type_name ? ' · ' + s.session_type_name : '')]));
    row.appendChild(mid);
    if (s.attendance) { const am = attMeta(s.attendance); row.appendChild(el('span', { class: 'ck-badge ' + am.cls }, [(am.glyph ? am.glyph + ' ' : '') + T(am.labelKey)])); }
    if (w.kind === 'type') row.appendChild(el('span', { class: 'ck-wl-amt num' }, [_money2(s.amount)]));
    else row.appendChild(el('span', { class: 'ck-chev' }, ['›']));
    row.onclick = () => _openSessionFromWorklist(s);
    return row;
  }
}

// ══ BILLING hub (SC-B1) — head-only settings: types, overrides, pay, clinic settings ══
// Pull-based: clinic_billing_hub loads once on open (logs ONE billing_hub_open); every write
// is ONE RPC that returns the changed row, patched into CS.billing locally (no re-fetch, no re-log).
async function openBilling() {
  CS.view = 'billing'; CS.billingScreen = 'hub'; CS.billing = null; CS.err = null;
  await loadBilling(); render();
}
async function loadBilling() {
  CS.loading = true; CS.err = null; render();
  try {
    const cid = CS.overview?.clinic_id;
    const { data, error } = await supa().rpc('clinic_billing_hub', { p_clinic: cid });
    if (error) throw error;
    CS.billing = data;
  } catch (e) { console.error('❌ clinic_billing_hub:', e); CS.err = T('pay_err_load'); }
  CS.loading = false;
}
const _bclinic = () => CS.billing?.clinic_id || CS.overview?.clinic_id;
const _vat = () => Number(CS.billing?.billing_settings?.vat_rate ?? 0.18);
function _money(n) { return '₪' + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }); }
function _money2(n) { return '₪' + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function _durLabel(min) { return (min == null) ? T('pay_dur_any') : T('pay_min', { n: min }); }
function _typeName(id) { const t = (CS.billing?.session_types || []).find(x => x.id === id); return t ? t.name : ''; }
function _childName(id) { const c = (CS.billing?.clients || []).find(x => x.child_id === id); return c ? c.name : id; }
// F-43 — a client is a child (child_id) or a private client of the clinic (specialist_patient_id). One key for both:
// 'c:<child_id>' / 'p:<specialist_patient_id>'. Rows of the overrides / defaults lists carry the same two columns.
function _clientKey(r) { return r?.specialist_patient_id ? 'p:' + r.specialist_patient_id : (r?.child_id ? 'c:' + r.child_id : null); }
function _clientArgs(key) { const k = key || ''; return { p_child_id: k.startsWith('c:') ? k.slice(2) : null, p_specialist_patient_id: k.startsWith('p:') ? k.slice(2) : null }; }
function _clientNameByKey(key) { const c = (CS.billing?.clients || []).find(x => _clientKey(x) === key); return c ? c.name : null; }
function _clientLabel(r) { return r.client_name || r.child_name || _clientNameByKey(_clientKey(r)) || '—'; }
// The picker: native <select>, children and private clients in two groups. An existing row whose client is no longer
// offered (e.g. an archived private client) keeps its own option so it can still be edited. Empty → the empty state.
function _clientPicker(key, existing, onChange) {
  const clients = CS.billing.clients || [];
  if (!clients.length && !existing) return null;
  const csel = el('select', { class: 'ck-inp ck-client-sel' });
  if (existing && !clients.some(c => _clientKey(c) === key)) csel.appendChild(el('option', { value: key }, [_clientLabel(existing)]));
  [['child', 'f43_group_family'], ['private', 'f43_group_private']].forEach(([kind, lbl]) => {
    const list = clients.filter(c => (c.kind || 'child') === kind);
    if (!list.length) return;
    const g = el('optgroup', { label: T(lbl) });
    list.forEach(c => g.appendChild(el('option', { value: _clientKey(c) }, [c.name])));
    csel.appendChild(g);
  });
  csel.value = key || '';
  csel.onchange = () => onChange(csel.value);
  return csel;
}
// The re-price result (preview before Save, or what the save did): a count line and the paid sessions kept.
function _repriceText(r) {
  const n = r?.update_count || 0, paid = (r?.paid_kept || []).length;
  if (!n && !paid) return T('f43_reprice_none');
  return n === 1 ? T('f43_reprice_count_one') : T('f43_reprice_count', { n });
}
function _repriceLines(box, r) {
  box.replaceChildren();
  if (!r) return;
  box.appendChild(el('div', { class: 'ck-rp-n' }, [_repriceText(r)]));
  const paid = r.paid_kept || [];
  if (!paid.length) return;
  box.appendChild(el('div', { class: 'ck-rp-paid' }, [paid.length === 1 ? T('f43_reprice_paid_one') : T('f43_reprice_paid', { n: paid.length })]));
  const list = el('div', { class: 'ck-rp-list' });
  paid.forEach(s => {
    const row = el('div', { class: 'ck-rp-row' });
    row.appendChild(el('span', {}, [s.date ? _ckDate(new Date(s.date + 'T00:00:00'), { day: 'numeric', month: 'short' }) : '']));
    const tr = _tr(s.time); if (tr) row.appendChild(tr);
    row.appendChild(el('span', { dir: 'auto' }, [s.specialist_name || '']));
    row.appendChild(el('span', { dir: 'auto' }, [s.session_type_name || '']));
    row.appendChild(el('span', { class: 'ck-rp-price', dir: 'ltr' }, [_money(s.stored_price)]));
    list.appendChild(row);
  });
  box.appendChild(list);
}
// Preview: one read per settled change (300 ms), an answer overtaken by a newer request is dropped (same guard as F-48).
function _repricePreview(box, getArgs) {
  let seq = 0, tmr = null;
  const run = async () => {
    const my = ++seq; const args = getArgs();
    if (!args) { _repriceLines(box, null); return; }
    try {
      const { data, error } = await supa().rpc('clinic_client_reprice_preview', Object.assign({ p_clinic: _bclinic() }, args));
      if (my !== seq) return;
      if (error) throw error;
      _repriceLines(box, data);
    } catch (e) { if (my === seq) { console.error('❌ clinic_client_reprice_preview:', e); _repriceLines(box, null); } }
  };
  return { now: run, soon: () => { clearTimeout(tmr); seq++; tmr = setTimeout(run, 300); } };
}
// After a save: the toast says what the save actually did (its own result, not the preview).
function _savedToast(msgKey, reprice) { toast(T(msgKey) + (reprice ? ' · ' + _repriceText(reprice) : ''), 'success'); }
function _billErr(e, fallback) { console.error('❌ billing:', e); toast((e && e.code === '23505') ? (fallback || T('pay_toast_error')) : (fallback || T('pay_toast_error')), 'error'); }
// a representative active type for the pay "Example" column: prefer 45-min, else first active w/ price
function _exampleType() {
  const ts = (CS.billing?.session_types || []).filter(t => t.active);
  return ts.find(t => t.duration_minutes === 45) || ts.find(t => Number(t.price) > 0) || ts[0] || null;
}
function _payExample(pay, ex) {
  if (!pay || !ex) return null;
  const price = Number(ex.price) || 0, vat = _vat();
  if (pay.model === 'fixed') return Number(pay.default_fixed) || 0;
  const pct = Number(pay.default_percent) || 0;
  const base = pay.vat_split === 'after' ? (price / (1 + vat)) : price;
  return pct / 100 * base;
}

function renderBilling(body) {
  if (!CS.billing) { if (!CS.err) body.appendChild(el('div', { class: 'ck-loading' }, ['…'])); return; }
  if (CS.billingScreen === 'prices') return renderBillingPrices(body);
  if (CS.billingScreen === 'overrides') return renderBillingOverrides(body);
  if (CS.billingScreen === 'pay') return renderBillingPay(body);
  return renderBillingHub(body);
}

function _crumb(body, label) {
  const c = el('div', { class: 'ck-crumb' });
  const a = el('a', {}, [T('pay_billing')]); a.onclick = () => { CS.billingScreen = 'hub'; render(); };
  c.appendChild(a); c.appendChild(document.createTextNode(' › ' + label)); body.appendChild(c);
}

function renderBillingHub(body) {
  const b = CS.billing;
  body.appendChild(el('div', { class: 'ck-sub-lead' }, [T('pay_hub_sub')]));
  const grid = el('div', { class: 'ck-hub' });
  const notSet = (b.members || []).filter(m => !(b.specialist_pay || []).some(p => p.user_id === m.user_id)).length;
  const bs = b.billing_settings || {};
  const startLabel = bs.billing_start_month ? String(bs.billing_start_month).slice(0, 7) : T('pay_start_none');
  const card = (icon, t, d, meta, onClick) => {
    const c = el('div', { class: 'ck-hubcard' });
    c.appendChild(el('div', { class: 'ck-hub-ic' }, [icon]));
    c.appendChild(el('div', { class: 'ck-hub-t' }, [t]));
    c.appendChild(el('div', { class: 'ck-hub-d' }, [d]));
    if (meta) c.appendChild(el('div', { class: 'ck-hub-meta' }, [meta]));
    c.onclick = onClick; return c;
  };
  grid.appendChild(card('🏷️', T('pay_card_types_t'), T('pay_card_types_d'),
    T('pay_types_count', { n: (b.session_types || []).length }), () => { CS.billingScreen = 'prices'; render(); }));
  grid.appendChild(card('🤝', T('pay_card_overrides_t'), T('pay_card_overrides_d'),
    T('pay_overrides_count', { n: (b.client_overrides || []).length }), () => { CS.billingScreen = 'overrides'; render(); }));
  grid.appendChild(card('💼', T('pay_card_pay_t'), T('pay_card_pay_d'),
    notSet ? T('pay_pay_count', { n: (b.members || []).length, m: notSet }) : T('pay_pay_count_all', { n: (b.members || []).length }),
    () => { CS.billingScreen = 'pay'; render(); }));
  grid.appendChild(card('⚙️', T('pay_card_settings_t'), T('pay_card_settings_d'),
    T('pay_settings_meta', { v: Math.round(_vat() * 100), r: bs.review_toggle ? T('pay_review_on') : T('pay_review_off'), m: startLabel }),
    _settingsModal));
  body.appendChild(grid);
}

// ── Price list ──
function renderBillingPrices(body) {
  _crumb(body, T('pay_card_types_t'));
  const bar = el('div', { class: 'ck-actbar' });
  const add = el('button', { class: 'ck-btn primary sm' }, [T('pay_add_type')]); add.onclick = () => _typeModal(null); bar.appendChild(add);
  body.appendChild(bar);
  body.appendChild(el('div', { class: 'ck-hint' }, [T('pay_types_hint')]));
  const types = CS.billing.session_types || [];
  if (!types.length) { body.appendChild(el('div', { class: 'ck-empty' }, [T('pay_empty_types')])); return; }
  const tbl = el('table', { class: 'ck-tbl' });
  const head = el('tr', {}, [el('th', {}, [T('pay_col_name')]), el('th', {}, [T('pay_col_duration')]), el('th', { class: 'pay-num' }, [T('pay_col_price')]), el('th', {}, [T('pay_col_active')]), el('th', {}, [''])]);
  tbl.appendChild(el('thead', {}, [head]));
  const tb = el('tbody');
  types.forEach(t => {
    const tr = el('tr', {});
    tr.appendChild(el('td', {}, [t.name]));
    tr.appendChild(el('td', {}, [_durLabel(t.duration_minutes)]));
    tr.appendChild(el('td', { class: 'pay-num' }, [_money(t.price)]));
    const tgl = el('span', { class: 'ck-toggle' + (t.active ? ' on' : '') });
    tgl.onclick = async () => {
      try { const { data, error } = await supa().rpc('clinic_set_session_type_active', { p_clinic: _bclinic(), p_id: t.id, p_active: !t.active }); if (error) throw error; _patchType(data); render(); }
      catch (e) { _billErr(e); }
    };
    tr.appendChild(el('td', {}, [tgl]));
    const ed = el('button', { class: 'ck-btn ghost xs' }, [T('pay_edit')]); ed.onclick = () => _typeModal(t);
    tr.appendChild(el('td', {}, [ed]));
    tb.appendChild(tr);
  });
  tbl.appendChild(tb); body.appendChild(tbl);
  body.appendChild(el('div', { class: 'ck-foot-note' }, [T('pay_types_footnote')]));
}

function _patchType(row) {
  const arr = (CS.billing.session_types || []).slice();
  const i = arr.findIndex(x => x.id === row.id);
  if (i > -1) arr[i] = row; else arr.push(row);
  arr.sort((a, b) => (a.sort - b.sort) || String(a.name).localeCompare(String(b.name)));
  CS.billing.session_types = arr;
}

function _typeModal(existing) {
  let name = existing?.name || '';
  let dur = existing ? (existing.duration_minutes == null ? '' : String(existing.duration_minutes)) : '';
  let price = existing ? String(existing.price) : '';
  ckDialog(T('pay_type_modal_title'), (bd) => {
    bd.appendChild(el('div', { class: 'ck-mh' }, [T('pay_type_modal_sub')]));
    bd.appendChild(el('div', { class: 'ck-lbl' }, [T('pay_f_name')]));
    const ni = el('input', { class: 'ck-inp', type: 'text', value: name }); ni.oninput = () => name = ni.value; bd.appendChild(ni);
    bd.appendChild(el('div', { class: 'ck-lbl' }, [T('pay_f_duration')]));
    const sel = el('select', { class: 'ck-inp' });
    sel.appendChild(el('option', { value: '' }, [T('pay_dur_any')]));
    [15, 30, 45, 60, 90].forEach(m => { const o = el('option', { value: String(m) }, [T('pay_minutes', { n: m })]); if (dur === String(m)) o.selected = true; sel.appendChild(o); });
    sel.onchange = () => dur = sel.value; bd.appendChild(sel);
    bd.appendChild(el('div', { class: 'ck-lbl' }, [T('pay_f_price')]));
    const pi = el('input', { class: 'ck-inp', type: 'number', value: price, min: '0' }); pi.oninput = () => price = pi.value; bd.appendChild(pi);
  }, [
    { label: T('pay_cancel'), cls: 'ghost', onClick: c => c() },
    { label: T('pay_save'), cls: 'primary', onClick: async (c) => {
      if (!name.trim()) { toast(T('pay_name_required'), 'error'); return; }
      // Full-payload rule: always send current price (update branch zeroes a NULL price).
      try {
        const { data, error } = await supa().rpc('clinic_upsert_session_type', {
          p_clinic: _bclinic(), p_id: existing?.id || null, p_name: name.trim(),
          p_price: Number(price) || 0, p_duration_minutes: dur === '' ? null : Number(dur),
          p_sort: existing?.sort ?? 0
        });
        if (error) throw error; _patchType(data); toast(T('pay_toast_type_saved'), 'success'); c(); render();
      } catch (e) { _billErr(e); }
    } },
  ]);
}

// ── Client overrides ──
function renderBillingOverrides(body) {
  _crumb(body, T('pay_card_overrides_t'));
  const bar = el('div', { class: 'ck-actbar' });
  const add = el('button', { class: 'ck-btn primary sm' }, [T('pay_add_override')]); add.onclick = () => _overrideModal(null); bar.appendChild(add);
  body.appendChild(bar);
  body.appendChild(el('div', { class: 'ck-hint' }, [T('pay_ovr_hint')]));
  const ovr = CS.billing.client_overrides || [];
  if (!ovr.length) { body.appendChild(el('div', { class: 'ck-empty' }, [T('pay_empty_overrides')])); _renderClientDefaults(body); return; }
  const tbl = el('table', { class: 'ck-tbl' });
  tbl.appendChild(el('thead', {}, [el('tr', {}, [el('th', {}, [T('pay_col_client')]), el('th', {}, [T('pay_col_applies')]), el('th', { class: 'pay-num' }, [T('pay_col_list')]), el('th', { class: 'pay-num' }, [T('pay_col_theirs')]), el('th', {}, [''])])]));
  const tb = el('tbody');
  ovr.forEach(o => {
    const tr = el('tr', {});
    tr.appendChild(el('td', { dir: 'auto' }, [_clientLabel(o)]));
    if (o.session_type_id) {
      const t = (CS.billing.session_types || []).find(x => x.id === o.session_type_id);
      tr.appendChild(el('td', {}, [o.session_type_name || _typeName(o.session_type_id)]));
      tr.appendChild(el('td', { class: 'pay-num ck-strike' }, [t ? _money(t.price) : T('pay_none_dash')]));
      tr.appendChild(el('td', { class: 'pay-num' }, [el('b', {}, [_money(o.fixed_price)])]));
    } else {
      tr.appendChild(el('td', {}, [T('pay_all_types')]));
      tr.appendChild(el('td', { class: 'pay-num' }, [T('pay_none_dash')]));
      tr.appendChild(el('td', { class: 'pay-num' }, [el('b', {}, ['−' + o.percent_off + '%'])]));
    }
    const ed = el('button', { class: 'ck-btn ghost xs' }, [T('pay_edit')]); ed.onclick = () => _overrideModal(o);
    tr.appendChild(el('td', {}, [ed]));
    tb.appendChild(tr);
  });
  tbl.appendChild(tb); body.appendChild(tbl);
  body.appendChild(el('div', { class: 'ck-foot-note' }, [T('pay_ovr_footnote')]));
  _renderClientDefaults(body);
}

// ── SC-B2b: client default session type — a sub-section on the overrides screen (one job
// per the §3b hub principle). Reads CS.billing.client_defaults (from the hub); writes via
// clinic_set_client_default (NULL clears). child_id-keyed (one default per client).
function _renderClientDefaults(body) {
  body.appendChild(el('div', { class: 'ck-dlg-sep' }));
  body.appendChild(el('div', { class: 'ck-sub-lead', style: { marginTop: '4px' } }, [T('pay_defaults_title')]));
  const bar = el('div', { class: 'ck-actbar' });
  const add = el('button', { class: 'ck-btn primary sm' }, [T('pay_defaults_add')]); add.onclick = () => _defaultModal(null); bar.appendChild(add);
  body.appendChild(bar);
  body.appendChild(el('div', { class: 'ck-hint' }, [T('pay_defaults_hint')]));
  const defs = CS.billing.client_defaults || [];
  if (!defs.length) { body.appendChild(el('div', { class: 'ck-empty' }, [T('pay_defaults_empty')])); return; }
  const tbl = el('table', { class: 'ck-tbl' });
  tbl.appendChild(el('thead', {}, [el('tr', {}, [el('th', {}, [T('pay_col_client')]), el('th', {}, [T('pay_col_default')]), el('th', {}, [''])])]));
  const tb = el('tbody');
  defs.forEach(d => {
    const tr = el('tr', {});
    tr.appendChild(el('td', { dir: 'auto' }, [_clientLabel(d)]));
    tr.appendChild(el('td', {}, [d.session_type_name || _typeName(d.default_session_type_id)]));
    const ed = el('button', { class: 'ck-btn ghost xs' }, [T('pay_edit')]); ed.onclick = () => _defaultModal(d);
    tr.appendChild(el('td', {}, [ed]));
    tb.appendChild(tr);
  });
  tbl.appendChild(tb); body.appendChild(tbl);
}
function _patchDefault(row) {
  const key = _clientKey(row);
  row.client_kind = row.specialist_patient_id ? 'private' : 'child';
  row.client_name = _clientNameByKey(key) || row.client_name || null;
  row.child_name = row.child_id ? _childName(row.child_id) : null;
  row.session_type_name = row.default_session_type_id ? _typeName(row.default_session_type_id) : null;
  const arr = (CS.billing.client_defaults || []).slice();
  const i = arr.findIndex(x => _clientKey(x) === key);
  if (i > -1) arr[i] = row; else arr.push(row);
  CS.billing.client_defaults = arr;
}
function _removeDefault(key) { CS.billing.client_defaults = (CS.billing.client_defaults || []).filter(x => _clientKey(x) !== key); }
function _defaultModal(existing) {
  let key = existing ? _clientKey(existing) : _clientKey((CS.billing.clients || [])[0]);
  let typeId = existing?.default_session_type_id || (CS.billing.session_types || []).filter(t => t.active)[0]?.id || null;
  const noClients = !(CS.billing.clients || []).length && !existing;
  let pv = null;
  ckDialog(T('pay_default_modal_title'), (bd) => {
    bd.appendChild(el('div', { class: 'ck-mh' }, [T('pay_default_modal_sub')]));
    bd.appendChild(el('div', { class: 'ck-lbl' }, [T('pay_f_client')]));
    const csel = _clientPicker(key, existing, v => { key = v; pv.soon(); });
    if (!csel) { bd.appendChild(el('div', { class: 'ck-empty ck-f43-empty' }, [T('f43_no_clients')])); return; }
    bd.appendChild(csel);
    bd.appendChild(el('div', { class: 'ck-lbl' }, [T('pay_f_default_type')]));
    const tsel = el('select', { class: 'ck-inp' });
    (CS.billing.session_types || []).filter(t => t.active).forEach(t => { const o = el('option', { value: t.id }, [t.name]); if (t.id === typeId) o.selected = true; tsel.appendChild(o); });
    tsel.onchange = () => { typeId = tsel.value; pv.soon(); }; bd.appendChild(tsel);
    const box = el('div', { class: 'ck-rp' }); bd.appendChild(box);
    pv = _repricePreview(box, () => key && typeId ? Object.assign({ p_action: 'default', p_id: null, p_session_type_id: typeId, p_fixed_price: null, p_percent_off: null }, _clientArgs(key)) : null);
    pv.now();
  }, [
    ...(existing ? [{ label: T('pay_delete'), cls: 'danger', onClick: (c) => {
      const ekey = _clientKey(existing);
      openConfirmDlg(T('pay_default_delete_confirm'), async () => {
        try { const { data, error } = await supa().rpc('clinic_set_client_default', Object.assign({ p_clinic: _bclinic(), p_session_type_id: null }, _clientArgs(ekey))); if (error) throw error; _removeDefault(ekey); _savedToast('pay_toast_default_deleted', data?.reprice); c(); render(); }
        catch (e) { _billErr(e); }
      }, (bd) => { const box = el('div', { class: 'ck-rp' }); bd.appendChild(box);
        _repricePreview(box, () => Object.assign({ p_action: 'default', p_id: null, p_session_type_id: null, p_fixed_price: null, p_percent_off: null }, _clientArgs(ekey))).now(); });
    } }] : []),
    { label: T('pay_cancel'), cls: 'ghost', onClick: c => c() },
    { label: T('pay_save'), cls: 'primary', disabled: noClients, onClick: async (c) => {
      if (noClients) return;
      if (!key) { toast(T('pay_pick_client'), 'error'); return; }
      if (!typeId) { toast(T('pay_pick_type'), 'error'); return; }
      try {
        const { data, error } = await supa().rpc('clinic_set_client_default', Object.assign({ p_clinic: _bclinic(), p_session_type_id: typeId }, _clientArgs(key)));
        if (error) throw error;
        const { reprice, ...row } = data || {};
        _patchDefault(row); _savedToast('pay_toast_default_saved', reprice); c(); render();
      } catch (e) { _billErr(e); }
    } },
  ]);
}

function _patchOverride(row) {
  // enrich with names from local lookups (the write RPC returns raw columns)
  row.client_kind = row.specialist_patient_id ? 'private' : 'child';
  row.client_name = _clientNameByKey(_clientKey(row)) || row.client_name || null;
  row.child_name = row.child_id ? _childName(row.child_id) : null;
  row.session_type_name = row.session_type_id ? _typeName(row.session_type_id) : null;
  const arr = (CS.billing.client_overrides || []).slice();
  const i = arr.findIndex(x => x.id === row.id);
  if (i > -1) arr[i] = row; else arr.push(row);
  CS.billing.client_overrides = arr;
}
function _removeOverride(id) { CS.billing.client_overrides = (CS.billing.client_overrides || []).filter(x => x.id !== id); }

function _overrideModal(existing) {
  let key = existing ? _clientKey(existing) : _clientKey((CS.billing.clients || [])[0]);
  const noClients = !(CS.billing.clients || []).length && !existing;
  let pv = null;
  const pvArgs = () => {
    if (!key || (flavor === 'fixed' && !typeId)) return null;
    return Object.assign({ p_action: 'override', p_id: existing?.id || null,
      p_session_type_id: flavor === 'fixed' ? typeId : null,
      p_fixed_price: flavor === 'fixed' ? (Number(fixed) || 0) : null,
      p_percent_off: flavor === 'percent' ? (Number(pct) || 0) : null }, _clientArgs(key));
  };
  let flavor = existing ? (existing.session_type_id ? 'fixed' : 'percent') : 'fixed';
  let typeId = existing?.session_type_id || (CS.billing.session_types || []).filter(t => t.active)[0]?.id || null;
  let fixed = existing?.fixed_price != null ? String(existing.fixed_price) : '';
  let pct = existing?.percent_off != null ? String(existing.percent_off) : '';
  ckDialog(T('pay_ovr_modal_title'), (bd) => {
    bd.appendChild(el('div', { class: 'ck-mh' }, [T('pay_ovr_modal_sub')]));
    bd.appendChild(el('div', { class: 'ck-lbl' }, [T('pay_f_client')]));
    const csel = _clientPicker(key, existing, v => { key = v; pv.soon(); });
    if (!csel) { bd.appendChild(el('div', { class: 'ck-empty ck-f43-empty' }, [T('f43_no_clients')])); return; }
    bd.appendChild(csel);
    bd.appendChild(el('div', { class: 'ck-lbl' }, [T('pay_f_how')]));
    const cards = el('div', { class: 'ck-radios' });
    const fixedCard = el('div', { class: 'ck-radio' + (flavor === 'fixed' ? ' sel' : '') });
    fixedCard.appendChild(el('div', { class: 'ck-radio-t' }, [T('pay_ovr_fixed_t')]));
    fixedCard.appendChild(el('div', { class: 'ck-radio-d' }, [T('pay_ovr_fixed_d')]));
    const fixedRow = el('div', { class: 'ck-inline-row' });
    const tsel = el('select', { class: 'ck-inp sm' });
    (CS.billing.session_types || []).filter(t => t.active).forEach(t => { const o = el('option', { value: t.id }, [t.name]); if (t.id === typeId) o.selected = true; tsel.appendChild(o); });
    tsel.onchange = () => { typeId = tsel.value; pv.soon(); };
    const fi = el('input', { class: 'ck-inp sm', type: 'number', value: fixed, min: '0' }); fi.oninput = () => { fixed = fi.value; pv.soon(); };
    fixedRow.appendChild(tsel); fixedRow.appendChild(fi); fixedRow.appendChild(el('span', {}, ['₪']));
    fixedCard.appendChild(fixedRow);
    const pctCard = el('div', { class: 'ck-radio' + (flavor === 'percent' ? ' sel' : '') });
    pctCard.appendChild(el('div', { class: 'ck-radio-t' }, [T('pay_ovr_pct_t')]));
    pctCard.appendChild(el('div', { class: 'ck-radio-d' }, [T('pay_ovr_pct_d')]));
    const pctRow = el('div', { class: 'ck-inline-row' });
    const ppi = el('input', { class: 'ck-inp sm', type: 'number', value: pct, min: '0', max: '100' }); ppi.oninput = () => { pct = ppi.value; pv.soon(); };
    pctRow.appendChild(ppi); pctRow.appendChild(el('span', {}, [T('pay_f_pct_off')]));
    pctCard.appendChild(pctRow);
    fixedCard.onclick = () => { if (flavor !== 'fixed') { flavor = 'fixed'; pv.soon(); } fixedCard.classList.add('sel'); pctCard.classList.remove('sel'); };
    pctCard.onclick = () => { if (flavor !== 'percent') { flavor = 'percent'; pv.soon(); } pctCard.classList.add('sel'); fixedCard.classList.remove('sel'); };
    cards.appendChild(fixedCard); cards.appendChild(pctCard); bd.appendChild(cards);
    const box = el('div', { class: 'ck-rp' }); bd.appendChild(box);
    pv = _repricePreview(box, pvArgs);
    pv.now();
  }, [
    ...(existing ? [{ label: T('pay_delete'), cls: 'danger', onClick: (c) => {
      openConfirmDlg(T('pay_ovr_delete_confirm'), async () => {
        try { const { data, error } = await supa().rpc('clinic_delete_client_override', { p_clinic: _bclinic(), p_id: existing.id }); if (error) throw error; _removeOverride(existing.id); _savedToast('pay_toast_ovr_deleted', data?.reprice); c(); render(); }
        catch (e) { _billErr(e); }
      }, (bd) => { const box = el('div', { class: 'ck-rp' }); bd.appendChild(box);
        _repricePreview(box, () => ({ p_action: 'override_delete', p_id: existing.id, p_child_id: null, p_specialist_patient_id: null, p_session_type_id: null, p_fixed_price: null, p_percent_off: null })).now(); });
    } }] : []),
    { label: T('pay_cancel'), cls: 'ghost', onClick: c => c() },
    { label: T('pay_save'), cls: 'primary', disabled: noClients, onClick: async (c) => {
      if (noClients) return;
      if (!key) { toast(T('pay_pick_client'), 'error'); return; }
      if (flavor === 'fixed' && !typeId) { toast(T('pay_pick_type'), 'error'); return; }
      try {
        const { data, error } = await supa().rpc('clinic_upsert_client_override', Object.assign({
          p_clinic: _bclinic(), p_id: existing?.id || null,
          p_session_type_id: flavor === 'fixed' ? typeId : null,
          p_fixed_price: flavor === 'fixed' ? (Number(fixed) || 0) : null,
          p_percent_off: flavor === 'percent' ? (Number(pct) || 0) : null
        }, _clientArgs(key)));
        if (error) throw error;
        const { reprice, ...row } = data || {};
        _patchOverride(row); _savedToast('pay_toast_ovr_saved', reprice); c(); render();
      } catch (e) { console.error('❌ billing:', e); toast((e && e.code === '23505') ? T('pay_err_ovr_duplicate') : T('pay_toast_error'), 'error'); }
    } },
  ]);
}

// tiny confirm (reuses ckDialog) — cockpit has no standalone confirm helper
function openConfirmDlg(msg, onYes, buildBody) {   // buildBody (optional): extra content, e.g. the F-43 re-price preview
  ckDialog(msg, buildBody || (() => {}), [
    { label: T('pay_cancel'), cls: 'ghost', onClick: c => c() },
    { label: T('pay_delete'), cls: 'danger', onClick: (c) => { c(); onYes(); } },
  ]);
}

// ── Specialist pay ──
function renderBillingPay(body) {
  _crumb(body, T('pay_card_pay_t'));
  body.appendChild(el('div', { class: 'ck-hint' }, [T('pay_pay_hint')]));
  const members = CS.billing.members || [];
  if (!members.length) { body.appendChild(el('div', { class: 'ck-empty' }, [T('pay_empty_pay')])); return; }
  const ex = _exampleType();
  const tbl = el('table', { class: 'ck-tbl' });
  tbl.appendChild(el('thead', {}, [el('tr', {}, [el('th', {}, [T('pay_col_specialist')]), el('th', {}, [T('pay_col_agreement')]), el('th', { class: 'pay-num' }, [T('pay_col_example')]), el('th', {}, [''])])]));
  const tb = el('tbody');
  members.forEach(m => {
    const pay = (CS.billing.specialist_pay || []).find(p => p.user_id === m.user_id) || null;
    const tr = el('tr', {});
    const nm = el('td', {}); nm.appendChild(el('b', {}, [m.name || '—']));
    if (m.role === 'head') nm.appendChild(badge(T('ck_badge_head'), 'b-head'));
    tr.appendChild(nm);
    const ag = el('td', {});
    if (!pay) { ag.appendChild(el('span', { class: 'ck-pill p-red' }, [T('pay_not_set')])); }
    else {
      if (pay.model === 'percent') ag.appendChild(el('span', { class: 'ck-pill p-teal' }, [T('pay_pct', { n: pay.default_percent })]));
      else ag.appendChild(el('span', { class: 'ck-pill p-teal' }, [_money(pay.default_fixed)]));
      ag.appendChild(el('span', { class: 'ck-pill p-blue' }, [pay.vat_split === 'after' ? T('pay_after_vat') : T('pay_before_vat')]));
      (pay.overrides || []).forEach(o => ag.appendChild(el('span', { class: 'ck-pill p-amber' }, [_typeName(o.session_type_id) + ': ' + (o.model === 'percent' ? o.percent + '%' : _money(o.fixed))])));
    }
    tr.appendChild(ag);
    const exv = _payExample(pay, ex);
    tr.appendChild(el('td', { class: 'pay-num' }, [exv == null ? T('pay_none_dash') : H()._arrLabel('', '→', ' ' + _money2(exv))]));   // F-18: the example payout follows the reading direction
    const btn = el('button', { class: 'ck-btn ' + (pay ? 'ghost' : 'secondary') + ' xs' }, [pay ? T('pay_edit') : T('pay_set_up')]);
    btn.onclick = () => _payModal(m, pay);
    tr.appendChild(el('td', {}, [btn]));
    tb.appendChild(tr);
  });
  tbl.appendChild(tb); body.appendChild(tbl);
  body.appendChild(el('div', { class: 'ck-foot-note' }, [T('pay_pay_footnote')]));
}

function _patchPay(row) {
  const arr = (CS.billing.specialist_pay || []).slice();
  const i = arr.findIndex(x => x.user_id === row.user_id);
  if (i > -1) { row.overrides = arr[i].overrides || []; arr[i] = row; } else { row.overrides = []; arr.push(row); }
  CS.billing.specialist_pay = arr;
}
function _patchPayOverride(userId, row) {
  const arr = (CS.billing.specialist_pay || []).slice();
  const p = arr.find(x => x.user_id === userId); if (!p) return;
  const ov = (p.overrides || []).slice(); const i = ov.findIndex(x => x.session_type_id === row.session_type_id);
  row.session_type_name = _typeName(row.session_type_id);
  if (i > -1) ov[i] = row; else ov.push(row); p.overrides = ov;
  CS.billing.specialist_pay = arr;
}
function _removePayOverride(userId, id) {
  const p = (CS.billing.specialist_pay || []).find(x => x.user_id === userId); if (!p) return;
  p.overrides = (p.overrides || []).filter(o => o.id !== id);
}

function _payModal(member, pay, onSaved) {
  let model = pay?.model || 'percent';
  let pct = pay?.default_percent != null ? String(pay.default_percent) : '';
  let fixed = pay?.default_fixed != null ? String(pay.default_fixed) : '';
  let split = pay?.vat_split || 'after';
  const nm = member.name || '—';
  ckDialog(T('pay_pay_modal_title', { name: nm }), (bd) => {
    bd.appendChild(el('div', { class: 'ck-mh' }, [T('pay_pay_modal_sub')]));
    // Q1 — how paid
    bd.appendChild(el('div', { class: 'ck-lbl' }, [T('pay_q1', { name: nm })]));
    const q1 = el('div', { class: 'ck-radios' });
    const pctCard = el('div', { class: 'ck-radio' + (model === 'percent' ? ' sel' : '') });
    pctCard.appendChild(el('div', { class: 'ck-radio-t' }, [T('pay_q1_pct_t')]));
    const pctRow = el('div', { class: 'ck-inline-row' }); const ppi = el('input', { class: 'ck-inp sm', type: 'number', value: pct, min: '0', max: '100' }); ppi.oninput = () => { pct = ppi.value; _recalc(); }; pctRow.appendChild(ppi); pctRow.appendChild(el('span', {}, ['%'])); pctCard.appendChild(pctRow);
    const fxCard = el('div', { class: 'ck-radio' + (model === 'fixed' ? ' sel' : '') });
    fxCard.appendChild(el('div', { class: 'ck-radio-t' }, [T('pay_q1_fixed_t')]));
    fxCard.appendChild(el('div', { class: 'ck-radio-d' }, [T('pay_q1_fixed_d')]));
    const fxRow = el('div', { class: 'ck-inline-row' }); const fxi = el('input', { class: 'ck-inp sm', type: 'number', value: fixed, min: '0' }); fxi.oninput = () => { fixed = fxi.value; _recalc(); }; fxRow.appendChild(fxi); fxRow.appendChild(el('span', {}, ['₪'])); fxCard.appendChild(fxRow);
    pctCard.onclick = (e) => { if (e.target === ppi) return; model = 'percent'; pctCard.classList.add('sel'); fxCard.classList.remove('sel'); _recalc(); };
    fxCard.onclick = (e) => { if (e.target === fxi) return; model = 'fixed'; fxCard.classList.add('sel'); pctCard.classList.remove('sel'); _recalc(); };
    q1.appendChild(pctCard); q1.appendChild(fxCard); bd.appendChild(q1);
    // Q2 — when split
    bd.appendChild(el('div', { class: 'ck-lbl' }, [T('pay_q2')]));
    const q2 = el('div', { class: 'ck-radios' });
    const aCard = el('div', { class: 'ck-radio' + (split === 'after' ? ' sel' : '') });
    aCard.appendChild(el('div', { class: 'ck-radio-t' }, [T('pay_q2_after_t')])); aCard.appendChild(el('div', { class: 'ck-radio-d' }, [T('pay_q2_after_d')]));
    const bCard = el('div', { class: 'ck-radio' + (split === 'before' ? ' sel' : '') });
    bCard.appendChild(el('div', { class: 'ck-radio-t' }, [T('pay_q2_before_t')])); bCard.appendChild(el('div', { class: 'ck-radio-d' }, [T('pay_q2_before_d')]));
    aCard.onclick = () => { split = 'after'; aCard.classList.add('sel'); bCard.classList.remove('sel'); _recalc(); };
    bCard.onclick = () => { split = 'before'; bCard.classList.add('sel'); aCard.classList.remove('sel'); _recalc(); };
    q2.appendChild(aCard); q2.appendChild(bCard); bd.appendChild(q2);
    // live example
    const calc = el('div', { class: 'ck-calc' }); bd.appendChild(calc);
    function _recalc() {
      const ex = _exampleType(); calc.replaceChildren();
      if (!ex) { calc.appendChild(el('div', { class: 'ck-calc-row' }, [T('pay_calc_example', { name: T('pay_dur_any') })])); return; }
      const price = Number(ex.price) || 0, vat = _vat();
      calc.appendChild(_calcRow(T('pay_calc_example', { name: ex.name }), _money2(price)));
      // an untouched input must not read as a real calc of zero → dash placeholder until a value is entered
      const raw = model === 'fixed' ? fixed : pct;
      const hasVal = raw !== '' && raw != null && !isNaN(Number(raw));
      const dash = T('pay_none_dash');
      let totalLbl, totalVal;
      if (model === 'fixed') {
        totalLbl = T('pay_calc_gets_fixed', { name: nm });
        totalVal = hasVal ? _money2(Number(fixed) || 0) : dash;
      } else {
        const base = split === 'after' ? price / (1 + vat) : price;
        if (split === 'after') calc.appendChild(_calcRow(T('pay_calc_aftervat', { v: Math.round(vat * 100) }), _money2(base)));
        totalLbl = T('pay_calc_gets', { name: nm, pct: hasVal ? (Number(pct) || 0) : dash });
        totalVal = hasVal ? _money2((Number(pct) || 0) / 100 * base) : dash;
      }
      const tot = _calcRow(totalLbl, totalVal); tot.classList.add('total'); calc.appendChild(tot);
    }
    _recalc();
    // per-type exceptions (progressive disclosure)
    const excWrap = el('div', { class: 'ck-exc' });
    const toggle = el('a', { class: 'ck-link' }, [T('pay_diff_deal')]);
    toggle.onclick = () => { excWrap.hidden = !excWrap.hidden; };
    excWrap.hidden = !(pay && (pay.overrides || []).length);
    bd.appendChild(toggle); bd.appendChild(excWrap);
    _renderExc();
    function _renderExc() {
      excWrap.replaceChildren();
      const cur = (CS.billing.specialist_pay || []).find(p => p.user_id === member.user_id);
      const list = cur?.overrides || [];
      if (!list.length) excWrap.appendChild(el('div', { class: 'ck-exc-empty' }, [T('pay_exc_none')]));
      list.forEach(o => {
        const r = el('div', { class: 'ck-exc-row' });
        r.appendChild(el('span', {}, [_typeName(o.session_type_id) + ' · ' + (o.model === 'percent' ? o.percent + '%' : _money(o.fixed))]));
        const x = el('button', { class: 'ck-btn danger xs' }, ['×']);
        x.onclick = async () => { try { const { error } = await supa().rpc('clinic_delete_pay_override', { p_clinic: _bclinic(), p_id: o.id }); if (error) throw error; _removePayOverride(member.user_id, o.id); toast(T('pay_toast_exc_removed'), 'success'); _renderExc(); render(); } catch (e) { _billErr(e); } };
        r.appendChild(x); excWrap.appendChild(r);
      });
      // add-exception inline (needs a saved default pay row first)
      const cur2 = (CS.billing.specialist_pay || []).find(p => p.user_id === member.user_id);
      if (cur2) {
        let etype = (CS.billing.session_types || []).filter(t => t.active)[0]?.id || null;
        let emodel = 'percent'; let eval1 = '';
        const addRow = el('div', { class: 'ck-exc-add' });
        const es = el('select', { class: 'ck-inp sm' }); (CS.billing.session_types || []).filter(t => t.active).forEach(t => { const o = el('option', { value: t.id }, [t.name]); es.appendChild(o); }); es.onchange = () => etype = es.value;
        const ems = el('select', { class: 'ck-inp sm' }); ems.appendChild(el('option', { value: 'percent' }, ['%'])); ems.appendChild(el('option', { value: 'fixed' }, ['₪'])); ems.onchange = () => emodel = ems.value;
        const evi = el('input', { class: 'ck-inp sm', type: 'number', min: '0' }); evi.oninput = () => eval1 = evi.value;
        const ab = el('button', { class: 'ck-btn secondary xs' }, [T('pay_exc_add')]);
        ab.onclick = async () => {
          if (!etype || eval1 === '') return;
          try { const { data, error } = await supa().rpc('clinic_set_pay_override', { p_clinic: _bclinic(), p_user_id: member.user_id, p_session_type_id: etype, p_model: emodel, p_percent: emodel === 'percent' ? Number(eval1) : null, p_fixed: emodel === 'fixed' ? Number(eval1) : null }); if (error) throw error; _patchPayOverride(member.user_id, data); toast(T('pay_toast_exc_saved'), 'success'); _renderExc(); render(); }
          catch (e) { _billErr(e); }
        };
        addRow.appendChild(es); addRow.appendChild(ems); addRow.appendChild(evi); addRow.appendChild(ab);
        excWrap.appendChild(addRow);
      }
    }
  }, [
    { label: T('pay_cancel'), cls: 'ghost', onClick: c => c() },
    { label: T('pay_save'), cls: 'primary', onClick: async (c) => {
      // Full payload: send the default fields for the chosen model.
      try {
        const { data, error } = await supa().rpc('clinic_set_specialist_pay', {
          p_clinic: _bclinic(), p_user_id: member.user_id, p_model: model,
          p_default_percent: model === 'percent' ? (Number(pct) || 0) : null,
          p_default_fixed: model === 'fixed' ? (Number(fixed) || 0) : null,
          p_vat_split: split
        });
        if (error) throw error; _patchPay(data); toast(T('pay_toast_pay_saved'), 'success'); c(); if (onSaved) onSaved(); else render();
      } catch (e) { _billErr(e); }
    } },
  ]);
}

// ── Clinic billing settings (modal from the hub) ──
function _settingsModal() {
  const bs = CS.billing.billing_settings || {};
  let vatPct = String(Math.round(Number(bs.vat_rate ?? 0.18) * 100));   // UI shows percent; DB stores fraction
  let review = !!bs.review_toggle;
  let start = bs.billing_start_month ? String(bs.billing_start_month).slice(0, 7) : '';
  let payLang = bs.pay_email_lang || 'he';   // SC-B6d clinic default (below the recipient's own preference)
  const _init = { vatPct, review, payLang };   // C-1 QA-02: what the modal opened with — only changed fields are sent
  ckDialog(T('pay_settings_modal_title'), (bd) => {
    bd.appendChild(el('div', { class: 'ck-mh' }, [T('pay_settings_modal_sub')]));
    bd.appendChild(el('div', { class: 'ck-lbl' }, [T('pay_f_vat')]));
    const vi = el('input', { class: 'ck-inp', type: 'number', value: vatPct, min: '0', max: '99' }); vi.oninput = () => vatPct = vi.value; bd.appendChild(vi);
    const rowR = el('div', { class: 'ck-set-toggle' });
    const tgl = el('span', { class: 'ck-toggle' + (review ? ' on' : '') }); tgl.onclick = () => { review = !review; tgl.classList.toggle('on', review); };
    const rl = el('div', {}); rl.appendChild(el('b', {}, [T('pay_f_review')])); rl.appendChild(el('div', { class: 'ck-set-d' }, [T('pay_f_review_d')]));
    rowR.appendChild(tgl); rowR.appendChild(rl); bd.appendChild(rowR);
    bd.appendChild(el('div', { class: 'ck-lbl' }, [T('pay_f_start')]));
    const mi = el('input', { class: 'ck-inp', type: 'month', value: start }); mi.oninput = () => start = mi.value; bd.appendChild(mi);
    bd.appendChild(el('div', { class: 'ck-set-d' }, [T('pay_start_hint')]));
    // SC-B6d: clinic pay-email language default
    bd.appendChild(el('div', { class: 'ck-lbl' }, [T('pay_f_pay_email_lang')]));
    const li = el('select', { class: 'ck-inp' });
    [['he', T('pay_lang_he')], ['en', T('pay_lang_en')]].forEach(([v, lbl]) => { const o = el('option', { value: v }, [lbl]); if (v === payLang) o.selected = true; li.appendChild(o); });
    li.onchange = () => payLang = li.value;
    bd.appendChild(li);
    bd.appendChild(el('div', { class: 'ck-set-d' }, [T('pay_pay_email_lang_hint')]));
  }, [
    { label: T('pay_cancel'), cls: 'ghost', onClick: c => c() },
    { label: T('pay_save'), cls: 'primary', onClick: async (c) => {
      // C-1 QA-02: send only the fields that changed. vat_rate / review_toggle / pay_email_lang: NULL = keep the stored
      // value (the RPC COALESCEs them), so an untouched field can never overwrite the stored one. billing_start_month
      // has no keep-value (the RPC sets it as given, null = clear), so it always goes as the modal shows it — unchanged
      // unless edited. Nothing changed → no write at all.
      const _vatChanged = (Number(vatPct) || 0) !== (Number(_init.vatPct) || 0);
      const _startOrig = bs.billing_start_month ? String(bs.billing_start_month).slice(0, 7) : '';
      if (!_vatChanged && review === _init.review && payLang === _init.payLang && start === _startOrig) { c(); return; }
      try {
        const { data, error } = await supa().rpc('clinic_set_billing_settings', {
          p_clinic: _bclinic(), p_vat_rate: _vatChanged ? (Number(vatPct) || 0) / 100 : null,
          p_review_toggle: review !== _init.review ? review : null, p_billing_start_month: start ? (start + '-01') : null,
          p_pay_email_lang: payLang !== _init.payLang ? payLang : null
        });
        if (error) throw error; CS.billing.billing_settings = data; toast(T('pay_toast_settings_saved'), 'success'); c(); render();
      } catch (e) { _billErr(e); }
    } },
  ]);
}
function _calcRow(l, v) { const r = el('div', { class: 'ck-calc-row' }); r.appendChild(el('span', {}, [l])); r.appendChild(el('span', { class: 'pay-num' }, [v])); return r; }

// ══ styles ══════════════════════════════════════════════════════════════════
function injectStyles() {
  if (document.getElementById('ck-styles')) return;
  const s = document.createElement('style'); s.id = 'ck-styles';
  s.textContent = `
  .ck-overlay{position:fixed;inset:0;z-index:1400;background:rgba(15,42,38,.5);display:flex;justify-content:stretch;align-items:stretch;padding:24px;overflow:hidden}
  .ck-shell{background:var(--mint,#f0fdf9);flex:1;min-width:0;display:flex;flex-direction:column;border-radius:20px;box-shadow:0 20px 60px rgba(0,0,0,.3);overflow:hidden;font-family:'Nunito',sans-serif}
  .ck-head{display:flex;flex-shrink:0;align-items:center;gap:12px;padding:16px 18px;background:linear-gradient(135deg,#0f766e,#0d9488);color:#fff}
  .ck-back{background:rgba(255,255,255,.18);border:none;color:#fff;width:34px;height:34px;border-radius:10px;font-size:1.1rem;cursor:pointer;flex-shrink:0}
  .ck-title{flex:1;font-family:'Fraunces',serif;font-weight:600;font-size:1.15rem;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .ck-exit{background:rgba(255,255,255,.18);border:none;color:#fff;width:34px;height:34px;border-radius:10px;font-size:1.1rem;cursor:pointer;flex-shrink:0;display:inline-flex;align-items:center;justify-content:center}
  .ck-exit:hover{background:rgba(255,255,255,.28)}
  /* C-5 C1 — the admin's account button + menu (mock C-4-admin-header): same 34px footprint as the ✕ */
  .ck-admin .ck-head{position:relative;z-index:5}
  /* C5-P1: an admin-only account has nothing behind the cockpit, so it fills the window: no backdrop, no card frame */
  .ck-overlay.ck-admin{padding:0;background:var(--mint,#f0fdf9)}
  .ck-admin .ck-shell{border-radius:0;box-shadow:none}
  .ck-acct{display:inline-flex;align-items:center;gap:6px;background:rgba(255,255,255,.18);border:none;color:#fff;height:34px;border-radius:10px;padding-block:0;padding-inline:4px 10px;font:800 13px 'Nunito',sans-serif;flex-shrink:0;cursor:pointer;max-width:40%}
  .ck-acct:hover,.ck-acct[aria-expanded=true]{background:rgba(255,255,255,.32)}
  .ck-acct-av{width:26px;height:26px;border-radius:8px;background:#fff;color:var(--teal-d,#0d6b63);display:inline-flex;align-items:center;justify-content:center;font:600 13px 'Fraunces',serif;flex-shrink:0}
  .ck-acct-nm{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .ck-acct-car{font-size:11px;opacity:.85}
  .ck-menu{position:absolute;top:calc(100% - 8px);inset-inline-end:14px;width:240px;max-width:calc(100% - 20px);background:#fff;border:1px solid var(--mint-l,#ccfbf1);border-radius:12px;box-shadow:0 8px 28px rgba(0,0,0,.18);padding:6px;color:var(--navy,#1a2e2b);font-family:'Nunito',sans-serif}
  .ck-menu-who{padding:8px 10px 10px;border-bottom:1px solid var(--mint-ll,#e8f4f2);margin-bottom:4px}
  .ck-menu-nm{font-weight:800;font-size:14px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .ck-menu-mail{font-size:12px;color:var(--slate,#64748b);direction:ltr;unicode-bidi:isolate;text-align:start;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .ck-menu-role{display:inline-block;margin-top:5px;font-size:11px;font-weight:900;letter-spacing:.03em;padding:2px 8px;border-radius:99px;background:#e0f2fe;color:#0369a1}
  .ck-mi{display:flex;align-items:center;gap:10px;width:100%;box-sizing:border-box;border:none;background:none;padding:10px;border-radius:8px;font:700 14px 'Nunito',sans-serif;color:var(--navy,#1a2e2b);text-align:start;cursor:pointer}
  .ck-mi:hover{background:var(--mint-ll,#e8f4f2)}
  .ck-mi.sub{padding-inline-start:38px;font-weight:600}
  .ck-mi-ic{width:18px;text-align:center;flex-shrink:0}
  .ck-mi-val{margin-inline-start:auto;font-size:12px;color:var(--slate,#64748b);font-weight:700}
  .ck-mi.out{color:#b91c1c}
  .ck-menu-sep{height:1px;background:var(--mint-ll,#e8f4f2);margin:4px 0}
  .ck-noacc{min-height:100%;display:flex;align-items:center;justify-content:center;padding:24px 0;box-sizing:border-box}
  .ck-noacc-card{background:#fff;border:1px solid var(--mint-l,#ccfbf1);border-radius:16px;padding:28px 26px;max-width:420px;text-align:center;box-shadow:0 1px 3px rgba(13,148,136,.08)}
  .ck-noacc-ic{font-size:30px;margin-bottom:8px}
  .ck-noacc-t{font-family:'Fraunces',serif;font-weight:600;font-size:20px;margin-bottom:8px;color:var(--navy,#1a2e2b)}
  [dir=rtl] .ck-noacc-t{font-family:'Heebo',sans-serif;font-weight:700}
  .ck-noacc-d{font-size:14px;color:var(--slate,#64748b);line-height:1.5;margin-bottom:18px}
  .ck-noacc-del{display:block;margin:12px auto 0;background:transparent;color:#b91c1c;border:1px solid #fca5a5}   /* F-41: a removed admin deletes the account */
  .ck-join-card .ck-noacc-t{margin-bottom:18px}
  .ck-join-acts{display:flex;gap:10px;justify-content:center;flex-wrap:wrap}
  .ck-nocaps{background:#fff;border:1px dashed var(--mint-l,#ccfbf1);border-radius:12px;padding:14px 16px;margin:4px 0 14px;font-size:14px;color:var(--slate,#64748b);line-height:1.5}
  .ck-logpill{background:rgba(255,255,255,.2);color:#fff;border-radius:99px;padding:3px 10px;font-size:11px;font-weight:800;flex-shrink:0}
  .ck-body{padding:16px 18px 24px;flex:1;min-height:0;overflow:auto}
  .ck-loading{text-align:center;color:var(--slate,#64748b);padding:30px;font-size:1.4rem} /* Arc A2: no longer used — the frame shows a skeleton, never '…' */
  /* Arc A2 — body crossfade, played ONLY on a view change (same-view reloads never fade). Mirrors the
     app's hudFadeIn spec (opacity + 4px rise, .15s ease-out); that app keyframe is referenced but never
     defined, so this scoped one stands alone. */
  @keyframes ckBodyFade{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}
  .ck-body.ck-fade{animation:ckBodyFade .15s ease-out}
  /* Arc A2 — cross-view / first-load skeleton (never blank the frame). */
  .ck-skel{padding:2px 0}
  .ck-skel-row{height:56px;border-radius:12px;margin-bottom:10px;background:linear-gradient(90deg,#eef5f4 25%,#e3efed 37%,#eef5f4 63%);background-size:400% 100%;animation:ckSkel 1.2s ease-in-out infinite}
  @keyframes ckSkel{0%{background-position:100% 50%}100%{background-position:0 50%}}
  @media (prefers-reduced-motion:reduce){ .ck-body.ck-fade{animation:none} .ck-skel-row{animation:none} }
  .ck-err{background:#fef2f2;color:#b91c1c;border:1px solid #fecaca;border-radius:10px;padding:11px 14px;font-size:.84rem;margin-bottom:12px}
  .ck-empty{color:var(--slate-l,#94a3b8);font-size:.84rem;font-style:italic;padding:8px 2px}
  .ck-roll{display:flex;gap:10px;flex-wrap:wrap;margin-bottom:14px}
  .ck-box{flex:1;min-width:90px;background:#fff;border:1px solid var(--mint-l,#ccfbf1);border-radius:12px;padding:12px;text-align:center}
  .ck-box-n{font-family:'Fraunces',serif;font-weight:600;font-size:1.5rem;color:var(--teal-d,#0d6b63)}
  .ck-box-l{font-size:11px;color:var(--slate,#64748b);font-weight:700;text-transform:uppercase;letter-spacing:.03em}
  .ck-row{display:flex;align-items:center;gap:12px;padding:11px 12px;background:#fff;border:1px solid #e6efec;border-radius:12px;margin-bottom:8px}
  .ck-row-click{cursor:pointer;transition:transform .1s,box-shadow .1s}
  .ck-row-click:hover{transform:translateY(-1px);box-shadow:0 3px 10px rgba(0,0,0,.06)}
  .ck-av{width:38px;height:38px;border-radius:11px;background:var(--mint-l,#ccfbf1);color:var(--teal-d,#0d6b63);display:flex;align-items:center;justify-content:center;font-family:'Fraunces',serif;font-weight:600;font-size:1.05rem;flex-shrink:0}
  .ck-av.sm{font-size:1rem;font-family:'Nunito'}
  .ck-grow{flex:1;min-width:0}
  .ck-nm{font-weight:800;font-size:.92rem;color:var(--navy,#1a2e2b);display:flex;align-items:center;gap:6px;flex-wrap:wrap}
  .ck-sub{font-size:.74rem;color:var(--slate,#64748b);margin-top:2px}
  .ck-counts{display:flex;gap:14px;flex-shrink:0}
  .ck-stat{text-align:center}.ck-stat-n{font-weight:900;font-size:1.05rem;color:var(--teal-d,#0d6b63)}.ck-stat-l{font-size:11px;text-transform:uppercase;color:var(--slate-l,#94a3b8);font-weight:800}
  .ck-chev{color:var(--slate-l,#94a3b8);font-size:1.2rem;flex-shrink:0}
  .ck-section{font-weight:800;font-size:.72rem;text-transform:uppercase;letter-spacing:.05em;color:var(--slate,#64748b);margin:16px 0 8px}
  .ck-meta{font-size:.8rem;color:var(--slate,#64748b);margin-bottom:12px;display:flex;align-items:center;gap:8px;flex-wrap:wrap}
  .ck-content{background:#fff;border:1px solid #e6efec;border-radius:12px;padding:18px 22px;font-size:.92rem;color:var(--navy,#1a2e2b);line-height:1.7;max-width:68ch;margin-inline:auto}
  .ck-content h1,.ck-content h2,.ck-content h3{font-family:'Fraunces',serif;font-weight:600;line-height:1.3;margin:14px 0 6px}
  .ck-content h1{font-size:1.25rem}.ck-content h2{font-size:1.1rem}.ck-content h3{font-size:1rem}
  .ck-content ul,.ck-content ol{margin:0 0 10px;padding-inline-start:22px}.ck-content li{margin:2px 0}
  .ck-content strong{font-weight:800}.ck-content em{font-style:italic}
  .ck-content blockquote{border-inline-start:3px solid var(--mint-l,#ccfbf1);margin:8px 0;padding-inline-start:12px;color:var(--slate,#64748b)}
  .ck-layer-lbl{font-weight:800;font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:var(--slate,#64748b);margin:16px 0 6px;display:flex;align-items:center;gap:6px}
  .ck-layer-lbl.shared{color:#0369a1}
  .ck-shared{background:#f0f9ff;border-color:#bae6fd}
  .ck-muted{color:var(--slate-l,#94a3b8);font-style:italic}
  .ck-sumbadge{display:inline-block;font-size:11px;font-weight:900;text-transform:uppercase;letter-spacing:.03em;padding:1px 6px;border-radius:99px;background:#e0f2fe;color:#0369a1;margin-inline-end:4px}
  .ck-content p{margin:0 0 10px}
  .ck-badge{display:inline-flex;align-items:center;font-size:11px;font-weight:900;text-transform:uppercase;letter-spacing:.03em;padding:2px 8px;border-radius:99px}
  .ck-badge.p-conf{background:#ccfbf1;color:#0d6b63}
  .ck-badge.p-wait{background:#fef3c7;color:#92400e}
  .ck-badge.p-fin{background:#e2e8f0;color:#334155}
  .b-head{background:#ede9fe;color:#7c3aed}.b-full{background:#dcfce7;color:#166534}.b-clinical{background:#e0f2fe;color:#0369a1}.b-schedule{background:#f1f5f9;color:#475569}
  .b-draft{background:#fef3c7;color:#92400e}.b-pub{background:#dcfce7;color:#166534}.b-owner{background:#fef9c3;color:#854d0e}
  /* B-status: payment badge colours + dot */
  .p-aw{background:#fef3c7;color:#92400e}.p-fu{background:#e0f2fe;color:#075985}.p-paid{background:#dcfce7;color:#166534}.p-wv{background:#e0e7ff;color:#3730a3}.p-nb{background:#f1f5f9;color:#64748b}
  .ck-ev-pdot{display:inline-block;width:7px;height:7px;border-radius:50%;margin-inline-start:5px;vertical-align:middle}
  .ck-dlg-sep{height:1px;background:var(--mint-ll,#e8f4f2);margin:14px 0 12px}
  .ck-dlg-subh{font-size:.72rem;font-weight:800;text-transform:uppercase;letter-spacing:.04em;color:var(--slate,#64748b);margin-bottom:10px}
  .ck-chips{display:flex;gap:5px;flex-wrap:wrap;margin-top:4px}
  /* Payments overview card */
  .ck-pay-card .ck-av{background:#fef3c7;color:#92400e}
  /* Payments queue */
  .ck-pay-tabs{margin-bottom:12px}
  .ck-pay-month{margin-bottom:12px}
  .ck-payrow{display:flex;align-items:center;gap:11px;padding:10px 12px;border:1px solid #e6efec;border-radius:12px;margin-bottom:8px;background:#fff}
  .ck-payrow.fu{border-inline-start:4px solid #0284c7;background:#f6fbfe}
  .ck-payrow.proc{background:#fbfefd}
  /* F-91: list search + filter */
  .ck-flt{background:#f0fdf9;border:1px solid #bfe9df;border-radius:10px;padding:9px 10px;margin-bottom:8px}
  .ck-flt-r{display:flex;gap:8px;align-items:center}
  .ck-flt .ck-flt-q{margin-bottom:0;flex:1;min-width:0}
  .ck-flt-more{display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-top:8px}
  .ck-flt-l{font-size:11px;font-weight:800;color:var(--slate,#64748b);display:flex;align-items:center;gap:5px}
  .ck-flt .ck-flt-d,.ck-flt .ck-flt-m{width:auto;margin-bottom:0;padding:6px 8px;font-size:.8rem}
  .ck-flt-chips{display:flex;flex-wrap:wrap;gap:6px;flex-basis:100%}
  .ck-flt-chip{border:1px solid var(--mint-l,#ccfbf1);background:#fff;border-radius:99px;padding:3px 10px;font-size:.74rem;cursor:pointer}
  .ck-flt-chip.on{background:#ccfbf1;border-color:#0d9488;color:#0d6b63;font-weight:800}
  .ck-flt-chip:focus-visible,.ck-flt-clr:focus-visible{outline:2px solid #1a2e2b;outline-offset:1px}
  .ck-flt-foot{display:flex;justify-content:space-between;align-items:center;gap:8px;margin-top:8px;font-size:.74rem;color:var(--slate,#64748b)}
  .ck-flt-clr{color:#0d6b63;font-weight:800;cursor:pointer;text-decoration:underline}
  .ck-flt-sort{font-size:.72rem;color:var(--slate,#64748b);margin:2px 2px 8px}
  .ck-age{flex-shrink:0;width:46px;text-align:center;border-radius:9px;padding:6px 2px;line-height:1.05}
  .ck-age b{font-size:.95rem;font-weight:900}.ck-age-u{display:block;font-size:11px;font-weight:800;text-transform:uppercase;opacity:.75}
  .ck-age.fresh{background:#ecfdf5;color:#047857}.ck-age.warn{background:#fffbeb;color:#b45309}.ck-age.stale{background:#fef2f2;color:#be123c}
  .ck-payrow-note{font-size:.72rem;color:var(--slate,#64748b);margin-top:4px;font-style:italic}
  .ck-payrow-who{flex-shrink:0;font-size:11px;color:var(--slate-l,#94a3b8);text-align:end;max-width:130px}
  .ck-payrow-who bdi{display:block}.ck-payrow-who .ck-dt{white-space:nowrap} /* C-7: name line, then the date-time as one unbroken unit */
  /* payments queue: session-type chip + amount-to-collect block (Option B) */
  .ck-badge.ck-type{background:#f0fdf9;color:#0d6b63;text-transform:none;font-weight:800;letter-spacing:0}
  .ck-badge.ck-unclass{background:#fef3c7;color:#92400e;text-transform:none;font-weight:800;letter-spacing:0;cursor:pointer}
  .ck-amt{flex-shrink:0;text-align:end;min-width:60px;font-weight:900;font-size:.98rem;color:#0d6b63;direction:ltr;unicode-bidi:isolate;font-variant-numeric:tabular-nums;line-height:1.12}
  .ck-amt .ck-amt-tag{display:block;font-size:11px;font-weight:800;color:#9a3412;text-transform:none;letter-spacing:0;direction:ltr;unicode-bidi:isolate}
  .ck-amt.unset{color:#b45309;font-weight:800;font-size:.7rem;direction:inherit;unicode-bidi:normal}
  .ck-paytotal{display:flex;align-items:baseline;gap:9px;flex-wrap:wrap;padding:9px 13px;margin-bottom:11px;background:#f0fdf9;border:1px solid #cbe9e2;border-radius:11px}
  .ck-paytotal .ck-pt-amt{font-weight:900;font-size:1.05rem;color:#0d6b63;direction:ltr;unicode-bidi:isolate;font-variant-numeric:tabular-nums}
  .ck-paytotal .ck-pt-lbl{font-size:.8rem;font-weight:700;color:#334155}
  .ck-paytotal .ck-pt-un{font-size:.72rem;font-weight:800;color:#92400e}
  .ck-actbar{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:12px}
  .ck-btn{border:none;border-radius:10px;padding:9px 15px;font-family:'Nunito',sans-serif;font-weight:800;font-size:.82rem;cursor:pointer}
  .ck-btn.sm{padding:7px 12px;font-size:.78rem}.ck-btn.xs{padding:5px 9px;font-size:.72rem}
  .ck-btn.primary{background:var(--teal,#0d9488);color:#fff}
  .ck-btn.ghost{background:#fff;color:var(--teal-d,#0d6b63);border:1.5px solid var(--mint-l,#ccfbf1)}
  .ck-btn.danger{background:#fff;color:#b91c1c;border:1.5px solid #fecaca}
  .ck-mrow{background:#fff;border:1px solid #e6efec;border-radius:12px;padding:12px;margin-bottom:8px}
  .ck-mrow-top{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:8px}
  .ck-mrow-top .ck-nm{flex:1}
  /* C-5 C2: clinic admins in Manage members */
  .ck-admin-invite-line{display:flex;gap:8px;margin-top:8px;flex-wrap:wrap}
  .ck-admin-invite-line .ck-input{flex:1 1 220px;min-width:0;border:1.5px solid var(--mint-l,#ccfbf1);border-radius:10px;padding:8px 10px;font:inherit;font-size:14px;text-align:start}
  .ck-admin-mail{direction:ltr;unicode-bidi:isolate;text-align:start;margin:-4px 0 8px}
  .ck-caps{display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:6px}
  .ck-cap{display:flex;align-items:flex-start;gap:10px;background:none;border:1px solid #eef4f3;border-radius:10px;padding:8px 10px;text-align:start;cursor:pointer;font:inherit;color:var(--navy,#1a2e2b)}
  .ck-cap-tog{flex-shrink:0;width:34px;height:20px;border-radius:99px;background:#cbd5e1;position:relative;margin-top:1px;transition:background .15s}
  .ck-cap-knob{position:absolute;top:2px;inset-inline-start:2px;width:16px;height:16px;border-radius:50%;background:#fff;box-shadow:0 1px 2px rgba(0,0,0,.2);transition:inset-inline-start .15s}
  .ck-cap.on .ck-cap-tog{background:var(--teal,#0d9488)}
  .ck-cap.on .ck-cap-knob{inset-inline-start:16px}
  .ck-cap-txt{display:flex;flex-direction:column;min-width:0}
  .ck-cap-l{font-weight:800;font-size:13px}
  .ck-cap-d{font-size:12px;color:var(--slate,#64748b);line-height:1.4}
  .ck-seg{display:inline-flex;gap:3px;background:var(--mint-ll,#e8f4f2);border-radius:10px;padding:3px}
  .ck-seg span{padding:5px 12px;border-radius:8px;font-weight:800;font-size:.74rem;color:var(--slate,#64748b);cursor:pointer}
  .ck-seg span.on{background:#fff;color:var(--teal-d,#0d6b63)}
  .ck-dlg-back{position:fixed;inset:0;z-index:1600;background:rgba(15,42,38,.45);display:flex;align-items:center;justify-content:center;padding:20px}
  .ck-overlay ~ .overlay{z-index:1700}   /* F-41: an app confirm opened from the cockpit (the removed admin's delete account) must sit above the cockpit (1400) and its dialogs (1600) */
  .ck-dlg{background:#fff;border-radius:16px;width:100%;max-width:420px;box-shadow:0 20px 60px rgba(0,0,0,.3);overflow:hidden}
  .ck-dlg-head{padding:14px 18px;font-family:'Fraunces',serif;font-weight:600;font-size:1.05rem;color:var(--navy,#1a2e2b);border-bottom:1px solid var(--mint-ll,#e8f4f2)}
  .ck-dlg-body{padding:16px 18px}
  .ck-dlg-foot{padding:12px 18px;display:flex;justify-content:flex-end;gap:8px;border-top:1px solid var(--mint-ll,#e8f4f2)}
  .ck-inp{width:100%;border:1.5px solid var(--mint-l,#ccfbf1);border-radius:10px;padding:10px 12px;font-family:inherit;font-size:.92rem;margin-bottom:12px;box-sizing:border-box}
  .ck-lbl{font-weight:800;font-size:.74rem;color:var(--slate,#64748b);margin:0 0 6px;text-transform:uppercase;letter-spacing:.04em}
  .ck-pick{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px}
  .ck-chip{border:1.5px solid var(--mint-l,#ccfbf1);border-radius:10px;padding:8px 12px;font-size:.82rem;font-weight:800;cursor:pointer}
  .ck-chip.sel{border-color:var(--teal,#0d9488);background:var(--mint,#f0fdf9)}
  /* Patient Lifecycle L2 — Unassigned box + draft pile */
  .ck-btn:disabled{opacity:.5;cursor:not-allowed}
  .ck-box-tap{cursor:pointer;transition:transform .1s,box-shadow .12s,border-color .12s}
  .ck-box-tap:hover{transform:translateY(-2px);box-shadow:0 6px 16px rgba(13,148,136,.12);border-color:var(--teal,#0d9488)}
  .ck-box-live{border-color:#f59e0b;background:linear-gradient(180deg,#fffdf5,#fff)}
  .ck-box-live .ck-box-n{color:#92400e}
  .ck-box-live .ck-box-l{color:#92400e}
  .pl-draft{border:1px solid #eadfc4;background:linear-gradient(180deg,#fffdf6,#fff);border-radius:12px;padding:12px;margin-bottom:9px}
  .pl-draft.arch{opacity:.72;border-style:dashed}
  .pl-draft-top{display:flex;align-items:flex-start;gap:9px}
  .pl-draft-nm{font-weight:900;font-size:.92rem;color:var(--navy,#1a2e2b);flex:1}
  .pl-days{background:#fef3c7;color:#92400e;border-radius:99px;font-weight:900;font-size:11px;padding:3px 8px;flex-shrink:0;white-space:nowrap}
  .pl-days.warn{background:#fee2e2;color:#b91c1c}
  .pl-arch-tag{background:#f1f5f9;color:#475569;border-radius:99px;font-weight:900;font-size:11px;padding:2px 8px;text-transform:uppercase;flex-shrink:0}
  .pl-hint{font-size:.72rem;color:var(--slate,#64748b);margin-top:5px}
  .pl-note{font-size:.74rem;color:var(--slate,#64748b);background:var(--mint-ll,#e8f4f2);border-radius:8px;padding:7px 9px;margin-top:7px;font-style:italic;line-height:1.4}
  .pl-acts{display:flex;gap:6px;margin-top:9px;flex-wrap:wrap}
  .pl-toggle-row{border-top:1px dashed #dfe8e5;margin-top:12px;padding-top:6px}
  .pl-toggle{background:none;border:none;color:var(--teal-d,#0d6b63);font-weight:800;font-size:.76rem;cursor:pointer;font-family:inherit;padding:6px 0}
  .pl-err{color:#b91c1c;font-size:.76rem;margin:-4px 0 10px}
  .pl-carry{border:1px dashed var(--mint-l,#ccfbf1);background:var(--mint,#f0fdf9);border-radius:12px;padding:11px;margin:10px 0}
  .pl-carry-h{font-size:11px;font-weight:900;text-transform:uppercase;letter-spacing:.04em;color:var(--teal-d,#0d6b63);margin-bottom:7px}
  .pl-carry-line{font-size:.8rem;color:var(--navy,#1a2e2b);margin:3px 0;display:flex;gap:7px}
  .pl-carry-k{color:var(--slate-l,#94a3b8);font-weight:800;min-width:56px;font-size:11px;text-transform:uppercase;letter-spacing:.02em;padding-top:1px}
  /* L4b — every dialog/popup is a fixed-height panel with an internally-scrolling body (head+foot pinned) */
  .ck-dlg{max-height:85vh;display:flex;flex-direction:column}
  .ck-dlg-head,.ck-dlg-foot{flex-shrink:0}
  .ck-dlg-body{overflow-y:auto;min-height:0}
  .pl-scroll{max-height:52vh;overflow-y:auto;margin-top:2px}   /* Patients list scrolls under the pinned search+chips */
  .ck-note{font-size:.78rem;color:var(--slate,#64748b);line-height:1.5;margin-bottom:10px}
  .ck-transfer{display:grid;gap:8px}
  .ck-t-yes{background:#dcfce7;border:1px solid #86efac;border-radius:10px;padding:9px 12px;font-size:.78rem;color:#166534}
  .ck-t-no{background:#f1f5f9;border:1px solid #e2e8f0;border-radius:10px;padding:9px 12px;font-size:.78rem;color:#334155}
  /* D3.2 — calendar (parity with the app's week view) */
  /* Arc A retired the per-view width override (.wide); C-2: every view renders in the one shell that fills
     the overlay (frame rules below the calendar styles). */
  .ck-cal-legend{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:10px}
  .ck-cchip{display:inline-flex;align-items:center;gap:6px;border:1.5px solid var(--mint-l,#ccfbf1);border-radius:99px;padding:4px 11px;font-size:.76rem;font-weight:800;color:var(--navy,#1a2e2b);cursor:pointer;background:#fff}
  .ck-cchip.off{opacity:.4;filter:grayscale(1)}
  .ck-sw{width:11px;height:11px;border-radius:3px;flex-shrink:0}
  .ck-cal-bar{display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;margin-bottom:12px}
  /* COCKPIT-CAL-SEARCH: the toolbar icon, the search bar that replaces the toolbar, the full list (the dropdown and the field are the member calendar's .cal-sdd / .cal-sfield / .cal-sinput) */
  .ck-sic{width:34px;height:34px;padding:0;border:1.5px solid var(--mint-l,#ccfbf1);border-radius:50%;background:#fff;color:var(--teal-d,#0d6b63);display:inline-flex;align-items:center;justify-content:center;flex:none;cursor:pointer}
  .ck-sic:hover{background:var(--mint-ll,#f0fdf9)}
  .ck-sic svg,.ck-sback svg{width:19px;height:19px;stroke:currentColor;fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}
  html[dir=rtl] .ck-sback svg{transform:scaleX(-1)}
  .ck-scal{position:relative;margin-bottom:12px}
  .ck-sbar{display:flex;align-items:center;gap:6px;flex-wrap:nowrap;padding:6px 8px;box-sizing:border-box;height:48px;border-radius:12px;background:var(--teal,#0d9488)}
  .ck-sback{width:34px;height:34px;padding:0;border:0;border-radius:50%;background:transparent;color:#fff;display:inline-flex;align-items:center;justify-content:center;flex:none;cursor:pointer}
  .ck-sres{border:1px solid var(--mint-l,#ccfbf1);border-radius:12px;max-height:min(70vh,640px)}
  .ck-cal-nav{display:grid;grid-template-columns:auto var(--ck-slot,130px) auto auto;align-items:center;gap:6px;justify-content:start} /* F-48: fixed tracks, the label never pushes the arrows */
  .ck-pay-month{grid-template-columns:auto var(--ck-slot,130px) auto}
  .ck-cal-wk{font-weight:800;font-size:.82rem;color:var(--navy,#1a2e2b);min-width:0;max-width:100%;text-align:center;background:none;border:none;cursor:pointer;font-family:inherit;display:flex;align-items:center;justify-content:center;gap:5px;padding:4px 8px;border-radius:8px;transition:background .12s;white-space:nowrap} /* B3: date-picker trigger; F-48: fills its fixed slot */
  .ck-cal-wk-t{overflow:hidden;text-overflow:ellipsis;min-width:0}
  .ck-cal-wk:hover{background:var(--mint-ll,#e8f4f2)}
  .ck-cal-wk-caret{font-size:11px;color:var(--slate-l,#94a3b8)}
  /* B3 — cockpit date-picker popover (z between the overlay 1400 and dialogs 1600) */
  .ck-picker{position:fixed;z-index:1500;background:#fff;border:1px solid var(--mint-l,#ccfbf1);border-radius:12px;box-shadow:0 8px 28px rgba(0,0,0,.18);width:300px;max-width:calc(100vw - 16px);padding:4px 4px 8px;font-family:'Nunito',sans-serif}
  .ck-picker-hd{display:flex;align-items:center;gap:6px;padding:6px 8px}
  .ck-picker-nav{background:var(--mint-ll,#e8f4f2);border:none;border-radius:6px;width:30px;height:30px;font-size:1rem;cursor:pointer;color:var(--teal-d,#0d6b63);font-family:inherit;display:inline-flex;align-items:center;justify-content:center}
  .ck-picker-nav:hover{background:var(--mint-l,#ccfbf1)}
  .ck-picker-mo{flex:1;text-align:center;font-weight:800;color:var(--navy,#1a2e2b);font-size:.9rem;white-space:nowrap}
  .ck-picker-today{background:var(--teal,#0d9488);color:#fff;border:none;border-radius:6px;padding:6px 12px;font-size:.76rem;font-weight:700;cursor:pointer;font-family:inherit;white-space:nowrap}
  .ck-picker-today:hover{background:var(--teal-d,#0d6b63)}
  .ck-picker-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:2px;padding:2px 6px 4px}
  .ck-picker-dl{text-align:center;font-size:11px;font-weight:800;color:var(--slate-l,#94a3b8);text-transform:uppercase;padding:2px 0}
  .ck-picker-day{min-height:34px;display:flex;align-items:center;justify-content:center;cursor:pointer}
  .ck-picker-day.empty{cursor:default}
  .ck-picker-num{width:30px;height:30px;display:flex;align-items:center;justify-content:center;border-radius:50%;font-size:.82rem;font-weight:700;color:var(--navy,#1a2e2b)}
  .ck-picker-day:hover .ck-picker-num{background:var(--mint-ll,#e8f4f2)}
  .ck-picker-num.today{color:var(--teal-d,#0d6b63);font-weight:900}
  .ck-picker-num.selected{background:var(--teal,#0d9488);color:#fff}
  .ck-seg.wrap{flex-wrap:wrap}
  .ck-grid-scroll{border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;background:#fff}
  .ck-grid-hdr{display:flex;background:#f5fafa;border-bottom:1px solid #e2e8f0;min-width:680px;position:sticky;top:0;z-index:2}
  .ck-grid-corner{width:48px;flex-shrink:0}
  .ck-grid-dh{flex:1;min-width:88px;display:flex;flex-direction:column;align-items:center;padding:5px 2px;border-inline-start:1px solid #e2e8f0}
  .ck-dh-tap{cursor:pointer;transition:background .12s} /* D1: day-header drill-in */
  .ck-dh-tap:hover{background:var(--mint-ll,#e8f4f2)}
  /* D1 — day-view member column header (avatar · name · count) */
  .ck-day-dh{gap:2px;padding:6px 4px}
  .ck-day-av{width:24px;height:24px;border-radius:50%;color:#fff;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:800;flex-shrink:0}
  .ck-day-nm{font-size:12px;font-weight:800;color:var(--navy,#1a2e2b);max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .ck-day-ct{font-size:11px;color:var(--slate-l,#94a3b8);font-weight:700}
  /* D2 — phone day chip switcher (avatar · first name · count; always visible = the roster summary) */
  .ck-mchips{display:flex;gap:8px;overflow-x:auto;padding:2px 2px 10px;-webkit-overflow-scrolling:touch;flex-shrink:0}
  .ck-mchip{display:inline-flex;align-items:center;gap:6px;flex-shrink:0;border:1.5px solid var(--mint-l,#ccfbf1);border-radius:99px;padding:4px 11px 4px 4px;background:#fff;cursor:pointer;transition:border-color .12s,background .12s}
  .ck-mchip.on{border-color:var(--teal,#0d9488);background:var(--mint,#f0fdf9)}
  .ck-mchip-av{width:22px;height:22px;border-radius:50%;color:#fff;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:800;flex-shrink:0}
  .ck-mchip-nm{font-size:.78rem;font-weight:800;color:var(--navy,#1a2e2b);white-space:nowrap}
  .ck-mchip-ct{font-size:.74rem;font-weight:800;color:var(--slate-l,#94a3b8)}
  .ck-mchip.on .ck-mchip-ct{color:var(--teal-d,#0d6b63)}
  .ck-dh-wd{font-size:11px;font-weight:800;letter-spacing:.06em;color:var(--slate,#64748b);text-transform:uppercase}
  .ck-dh-dt{font-size:.9rem;font-weight:800;width:26px;height:26px;display:flex;align-items:center;justify-content:center;border-radius:50%;margin-top:2px;color:var(--navy,#1a2e2b)}
  .ck-dh-dt.today{background:var(--lav-l,#ede9fe);color:var(--lav-d,#7c3aed)}
  .ck-dh-dt.picked{box-shadow:0 0 0 2px var(--teal,#0d9488)} /* B3: picked-day ring (composes with .today) */
  .ck-grid-body{max-height:52vh;overflow:auto;-webkit-overflow-scrolling:touch}
  .ck-grid{display:flex;position:relative;min-width:680px}
  .ck-grid-gut{width:48px;flex-shrink:0;position:relative;border-inline-end:1px solid #e2e8f0}
  .ck-grid-hr{position:absolute;inset-inline-end:4px;font-size:11px;color:var(--slate-l,#94a3b8);font-weight:600}
  .ck-grid-col{flex:1;min-width:88px;position:relative;border-inline-start:1px solid #e2e8f0}
  .ck-grid-col.today{background:rgba(196,181,253,.06)}
  .ck-grid-line{position:absolute;inset-inline:0;height:0;border-top:1px solid #f1f5f9}
  .ck-ev{position:absolute;font-size:11px;line-height:1.1;border:1.5px solid;border-inline-start-width:3px;border-radius:5px;padding:1px 4px;overflow:hidden;cursor:pointer;box-sizing:border-box;transition:inset-inline-start .18s ease-out,width .18s ease-out,box-shadow .18s ease-out} /* B2.5: borrow transition ≤180ms */
  .ck-ev-t{font-size:11px;font-weight:800;color:var(--navy,#1a2e2b);line-height:1.15;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .ck-ev-tm{font-size:11px;color:var(--slate,#64748b);line-height:1.1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .ck-ev-pill{display:inline-block;margin-top:1px;font-size:11px;line-height:1.1;font-weight:900;padding:0 4px;border-radius:99px}
  /* B2.5 width ladder — the 3px inline-start colour-bar survives every rung; only the text/pills change. */
  .ck-ev-dot{display:none;width:6px;height:6px;border-radius:50%;margin-inline-start:5px;vertical-align:middle} /* shown only at 3 lanes */
  .ck-meta{display:inline-flex;flex-wrap:wrap;align-items:baseline;column-gap:6px;max-width:100%}   /* F-101: a row's meta line = [therapist · date · time] + the title */
  .ck-note.ck-meta{display:flex}
  .ck-ml{display:inline-flex;flex-wrap:nowrap;align-items:baseline;column-gap:6px;min-width:0;max-width:100%}
  .ck-ml > .ck-mp:first-child{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .ck-ml > .ck-mp:not(:first-child){flex:none}
  .ck-ml > .ck-mp + .ck-mp::before,.ck-meta > .ck-mt::before{content:'·';margin-inline-end:6px}
  .ck-tr{direction:ltr;unicode-bidi:isolate} /* C-2: time ranges inside rows / dialog lines (see _tr) */
  /* C-6: name blocks take their direction from the name (dir=auto). In the Hebrew UI they still align right; a name that
     overflows is start-aligned by CSS, so its start stays visible and the ellipsis is at its end. */
  [dir="rtl"] .ck-overlay [dir="auto"],[dir="rtl"] .ck-dlg-back [dir="auto"]{text-align:right}
  [dir="rtl"] .ck-overlay .ck-po-barlbl[dir="auto"]{text-align:center}
  /* C-6: overview/calendar titles mix an emoji, the clinic name and a suffix. Only the name (its own <bdi>) shrinks and
     ellipsizes, so a Latin clinic name keeps its start in Hebrew and the suffix always shows. */
  .ck-title.ck-title-split{display:flex;align-items:baseline}
  .ck-title-split > .ck-title-nm{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .ck-title-split > .ck-title-fix{flex-shrink:0;white-space:pre}
  [dir="rtl"] .ck-ev-tm,[dir="rtl"] .ck-ev-range{direction:ltr;text-align:right} /* C-2: times read 09:00 – 09:45 in Hebrew too (skill §4), still right-aligned */
  .ck-ev-range,.ck-ev-mem{display:none;font-size:11px;color:var(--slate,#64748b);line-height:1.2} /* borrow-only detail */
  .ck-ev-mem{font-weight:700;color:#475569;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .ck-ev-l3 .ck-ev-pill,.ck-ev-l3 .ck-ev-pdot{display:none}
  .ck-ev-l3 .ck-ev-dot{display:inline-block}
  .ck-ev-bar .ck-ev-t,.ck-ev-bar .ck-ev-tm,.ck-ev-bar .ck-ev-pill,.ck-ev-bar .ck-ev-pdot,.ck-ev-bar .ck-ev-dot{display:none}
  /* B2.5 borrow — OPAQUE white card (bg/shadow/height set inline per-member), full untruncated detail.
     Rules placed AFTER the ladder hides so equal-specificity wins when both classes are present. */
  .ck-ev-borrow{overflow:visible}
  .ck-ev-borrow .ck-ev-t{display:block;white-space:normal;overflow:visible;text-overflow:clip}
  .ck-ev-borrow .ck-ev-tm{display:none}                 /* collapsed start-time hidden; range shows instead */
  .ck-ev-borrow .ck-ev-range{display:block}
  .ck-ev-borrow .ck-ev-mem{display:block}
  .ck-ev-borrow .ck-ev-pill{display:inline-block}
  .ck-ev-borrow .ck-ev-pdot{display:none}               /* the ✓/💳 glyph pills carry payment now, not the mini-dot */
  .ck-ev-borrow .ck-ev-dot{display:none}
  .strike{text-decoration:line-through}
  .ck-nowline{position:absolute;inset-inline:0;height:2px;background:#dc2626;z-index:3;pointer-events:none} /* B1/B2: today-column-only (was gutter→edge across all columns) */
  .ck-nowline::before{content:"";position:absolute;inset-inline-start:-4px;top:-3px;width:8px;height:8px;border-radius:50%;background:#dc2626}
  .ck-att-legend{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px;font-size:11px;font-weight:800}
  .ck-strip{display:grid;grid-template-columns:repeat(7,minmax(88px,1fr));gap:8px;overflow-x:auto}
  .ck-strip-col{min-width:0}
  .ck-strip-dh{text-align:center;font-size:.72rem;font-weight:800;color:var(--slate,#64748b);margin-bottom:6px}
  .ck-strip-dh.today{color:var(--lav-d,#7c3aed)}
  .ck-strip-dh.picked{color:var(--teal-d,#0d6b63);text-decoration:underline;text-underline-offset:3px} /* B3: picked-day mark */
  .ck-strip-empty{color:var(--slate-l,#cbd5e1);text-align:center;font-size:.8rem;padding:6px}
  .ck-acard{border:1px solid var(--mint-ll,#e8f4f2);border-inline-start:4px solid var(--teal);border-radius:8px;padding:7px 8px;font-size:.74rem;margin-bottom:6px;cursor:pointer}
  .ck-acard.unmarked{outline:1px dashed #fcd34d;outline-offset:-1px}
  .ck-acard-t{color:var(--navy,#1a2e2b)}
  .ck-acard-b{margin-top:4px}
  .ck-acard-r{margin-top:3px;font-size:11px;color:var(--slate,#64748b);line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
  .a-un{background:#f1f5f9;color:#475569}.a-att{background:#dcfce7;color:#166534}.a-no{background:#fed7aa;color:#9a3412}
  .a-pc{background:#e2e8f0;color:#475569}.a-tc{background:#e2e8f0;color:#475569}
  /* D4-1 — attendance picker aligned to the therapist popup control */
  .ck-attrow{display:flex;gap:6px;flex-wrap:wrap;margin-bottom:10px}
  .ck-attbtn{padding:8px 12px;border-radius:8px;font-size:.8rem;font-weight:700;cursor:pointer;font-family:inherit;background:transparent;border:1.5px solid var(--mint-l,#ccfbf1);color:var(--slate,#64748b);transition:all .12s}
  .ck-attbtn.on{font-weight:800}
  .ck-attbtn.on.a-att{background:#dcfce7;color:#166534;border-color:#166534}
  .ck-attbtn.on.a-no{background:#fed7aa;color:#9a3412;border-color:#9a3412}
  .ck-attbtn.on.a-pc,.ck-attbtn.on.a-tc{background:#e2e8f0;color:#475569;border-color:#475569}
  .ck-attbtn.on.a-charge{background:#ccfbf1;color:#0d6b63;border-color:#0d9488}
  .ck-attbtn.on.a-waive{background:#e2e8f0;color:#475569;border-color:#475569}
  .ck-submark{font-size:.74rem;color:var(--slate,#64748b);margin-bottom:8px}
  .ck-reason{display:none;width:100%;box-sizing:border-box;margin-bottom:8px;padding:8px 10px;border:1.5px solid var(--mint-l,#ccfbf1);border-radius:8px;font-size:.8rem;font-family:inherit;color:var(--navy,#1a2e2b);resize:vertical}
  /* C-2 — notes-sheet frame (supersedes the Arc A constant 1180 × min(92vh,960) frame): the shell fills the
     overlay at every width, no max size. Inset 24px ≥1024, 16px 768–1023, full-bleed <768 with safe areas.
     The shell is a flex column so .ck-body fills it and scrolls internally. Calendar grid mode (.ck-body.cal)
     hands the sole scroller to .ck-grid-body via a three-link flex chain (body → grid-scroll → grid-body);
     strip mode + every other view scroll on .ck-body itself. Breakpoints 768/1024 match notes (JS: _ckBand). */
  .ck-body.cal{overflow:hidden;display:flex;flex-direction:column}
  .ck-measure{max-width:1200px;margin-inline:auto} /* C-8: lists/tables read in a centred column on wide screens; no effect below ~1236px */
  .ck-grid-scroll{flex:1;min-height:0;display:flex;flex-direction:column}
  .ck-grid-body{flex:1;min-height:0;max-height:none}
  @media (min-width:768px) and (max-width:1023px){ .ck-overlay{padding:16px} }
  @media (max-width:767px){
    .ck-cal-bar .ck-cal-nav{flex:1 1 100%;grid-template-columns:auto minmax(0,1fr) auto auto} /* F-48 phone: the nav owns a full row; the slot fills it */
    .ck-overlay{padding:0}
    .ck-shell{border-radius:0}
    .ck-head{padding-top:calc(16px + env(safe-area-inset-top))}
    .ck-acct-nm{display:none} /* C-5 C1: phone = initial + caret; the clinic name keeps the room */
    .ck-menu{inset-inline-end:10px}
    .ck-body{padding-bottom:calc(24px + env(safe-area-inset-bottom))}
    /* phone calendar: ONE scroller (the grid body, via the flex chain above) and no 680px floor — the grid is as
       wide as its columns need (one member column in day view fits; week view's 7×88 scrolls inside the grid body). */
    .ck-grid,.ck-grid-hdr{min-width:min-content}
    .ck-content{padding:14px 16px}
    /* C-9: a payment row on a phone is two lines. Line 1: the age pill (or status badge) + the details at full width;
       line 2: the amount, (processed) who/when, and the actions pushed to the end. The details basis is wide enough
       that the amount always wraps; DOM order is unchanged, so RTL mirrors. */
    .ck-payrow{flex-wrap:wrap;row-gap:8px;column-gap:8px;padding:10px}
    /* processed rows: the status badge joins line 2 (with the amount and who/when), so the details get the full width */
    .ck-payrow.proc > .ck-grow{order:-1;flex-basis:100%}
    /* the "therapist · date …" line always starts its own line under the name, so it drops its leading "· " (the
       separators between its items stay) */
    .ck-payrow .ck-nm > .ck-sub{flex-basis:100%}
    .ck-payrow .ck-sub-dot{display:none}
    .ck-payrow .ck-meta > .ck-mt{flex-basis:100%}   /* F-101 (Eytan): on the phone the title is ALWAYS its own second line, the meta line stays one line */
    .ck-payrow .ck-meta > .ck-mt::before{content:none}
    .ck-payrow > .ck-grow{flex:1 1 calc(100% - 120px);padding-bottom:8px;border-bottom:1px solid #eef4f3}
    .ck-payrow > .ck-acts,.ck-payrow.proc > .ck-btn:last-child{margin-inline-start:auto}
  }
  /* ── Billing hub (SC-B1) ── */
  .ck-sub-lead{font-size:.86rem;color:var(--slate,#64748b);line-height:1.5;margin-bottom:16px;max-width:60ch}
  .ck-crumb{font-size:.78rem;color:var(--slate,#64748b);margin-bottom:10px}
  .ck-crumb a{color:var(--teal,#0d9488);cursor:pointer;font-weight:700}
  .ck-hint{font-size:.8rem;color:var(--slate,#64748b);margin-bottom:12px;max-width:60ch}
  .ck-foot-note{font-size:.74rem;color:var(--slate-l,#94a3b8);margin-top:11px}
  .ck-actbar{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px}
  .ck-hub{display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:14px}
  .ck-hubcard{background:#fff;border:1px solid #e6efec;border-radius:14px;padding:20px;cursor:pointer;
    box-shadow:0 1px 3px rgba(13,148,136,.08);transition:transform .15s,box-shadow .15s,border-color .15s}
  .ck-hubcard:hover{transform:translateY(-2px);box-shadow:0 4px 12px rgba(13,148,136,.12);border-color:var(--teal-l,#2dd4bf)}
  .ck-hub-ic{font-size:24px;margin-bottom:8px}
  .ck-hub-t{font-weight:800;font-size:.95rem;color:var(--navy,#1a2e2b)}
  .ck-hub-d{font-size:.8rem;color:var(--slate,#64748b);margin-top:3px;line-height:1.45}
  .ck-hub-meta{font-size:.75rem;color:var(--teal,#0d9488);margin-top:11px;font-weight:700}
  .ck-tbl{width:100%;border-collapse:collapse;font-size:.86rem}
  .ck-tbl th{text-align:start;font-size:11px;text-transform:uppercase;letter-spacing:.03em;color:var(--slate,#64748b);font-weight:800;padding:8px 10px;border-bottom:1px solid #e6efec}
  .ck-tbl td{padding:10px;border-bottom:1px solid #f1f6f5;vertical-align:middle;color:var(--navy,#1a2e2b)}
  .ck-tbl tr:last-child td{border-bottom:none}
  .pay-num{text-align:end;font-variant-numeric:tabular-nums}
  th.pay-num{text-align:end}
  .ck-strike{text-decoration:line-through;color:var(--slate-l,#94a3b8)}
  .ck-pill{display:inline-block;padding:2px 9px;border-radius:999px;font-size:.7rem;font-weight:800;margin-inline-end:4px;white-space:nowrap}
  .ck-pill.p-teal{background:var(--mint-l,#ccfbf1);color:var(--teal-d,#0d6b63)}
  .ck-pill.p-blue{background:#dbeafe;color:#1e40af}
  .ck-pill.p-amber{background:#fef3c7;color:#92610a}
  .ck-pill.p-red{background:#fee2e2;color:#991b1b}
  .ck-empty{font-size:.82rem;color:var(--slate-l,#94a3b8);padding:20px 4px;text-align:center}
  .ck-toggle{position:relative;width:38px;height:22px;background:#c4dbd8;border-radius:999px;cursor:pointer;transition:background .15s;display:inline-block;vertical-align:middle;flex-shrink:0}
  .ck-toggle::after{content:'';position:absolute;top:2px;inset-inline-start:2px;width:18px;height:18px;background:#fff;border-radius:50%;transition:inset-inline-start .15s;box-shadow:0 1px 2px rgba(0,0,0,.2)}
  .ck-toggle.on{background:var(--teal,#0d9488)}
  .ck-toggle.on::after{inset-inline-start:18px}
  .ck-lbl{font-size:.78rem;font-weight:700;color:var(--navy,#1a2e2b);margin:12px 0 5px}
  .ck-mh{font-size:.8rem;color:var(--slate,#64748b);margin-bottom:6px}
  .ck-rp{margin-top:12px;font-size:.82rem;color:var(--ink,#1a2e2b)} /* F-43: re-price preview / result */
  .ck-rp:empty{display:none}
  .ck-rp-n{font-weight:600}
  .ck-rp-paid{margin-top:6px;color:var(--slate,#64748b)}
  .ck-rp-list{margin-top:4px;display:flex;flex-direction:column;gap:2px;max-height:160px;overflow:auto}
  .ck-rp-row{display:flex;flex-wrap:wrap;gap:4px 10px;padding:4px 0;border-bottom:1px solid #eef3f2}
  .ck-rp-price{margin-inline-start:auto;font-variant-numeric:tabular-nums}
  .ck-dlg-foot .ck-btn:disabled{opacity:.45;cursor:not-allowed}
  .ck-inp{width:100%;padding:9px 11px;border:1px solid #c4dbd8;border-radius:9px;font-size:.86rem;font-family:inherit;color:var(--navy,#1a2e2b)}
  .ck-inp:focus{outline:none;border-color:var(--teal,#0d9488);box-shadow:0 0 0 3px rgba(13,148,136,.15)}
  .ck-inp.sm{width:auto;padding:7px 9px;font-size:.8rem}
  .ck-inline-row{display:flex;gap:8px;align-items:center;margin-top:8px;flex-wrap:wrap}
  .ck-radios{display:grid;gap:8px}
  .ck-radio{border:1.5px solid #c4dbd8;border-radius:9px;padding:10px 12px;cursor:pointer;transition:border-color .15s,background .15s}
  .ck-radio:hover{border-color:var(--teal-l,#2dd4bf)}
  .ck-radio.sel{border-color:var(--teal,#0d9488);background:var(--mint-ll,#f0fdf9)}
  .ck-radio-t{font-weight:700;font-size:.84rem;color:var(--navy,#1a2e2b)}
  .ck-radio-d{font-size:.76rem;color:var(--slate,#64748b);margin-top:2px}
  .ck-calc{background:#f5fafa;border:1px solid #e6efec;border-radius:9px;padding:10px 13px;font-size:.8rem;margin-top:10px}
  .ck-calc-row{display:flex;justify-content:space-between;padding:2px 0}
  .ck-calc-row.total{border-top:1px solid #c4dbd8;margin-top:5px;padding-top:5px;font-weight:800}
  .ck-link{color:var(--teal,#0d9488);font-weight:700;font-size:.8rem;cursor:pointer;display:inline-block;margin-top:12px}
  .ck-exc{margin-top:8px;border-top:1px solid #f1f6f5;padding-top:8px}
  .ck-exc-empty{font-size:.76rem;color:var(--slate-l,#94a3b8);padding:4px 0}
  .ck-exc-row{display:flex;justify-content:space-between;align-items:center;gap:8px;padding:4px 0;font-size:.8rem}
  .ck-exc-add{display:flex;gap:6px;align-items:center;margin-top:8px;flex-wrap:wrap}
  .ck-set-toggle{display:flex;align-items:flex-start;gap:11px;margin-top:12px}
  .ck-set-d{font-size:.76rem;color:var(--slate,#64748b);margin-top:2px;line-height:1.4}
  .ck-btn.secondary{background:var(--mint-ll,#f0fdf9);color:var(--teal-d,#0d6b63);border:1.5px solid var(--teal,#0d9488)}
  /* SC-B4b payouts */
  .num{font-variant-numeric:tabular-nums}
  .ck-po-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin:6px 0 12px}
  @media(min-width:768px){.ck-po-grid{grid-template-columns:repeat(6,1fr)}}
  .ck-po-stat{background:#fff;border:1px solid #e6efec;border-radius:12px;padding:11px 8px;text-align:center}
  .ck-po-stat-n{font-family:'Fraunces',serif;font-weight:600;font-size:1.15rem;color:var(--navy,#1a2e2b);direction:ltr}
  .ck-po-stat-l{font-size:11px;text-transform:uppercase;letter-spacing:.03em;color:var(--slate,#64748b);font-weight:800;margin-top:2px}
  .ck-po-warn{background:#fffbeb;border:1px solid #fde68a;border-radius:11px;padding:9px 12px;margin-bottom:12px}
  .ck-po-warn-l{font-size:.78rem;color:#92610a;line-height:1.55}
  .ck-po-finbar{display:flex;align-items:center;justify-content:flex-end;gap:10px;margin:0 0 14px}
  .ck-po-finnote{font-size:.74rem;color:var(--slate,#64748b);font-style:italic}
  .ck-po-mail{margin-top:3px}
  .ck-po-mailbtn{font-size:11px;font-weight:700;color:var(--teal-d,#0d6b63);cursor:pointer;white-space:nowrap}
  .ck-po-mailbtn:hover{text-decoration:underline}
  .ck-po-sent{font-size:11px;color:var(--slate-l,#94a3b8);cursor:pointer}
  .ck-po-sent:hover{text-decoration:underline;color:var(--slate,#64748b)}
  .ck-po-chart-card{background:#fff;border:1px solid #e6efec;border-radius:14px;padding:14px;margin-bottom:14px}
  .ck-po-legend{display:flex;gap:14px;justify-content:center;margin-bottom:10px;flex-wrap:wrap}
  .ck-po-leg{display:inline-flex;align-items:center;gap:5px;font-size:.72rem;color:var(--slate,#64748b);font-weight:700}
  .ck-po-leg i{width:11px;height:11px;border-radius:3px;display:inline-block}
  .ck-po-chart{display:flex;align-items:flex-end;justify-content:space-around;gap:10px;height:132px}
  .ck-po-bargrp{display:flex;flex-direction:column;align-items:center;gap:6px;flex:1;min-width:0;height:100%}
  .ck-po-barwrap{flex:1;display:flex;align-items:flex-end;width:100%;justify-content:center}
  .ck-po-bar{width:min(46px,70%);border-radius:6px 6px 0 0;overflow:hidden;display:flex;flex-direction:column}
  .ck-po-seg:first-child{border-radius:6px 6px 0 0}
  .ck-po-barlbl{font-size:.72rem;color:var(--navy,#1a2e2b);font-weight:700;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;text-align:center}
  .ck-po-row{display:grid;grid-template-columns:18px 1fr 34px 34px 34px minmax(88px,auto);align-items:center;gap:8px;padding:11px 12px;background:#fff;border:1px solid #e6efec;border-radius:11px;margin-bottom:6px}
  .ck-po-row.open{border-color:var(--teal-l,#2dd4bf);border-bottom-left-radius:0;border-bottom-right-radius:0;margin-bottom:0}
  .ck-po-hdr{background:transparent;border:none;padding:2px 12px 4px;margin-bottom:2px}
  .ck-po-hdr .ck-po-nm,.ck-po-hdr .ck-po-c,.ck-po-hdr .ck-po-amt{font-size:11px;text-transform:uppercase;letter-spacing:.03em;color:var(--slate,#64748b);font-weight:800}
  .ck-po-chev{color:var(--slate,#64748b);font-size:.9rem}
  .ck-po-nm{font-weight:800;font-size:.88rem;color:var(--navy,#1a2e2b);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .ck-po-c{text-align:end;font-size:.84rem;color:var(--slate,#64748b)}
  .ck-po-amt{text-align:end;font-size:.9rem;color:var(--navy,#1a2e2b)}
  .ck-po-setup{color:#dc2626;font-weight:800;font-size:.8rem;cursor:pointer;text-decoration:underline}
  .ck-po-exp{background:#f8fbfb;border:1px solid var(--teal-l,#2dd4bf);border-top:none;border-radius:0 0 11px 11px;padding:10px 14px;margin-bottom:6px}
  .ck-po-line{display:flex;justify-content:space-between;gap:10px;font-size:.82rem;color:var(--navy,#1a2e2b);padding:3px 0}
  .ck-po-line.muted{color:var(--slate,#64748b)}
  .ck-po-line.sec{border-top:1px solid #dcece9;margin-top:5px;padding-top:6px;font-weight:700}
  .ck-po-line.total{border-top:1px solid #c4dbd8;margin-top:5px;padding-top:6px;font-weight:800}
  .ck-po-warn-l.ck-row-click{cursor:pointer;display:flex;justify-content:space-between;align-items:center;gap:8px}
  /* SC-B4c worklists + calendar indicator */
  .ck-wl-chip{display:inline-flex;align-items:center;gap:6px;background:#fffbeb;border:1px solid #fde68a;border-radius:999px;padding:4px 11px;font-size:.74rem;font-weight:800;color:#92610a;margin-inline-start:auto}
  .ck-wl-seg{cursor:pointer;white-space:nowrap}
  .ck-wl-seg:hover{text-decoration:underline}
  .ck-wl-dot{color:#d6b45f}
  .ck-wl-head{display:flex;align-items:center;gap:10px;margin:4px 0 12px;flex-wrap:wrap}
  .ck-wl-countpill{background:var(--teal,#0d9488);color:#fff;border-radius:999px;padding:2px 11px;font-weight:800;font-size:.82rem;font-variant-numeric:tabular-nums}
  .ck-wl-hint{font-size:.8rem;color:var(--slate,#64748b)}
  .ck-wl-empty{text-align:center;padding:34px 12px;font-size:.95rem;font-weight:700;color:var(--teal-d,#0d6b63)}
  .ck-wl-row{display:flex;align-items:center;gap:10px;padding:11px 12px;background:#fff;border:1px solid #e6efec;border-radius:11px;margin-bottom:6px}
  .ck-wl-when{flex-shrink:0;font-size:.8rem;font-weight:800;color:var(--navy,#1a2e2b);min-width:82px;line-height:1.35}
  .ck-wl-when .ck-sub{display:block;font-weight:600}
  .ck-wl-amt{font-size:.88rem;font-weight:800;color:var(--navy,#1a2e2b)}
  `;
  document.head.appendChild(s);
}
